import {getUserDefinedObjectAdditionalColumnDetails} from "~/domainModels/userDefinedObjectAdditionalColumns";
import {UserDefinedObjectTemplateAdditionalColumnType as UdoColumnType} from "@vesta/vos-types/modules/workspaceLevel/userDefinedObjects/domain/UserDefinedObjectTemplateAdditionalColumn";
import {useCapabilityUdoColumns} from "~/contexts/workspaceContext";
import AddIcon from "@atlaskit/icon/core/add";
import DeleteIcon from "@atlaskit/icon/core/delete";
import ShowMoreHorizontalIcon from "@atlaskit/icon/core/show-more-horizontal";
import ShowMoreVerticalIcon from "@atlaskit/icon/core/show-more-vertical";
import {token} from "@atlaskit/tokens";
import {useMutation, useQueryClient} from "@tanstack/react-query";
import {createFileRoute, notFound, redirect, useNavigate, useSearch} from "@tanstack/react-router";
import {useCallback, useEffect, useMemo, useRef, useState, type ReactNode, type Ref} from "react";
import {z} from "zod";
import {AmaronLeadDispositionModal} from "~/components/amaron/amaronLeadDispositionModal";
import {DuluxLeadDispositionModal} from "~/components/dulux/duluxLeadDispositionModal";
import {AnankeTempDropdownMenuWithIconButtonTrigger} from "~/components/anankeTemp/anankeTempDropdownMenuWithIconButtonTrigger";
import {BottomDrawer} from "~/components/BottomDrawer";
import {CallCreateDropdownButton} from "~/components/callCreateDropdownButton";
import {CopyDataDropdownItem} from "~/components/dataActions/CopyDataDropdownItem";
import {ExportDataDropdownItem} from "~/components/dataActions/ExportDataDropdownItem";
import {ExportFlatDropdownItems} from "~/components/dataActions/ExportFlatDropdownItems";
import {ExportNestedDropdownItem} from "~/components/dataActions/ExportNestedDropdownItem";
import {ExportOptionsDrawerContent} from "~/components/dataActions/ExportOptionsDrawerContent";
import {ModuleMoreActionsDrawerContent} from "~/components/dataActions/ModuleMoreActionsDrawerContent";
import {RestoreDataDropdownItem} from "~/components/dataActions/RestoreDataDropdownItem";
import {EmotoradLeadDispositionModal} from "~/components/emotorad/emotoradLeadDispositionModal";
import {EventAdditionalButtonFormModal} from "~/components/eventAdditionalButtonFormModal";
import {EventAdditionalButtonTrigger} from "~/components/eventAdditionalButtonTrigger";
import {useEventArchiveAction} from "~/components/eventArchiveAction";
import {EventEditPanel} from "~/components/eventComponents/eventEditPanel";
import {EventViewPanel} from "~/components/eventComponents/eventViewPanel";
import {JourneyEntityProfileAside} from "~/components/entityProfile/journeyEntityProfileAside";
import {EventContainerListDispositionModal} from "~/components/eventContainerDetails/eventContainerListDispositionModal";
import {useListNotesPanel} from "~/components/notes/useListNotesPanel";
import {OttopilotColumnType, type OttopilotColumn} from "~/components/ottopilotDataViewer/column";
import {
    useConfiguredEventListCardPresentation,
    type AssignedOuListCardFields,
} from "~/components/ottopilotDataViewer/configuredListCardPresentation";
import {mergeDefaultCreatedAtMonthFilter} from "~/components/ottopilotDataViewer/dataViewerDateRangeDefaults";
import {useEmotoradEventListCardPresentation} from "~/components/ottopilotDataViewer/emotoradListCardPresentation";
import type {VisibleFiltersConfiguration} from "~/components/ottopilotDataViewer/filterComponentStupider";
import {type FilterConfiguration, type FilterConfigurationLineItem} from "~/components/ottopilotDataViewer/filterConfiguration";
import {FilterOperation} from "@vesta/vos-types/modules/workspaceLevel/domain/FilterOperation";
import {
    applyJourneyScopeSelectionToFilterConfiguration,
    getJourneyScopeSelectionFromFilter,
} from "~/components/ottopilotDataViewer/journeyScopeFilterConfiguration";
import {
    invalidateDataViewerQueriesAfterEventArchive,
    invalidateOttopilotDataViewerQueries,
} from "~/components/ottopilotDataViewer/invalidateOttopilotDataViewerQueries";
import type {ListCardStatus} from "~/components/ottopilotDataViewer/listStatusConfig";
import {
    listStatusPillTypeToLozengeStyle,
    resolveListCardStatusFromColumnConfig,
    withKanbanAccentTextColors,
} from "~/components/ottopilotDataViewer/listStatusConfig";
import {OttopilotDataViewer} from "~/components/ottopilotDataViewer/ottopilotDataViewer";
import {
    useOttopilotDataViewerController,
    type OttopilotDataViewerController,
} from "~/components/ottopilotDataViewer/ottopilotDataViewerController";
import {useEventRowEngagement} from "~/components/ottopilotDataViewer/rowEngagement/useEventRowEngagement";
import {renderEventTableAdditionalMenuItems} from "~/components/ottopilotDataViewer/rowEngagement/renderEventTableAdditionalMenuItems";
import {RowEngagementPanelType, RowEngagementType} from "~/components/ottopilotDataViewer/rowEngagementPanel";
import type {SearchConfiguration} from "~/components/ottopilotDataViewer/searchConfiguration";
import {SortDirection, type SortConfiguration} from "~/components/ottopilotDataViewer/sortConfiguration";
import {orderColumnsLikeTable} from "~/components/ottopilotDataViewer/exportFromDb";
import {useOttopilotExportActions} from "~/components/ottopilotDataViewer/useOttopilotExportActions";
import {nativeCsvEventColumn} from "~/utilities/nativeCsvDownload";
import {ViewType} from "~/components/ottopilotDataViewer/view";
import {WorkspaceListCardCreateChrome} from "~/components/pageScaffolds/workspacePageScaffold/bottomNav/workspaceListCardCreateChrome";
import {RestoreConfirmationModal} from "~/components/restoreConfirmationModal";
import {TableNoArchivedDataEmptyState} from "~/components/table/TableNoArchivedDataEmptyState";
import {RESTORE_SUCCESS_TOAST_BACKGROUND, TableSuccessToast} from "~/components/table/TableOperationToasts";
import {TableOnlyPage} from "~/components/tableOnlyPage";
import {useOttopilotConfiguration} from "~/contexts/ottopilotConfigurationContext";
import {useBtlActivities} from "~/contexts/workspaceBtlActivitiesContext";
import {
    usePlatformUserDetails,
    useProductCatalogueTemplates,
    useWorkspace,
} from "~/contexts/workspaceContext";
import {useEventTemplates} from "~/contexts/workspaceEventTemplatesContext";
import {useOrganizationalUnits} from "~/contexts/organizationalUnitsContext";
import {useOrganizationalUnitTemplates} from "~/contexts/workspaceContext";
import {useProductCatalogue} from "~/contexts/productCatalogueContext";
import {useWorkspaceUser} from "~/contexts/workspaceUserContext";
import {useUsers} from "~/contexts/workspaceUsersContext";
import {useWorkspaceUserTemplates as useUserTemplates} from "~/contexts/workspaceContext";

import type {BtlActivity} from "~/domainModels/btlActivities";
import {OttopilotEventStatus} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventsWorkspaceCapability/domain/OttopilotEvent";
import type {OttopilotEventTemplate} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventsWorkspaceCapability/domain/OttopilotEventTemplate";
import {getProductCatalogueDisplayName} from "~/utilities/productCatalogueDisplayName";
import type {
    TableRowActionsMenuItem,
    TableRowActionsToolbarItem,
} from "~/components/table/tableRowActions.types";
import {
    isKanbanCardSystemFieldId,
    type KanbanCardSystemFieldId,
} from "@vesta/vos-types/modules/workspaceLevel/domain/KanbanBoardColumnConfig";
import {
    OttopilotAdditionalButtonKind,
    type OttopilotAdditionalButtonsConfig,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventsWorkspaceCapability/domain/OttopilotEventAdditionalButtonsConfig";
import {filterAdditionalButtonsVisibleForEvent} from "~/domainModels/eventTemplates";

import {isListCardFooterRestrictedByGlobalConstraints} from "~/domainModels/workspaceModules/eventContainerEditPanelConstraints";

import {
    isRegistryListEventTemplate,
    resolveEventsListConfig,
    resolveWorkspaceListSupport,
} from "~/domainModels/listView/workspaceListViewRegistry";
import {resolveWorkspaceListCreateItemsConfiguration} from "~/domainModels/listView/resolveWorkspaceListCreateItemsConfiguration";
import {
    ARCHIVED_MODULE_KANBAN_LAYOUT,
    buildEventModuleInitialFilterConfiguration,
    filterKanbanColumnsForDataSlice,
    mergeModuleSliceMandatoryFilters,
    resolveEventArchivedKanbanStatusIds,
    resolveEventModuleSliceConfig,
    type ModuleDataSlice,
} from "~/domainModels/moduleDataSlice";
import {
    DULUX_MODULE_STATUS_FILTER_SCAFFOLD,
    applyAmaronModuleStatusFilterVisibilityToColumns,
    applyModuleStatusFilterVisibilityToColumns,
    filterKanbanColumnsForModuleStatusId,
    getAmaronEventTemplateStatusNavConfig,
    getAmaronModuleStatusNavValidStatusIds,
    getDuluxEventTemplateStatusNavConfig,
    getDuluxModuleStatusNavValidStatusIds,
    getEmotoradEventTemplateStatusNavConfig,
    getEmotoradModuleStatusNavValidStatusIds,
    resolveAmaronModuleStatusFilterPickerValues,
} from "~/domainModels/moduleStatusNav";
import {
    AMARON__CALL__LOG__FORM__ID,
    AMARON__CALL__SCHEDULE__FORM__ID,
    AMARON__CALLS_EVENT__ADDITIONAL_BUTTONS,
    AMARON__EVENT__CALLS__CONTACT_NUMBER__COLUMN_ID,
    AMARON__EVENT_CONTAINER__CONTACT_NUMBER__COLUMN_ID,
    AMARON__EVENT_CONTAINER__CUSTOMER_NAME__COLUMN_ID,
    AMARON__EVENT_CONTAINER__LEAD_STATUS__COLUMN_ID,
    AMARON__EVENT_CONTAINER__REMARKS__COLUMN_ID,
    AMARON__SERVICE_EVENT__ADDITIONAL_BUTTONS,
    AMARON_DEFAULT_JOURNEY_TRIGGER_CONFIG,
    AMARON__EVENT__ENQUIRY__FUEL_TYPE__COLUMN_ID,
    AMARON__EVENT__SERVICE__FUEL_TYPE__COLUMN_ID,
    AMARON__EVENT_CONTAINER__FUEL_TYPE__COLUMN_ID,
    AMARON_ENQUIRY_EVENT_TABLE_VISIBLE_COLUMN_IDS,
    AMARON_SERVICE_EVENT_TABLE_VISIBLE_COLUMN_IDS,
    AmaronEventTypes,
    AmaronRoles,
    createAmaronJourneyListFunnelOptions,
    filterAmaronCallKanbanColumnConfig,
    filterAmaronEnquiryKanbanColumnConfig,
    filterAmaronServiceKanbanColumnConfig,
    formatAmaronFuelTypeIdForDisplay,
    getAmaronEventsPageScopeFilterConfig,
    getAmaronJourneyScopeFilterStatusIdsForEvents,
    isAmaronScheduledCallEvent,
    isAmaronWorkspace,
    shouldHideAmaronCallLogAction,
    normalizeAmaronCallsPluralDisplayLabel,
    getAmaronDefaultVisibleFilterColumnIdsForEventTemplate,
    mergeUniqueOrderedColumnIds,
    withAmaronEventArchiveStatusColumnConfig,
    AMARON_CALLS_EVENT_TABLE_VISIBLE_COLUMN_IDS,
    isAmaronCancelledCallEvent,
} from "~/domainModels/workspaceSpecific/amaron";
import {resolveAmaronEventCalendarDisplayMetrics} from "~/domainModels/calendarDisplayMetrics";
import {getAdditionalColumnOptions} from "~/domainModels/additionalColumns";
import {
    resolveEventContainerEditPanelGlobalConstraints,
    resolveEventContainerScheduleCallDisableConfig,
} from "~/utilities/eventContainerGlobalConstraints";
import {isAmaronEventCallsDispositionUiFilterColumn, sanitizeAmaronDispositionFiltersForExport} from "~/domainModels/workspaceSpecific/amaronDispositionFilters";
import {
    BOBCARD__CALL__LOG__FORM__ID,
    BOBCARD__CALL__SCHEDULE__FORM__ID,
    BOBCARD__EVENT__ENQUIRY__DIGITAL_LEAD_STATUS__COLUMN_ID,
    BOBCARD__EVENT_CONTAINER__APPLICATION_STAGE__COLUMN_ID,
    BOBCARD__EVENT_CONTAINER__REJECTION_STAGE__COLUMN_ID,
    BOBCARD_ENQUIRY_EVENT_TABLE_VISIBLE_COLUMN_IDS,
    bobcardApplicationStages,
    bobcardEnquiryDigitalLeadStatuses,
    BobcardEventTypes,
    bobcardRejectionStages,
    getBobcardJourneyScopeFilterStatusIdsForEvents,
    isBobcardWorkspace,
} from "~/domainModels/workspaceSpecific/bobcard";
import {
    DHIRAAGU__CALL__LOG__FORM__ID,
    DHIRAAGU__CALL__SCHEDULE__FORM__ID,
    DHIRAAGU__CALL_EVENT__ADDITIONAL_BUTTONS,
    DHIRAAGU__SALES__CREATE__FORM__ID,
    DhiraaguEventTypes,
    isDhiraaguWorkspace,
} from "~/domainModels/workspaceSpecific/dhiraagu";
import {
    AppointmentStatusEnum,
    CallStatusEnum,
    DULUX__APPOINTMENT__LOG__FORM__ID,
    DULUX__APPOINTMENT__SCHEDULE__FORM__ID,
    DULUX__APPOINTMENTS_EVENT__ADDITIONAL_BUTTONS,
    DULUX__CALL__LOG__FORM__ID,
    DULUX__CALL__SCHEDULE__FORM__ID,
    DULUX__CALLS_EVENT__ADDITIONAL_BUTTONS,
    DULUX__EVENT__APPOINTMENT__APPOINTMENT_STATUS__COLUMN_ID,
    DULUX__EVENT__APPOINTMENT__CREATED_BY_ROLE__COLUMN_ID,
    DULUX__EVENT__CALLS__CALL_STATUS__COLUMN_ID,
    DULUX__EVENT__CALLS__CREATED_BY_ROLE__COLUMN_ID,
    DULUX_APPOINTMENTS_EVENT_TABLE_VISIBLE_COLUMN_IDS,
    DULUX_CALLS_EVENT_TABLE_VISIBLE_COLUMN_IDS,
    DULUX_ENQUIRY_EVENT_TABLE_VISIBLE_COLUMN_IDS,
    DULUX_EVENT_TEMPLATE_EVENT_OWNER_COLUMN_IDS,
    DULUX_MODULE_KANBAN_LAYOUT,
    DULUX_ONBOARDING_EVENT_TABLE_VISIBLE_COLUMN_IDS,
    DuluxEventTypes,
    applyDuluxEventTableColumnFiltering,
    duluxColumnIsExportable,
    getDuluxEventSearchPlaceholder,
    isDuluxWorkspace,
    resolveDuluxEventExportFilename,
    resolveDuluxEventSearchColumns,
    resolveDuluxJourneyFallbackTableColumnDisplayName,
} from "~/domainModels/workspaceSpecific/dulux";
import {
    EMOTORAD__CALL__LOG__FORM__ID,
    EMOTORAD__CALL__SCHEDULE__FORM__ID,
    EMOTORAD__EVENT__CALL__L1_DISPOSITION__COLUMN_ID,
    EMOTORAD__EVENT__CALL__L2_DISPOSITION__COLUMN_ID,
    EMOTORAD__EVENT__CALL__L3_DISPOSITION__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__CALL_DISPOSITION__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__CANCELLED_BY__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__CURRENT_PLATFORM__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__EXPECTED_RIDING_FREQUENCY__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__EXPLORING_FOR__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__HEIGHT_OF_RIDER__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__LOGGED_BY__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__MODEL_VARIANT__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__PRIMARY_RIDING_PURPOSE__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__SCHEDULED_BY__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__STATUS__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__TEST_RIDE_TAKEN__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__AON__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__ASSIGNED_ON__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__CREATED_BY_NAME__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__CREATED_BY_ROLE__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__CURRENT_PLATFORM__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__DIGITAL_LEAD_STATUS__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__EXPECTED_RIDING_FREQUENCY__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__EXPLORING_FOR__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__FIRST_CYCLE__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__GENDER__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__HEIGHT_OF_RIDER__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__LEAD_STATUS__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__OTHER_BRANDS_CONSIDERED__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__PREFERRED_COLOR__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__PRIMARY_RIDING_PURPOSE__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__SOURCE_CHANNEL__COLUMN_ID,
    EMOTORAD__EVENT__SALES__ACTION_PLATFORM__COLUMN_ID,
    EMOTORAD__EVENT__SALES__CREATED_BY_NAME__COLUMN_ID,
    EMOTORAD__EVENT__SALES__CREATED_BY_ROLE__COLUMN_ID,
    EMOTORAD__EVENT__SALES__CUSTOMER_PINCODE__COLUMN_ID,
    EMOTORAD__EVENT__SALES__KAM__COLUMN_ID,
    EMOTORAD__EVENT__SALES__MODEL__COLUMN_ID,
    EMOTORAD__EVENT__SALES__MODEL_VARIANT__COLUMN_ID,
    EMOTORAD__EVENT__SALES__PREFERRED_COLOR__COLUMN_ID,
    EMOTORAD__EVENT__SALES__RM__COLUMN_ID,
    EMOTORAD__EVENT__SALES__SHOWROOM_ID__COLUMN_ID,
    EMOTORAD__EVENT__SALES__SOURCE_CHANNEL__COLUMN_ID,
    EMOTORAD__EVENT__SALES__STATUS__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__CANCELLED_BY__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__CANCELLED_ON__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__EXPECTED_RIDING_FREQUENCY__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__EXPLORING_FOR__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__HEIGHT_OF_RIDER__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__LAST_TEST_RIDE_DATE__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__LOGGED_BY__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__LOGGED_ON__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__PRIMARY_RIDING_PURPOSE__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__SCHEDULED_BY__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__SCHEDULED_ON__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__STATUS__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__TEST_RIDE_DATE__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__TEST_RIDE_TIME__COLUMN_ID,
    EMOTORAD__EVENT__WALKIN__L1_DISPOSITION__COLUMN_ID,
    EMOTORAD__EVENT__WALKIN__L2_DISPOSITION__COLUMN_ID,
    EMOTORAD__EVENT__WALKIN__L3_DISPOSITION__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__CANCELLED_BY__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__DISPOSITION__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__EXPECTED_RIDING_FREQUENCY__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__EXPLORING_FOR__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__HEIGHT_OF_RIDER__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__LOGGED_BY__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__LOGGED_ON__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__PRIMARY_RIDING_PURPOSE__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__PURCHASE_TIMELINE__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__SCHEDULED_BY__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__STATUS__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__TEST_RIDE_TAKEN__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__ACTION_PLATFORM__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__AON__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_CONTACT__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_EMAIL__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_NAME__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__ASSIGNED_ON__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CALLS_COUNT__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CONTACT_NUMBER__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CUSTOMER_PINCODE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CUSTOMER_ZONE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__DEALER_CITY__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__DEALER_PINCODE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__DEALER_STATE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__DEALER_ZONE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__KAM_NAME__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__L1_DISPOSITION__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__L2_DISPOSITION__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__L3_DISPOSITION__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__LEAD_OWNER__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__MODEL__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__MODEL_VARIANT__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__PURCHASE_TIMELINE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__SOURCE_CHANNEL__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__ZSM_NAME__COLUMN_ID,
    EMOTORAD__PRODUCT_CATALOGUE__COLOR_NAME__COLUMN_ID,
    EMOTORAD__SALES__SCHEDULE__FORM__ID,
    EMOTORAD__TESTRIDE__LOG__FORM__ID,
    EMOTORAD__TESTRIDE__SCHEDULE__FORM__ID,
    EMOTORAD__USER__ADDRESS_PINCODE__COLUMN_ID,
    EMOTORAD__USER__CONTACT_NUMBER__COLUMN_ID,
    EMOTORAD__USER__DEALERSHIP__COLUMN_ID,
    EMOTORAD__USER__EMAIL__COLUMN_ID,
    EMOTORAD__USER__GENDER__COLUMN_ID,
    EMOTORAD__WALKIN__LOG__FORM__ID,
    EMOTORAD__WALKIN__SCHEDULE__FORM__ID,
    EMOTORAD_CALLS_EVENT_DEFAULT_VISIBLE_FILTER_COLUMN_IDS,
    EMOTORAD_CALLS_EVENT_TABLE_VISIBLE_COLUMN_IDS,
    EMOTORAD_LEADS_EVENT_DEFAULT_VISIBLE_FILTER_COLUMN_IDS,
    EMOTORAD_LEADS_EVENT_TABLE_VISIBLE_COLUMN_IDS,
    EMOTORAD_SALES_EVENT_DEFAULT_VISIBLE_FILTER_COLUMN_IDS,
    EMOTORAD_SALES_EVENT_TABLE_VISIBLE_COLUMN_IDS,
    EMOTORAD_TESTRIDE_EVENT_DEFAULT_VISIBLE_FILTER_COLUMN_IDS,
    EMOTORAD_TESTRIDE_EVENT_TABLE_VISIBLE_COLUMN_IDS,
    EMOTORAD_WALKINS_EVENT_DEFAULT_VISIBLE_FILTER_COLUMN_IDS,
    EMOTORAD_WALKINS_EVENT_TABLE_VISIBLE_COLUMN_IDS,
    EmotoradCallStatusEnum,
    emotoradCallStatuses,
    emotoradCurrentPlatforms,
    emotoradEnquiryAonConfig,
    emotoradEnquiryDigitalLeadStatuses,
    emotoradEventSyntheticFunction,
    EmotoradEventTypes,
    emotoradGenders,
    emotoradJourneyAonConfig,
    emotoradLeadEventStatuses,
    EmotoradOus,
    EmotoradSalesStatusEnum,
    EmotoradYesNoEnum,
    emotoradYesNoOptions,
    expandEmotoradCustomerZoneFilterValues,
    formatEmotoradCalendarEventTitle,
    formatEmotoradCallEventCurrentPlatformForDisplay,
    formatEmotoradCurrentPlatformCell,
    formatEmotoradEventCalendarStatusIdForDisplay,
    formatEmotoradSalesStatusIdForDisplay,
    getEmotoradCallEventCurrentPlatformRaw,
    getEmotoradCurrentPlatformSelectOptions,
    getEmotoradDispositionIdsForLevels,
    getEmotoradJourneyScopeFilterStatusIdsForEvents,
    getEmotoradListEventCreateItems,
    getEmotoradListModuleConfig,
    getEmotoradUniqueDispositionLevelValues,
    getEmotoradYesNoSelectOptions,
    intersectEmotoradPreferredVisibleFilterColumnIdsWithModule,
    isEmotoradActionPlatformColumnId,
    isEmotoradWorkspace,
    normalizeEmotoradCustomerZone,
    sanitizeEmotoradFilterColumnValues,
} from "~/domainModels/workspaceSpecific/emotorad";
import {EMOTORAD__LEAD__CREATE__FORM__ID, isEmotoradJourneyModuleConfiguration} from "~/domainModels/workspaceSpecific/emotorad_old";
import {useRequiredWorkspaceMobileNavigationConfiguration} from "~/hooks/workspaceConfiguration";
import {useOnlyEventContainerTemplate} from "~/contexts/workspaceEventContainerTemplatesContext";
import {useCheckMobile} from "~/hooks/useCheckMobile";
import {
    mergeModuleStatusFilterIntoFilterConfiguration,
    resolveModuleStatusIdForFilter,
    useHideAmaronModuleStatusFilterUiInChildView,
    useHideModuleStatusFilterUiInChildView,
    useSyncModuleStatusFilterFromSearch,
} from "~/hooks/useSyncModuleStatusFilterFromSearch";
import {
    formatEmotoradTestRideDateOnlyForDisplay,
    formatEmotoradTestRideShortRangeLabelFromSlotHhMm,
    getEmotoradTestRideEffectiveSlotHhMmFromEventRow,
} from "~/workspaceConfig/hostedForms/shared/emotorad/emotoradTestrideTimeSlots";
import {
    buildAmaronCallEditPanelTemplate,
    buildAmaronEnquiryEditPanelTemplate,
    ensureAmaronEventAttributionEditPanel,
} from "~/routes/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/w/$workspaceId/ottopilot/_ottopilotScaffold/events/$eventTemplateId/index/-amaron-edit-panel";
import {
    buildDuluxCallEditPanelTemplate,
    isDuluxNotOnboardedOnboardingEvent,
    isDuluxScheduledAppointmentEvent,
    isDuluxScheduledCallEvent,
} from "~/routes/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/w/$workspaceId/ottopilot/_ottopilotScaffold/events/$eventTemplateId/index/-dulux-edit-panel";
import {
    amaronCallEventSyntheticFunction,
    buildAmaronCallTableColumns,
} from "~/routes/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/w/$workspaceId/ottopilot/_ottopilotScaffold/events/$eventTemplateId/index/-amaron-table-colunms";
import {
    buildAmaronCallViewPanelTemplate,
    buildAmaronOpportunityViewPanelTemplate,
    buildAmaronServiceEventTemplate,
    ensureAmaronEventAttributionViewPanel,
} from "~/routes/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/w/$workspaceId/ottopilot/_ottopilotScaffold/events/$eventTemplateId/index/-amaron-view-panel";
import {EventKanbanBoardCard} from "~/routes/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/w/$workspaceId/ottopilot/_ottopilotScaffold/events/$eventTemplateId/index/-event-kanban-board";
import {AnankeButton} from "@vesta/ananke/components/anankeButton";
import {AnankeDropdownItem} from "@vesta/ananke/components/anankeDropdownItem";
import {AnankeDropdownItemGroup} from "@vesta/ananke/components/anankeDropdownItemGroup";
import {AnankeDropdownMenu} from "@vesta/ananke/components/anankeDropdownMenu";
import {AnankeIconButton} from "@vesta/ananke/components/anankeIconButton";

import {useAnankeFlags} from "@vesta/ananke/hooks/useAnankeFlags";
import type {Integer, Iso8601DateTime, Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {generateUuid} from "@vesta/global-common-typescript/utilities/utilities";
import {uuidZodValidator} from "@vesta/global-common-typescript/validators/zodValidators";
import {getAmaronJourneyListCardAssignedOuColumnLabel, getAmaronJourneyListCardAssignedOuName} from "~/utilities/amaronListCardOuFields";
import {getOptionalEventContainerOrganizationalUnitId} from "~/utilities/ottopilotSemanticColumns";
import {
    convertFilterConfigurationToSrimanFiltersForEvents,
    convertSortConfigurationToSrimanOrderByForEvents,
    getEvent_ColumnValues,
    getEvents_Length,
    getEventsWithEventContainers,
    getEventContainerColumnValues,
    resolveEventsFilterColumnId,
    type EventContainerWithEvents,
    type EventWithEventContainer,
} from "~/utilities/apis/api";
import {
    countOrchestratedEventsByFilterConfiguration,
    getOrchestratedEventsWithEventContainers,
    resolveCalendarOrchestrationEligibility,
} from "~/utilities/apis/eventContainerAndEventOrchestrationApi";
import {resolveCalendarDisplayMetricFilterColumnId} from "~/domainModels/calendarDisplayMetrics/types";
import {resolveKamRmUserIdsFromDealershipReportingChain} from "~/utilities/emotoradDealershipSubmitResolution";
import {
    emotoradJourneyTestRideCountCellString,
    isEmotoradJourneyTestRideCountColumnField,
    isEmotoradTestRideCountJourneyColumnDisplayName,
} from "~/utilities/emotoradJourneyTestRideCount";
import {
    createAmaronCalendarGetEventColor,
    getAmaronCalendarLegendItems,
    getAmaronEventKanbanColumnConfig,
    resolveAmaronCalendarEventTypeLabel,
    resolveAmaronEventStatusColorModule,
    type AmaronCalendarColorModule,
} from "~/domainModels/amaronCalendarStatusColors";
import {resolveEventFormCreateHref} from "~/utilities/eventFormCreateHref";
import {
    getEventColumnValuesFromTemplateEvents,
    getEventContainerColumnValuesFromTemplateEvents,
} from "~/utilities/eventContainerColumnValuesQuery";
import {eventTableViewColumnsToOttopilotColumns} from "~/utilities/eventTableViewColumnsTransform";
import {restoreArchivedEvent} from "~/utilities/restoreArchivedRow";
import {
    DEFAULT_CALENDAR_SCHEDULE_EMPTY_STATE,
    EMOTORAD_CALENDAR_SCHEDULE_EMPTY_STATE,
    EMOTORAD_LEADS_CALENDAR_SCHEDULE_EMPTY_STATE,
    EMOTORAD_SALES_CALENDAR_SCHEDULE_EMPTY_STATE,
    resolveCalendarAllDayStyle,
    resolveCalendarAllDayStyleForModuleSlice,
    resolveCalendarEventTypeColor,
    resolveCalendarEventTypeLabel,
    resolveCalendarLegendItems,
    resolveCalendarScheduleEmptyState,
    wrapGetEventColorForArchivedModuleSlice,
} from "~/utilities/sharedCalendarConfig";
import {
    buildAonCustomFilter,
    buildDistinctAonFilterValuesFromCreatedAtColumnValues,
    isAonFilterRangesLineItem,
    mapAonFilterLineItemForApi,
    type AonWorkspaceConfig,
} from "~/utilities/syntheticFields/aon";
import {formatDateTime, getUserDisplayName, transformRowForExport} from "~/utilities/utilities";
import {processOttopilotStringTemplate} from "@vesta/vos-types/modules/workspaceLevel/domain/OttopilotStringTemplate";
import {uniqueUuid} from "@vesta/unique/unique";
import {
    buildEmotradCallEditPanelTemplate,
    buildEmotradLeadEditPanelTemplate,
    buildEmotradSalesEditPanelTemplate,
    buildEmotradTestrideEditPanelTemplate,
    buildEmotradWalkinEditPanelTemplate,
} from "./-emotrad-edit-panels";
import {
    buildEmotradCallTableColumns,
    buildEmotradLeadTableColumns,
    buildEmotradSalesTableColumns,
    buildEmotradTestrideTableColumns,
    buildEmotradWalkinTableColumns,
} from "./-emotrad-table-columns";
import {
    buildEmotradCallViewPanelTemplate,
    buildEmotradLeadViewPanelTemplate,
    buildEmotradSalesViewPanelTemplate,
    buildEmotradTestrideViewPanelTemplate,
    buildEmotradWalkinViewPanelTemplate,
} from "./-emotrad-view-panels";
import {createOttopilotListView} from "~/components/ottopilotDataViewer/ottopilotListCardPresentation";

const eventsSearchSchema = z.object({
    view: z.enum(["list", "table", "kanban", "calendar", "create"]).optional().default("table").catch("table"),
    statusId: z.string().optional(),
});

const VALID_VIEWS = ["list", "table", "kanban", "calendar", "create"] as const;
const EVENTS_LIST_VIEW_ID = uniqueUuid`1fcfcae8-0aad-4da2-8d8b-6ff48cb6de5e`;
const EVENTS_TABLE_VIEW_ID = uniqueUuid`7485111d-3520-4028-a593-9ee492a82c33`;
const EVENTS_KANBAN_VIEW_ID = uniqueUuid`8baac944-642e-4856-b697-31a736e0d846`;
const EVENTS_CALENDAR_VIEW_ID = uniqueUuid`2b30fd87-8d4a-4e5b-ba0f-6af5cb75a16d`;
const EMPTY_EVENT_SEARCH_COLUMNS: Array<string> = [];

/** Calls list: workspace config often omits phone columns; search must hit journey + event contact. */
const AMARON_CALLS_FALLBACK_SEARCH_COLUMN_IDS: ReadonlyArray<string> = [
    AMARON__EVENT_CONTAINER__CUSTOMER_NAME__COLUMN_ID,
    AMARON__EVENT_CONTAINER__CONTACT_NUMBER__COLUMN_ID,
    AMARON__EVENT__CALLS__CONTACT_NUMBER__COLUMN_ID,
];

function coerceValidCalendarIsoStart(candidate: string, fallback: string): string {
    for (const value of [candidate, fallback]) {
        const trimmed = value.trim();
        if (trimmed !== "" && Number.isNaN(Date.parse(trimmed)) === false) {
            return trimmed;
        }
    }
    const fallbackTrimmed = fallback.trim();
    return fallbackTrimmed !== "" ? fallbackTrimmed : candidate.trim();
}

function resolveEventCalendarIsoStart(
    event: EventWithEventContainer,
    columnId: Uuid | KanbanCardSystemFieldId | undefined,
    fallback: string,
): string {
    if (columnId === undefined) {
        return coerceValidCalendarIsoStart(fallback, fallback);
    }
    if (isKanbanCardSystemFieldId(columnId) === true) {
        if (columnId === "createdAt") {
            return coerceValidCalendarIsoStart(event.createdAt, fallback);
        }
        if (columnId === "updatedAt") {
            return coerceValidCalendarIsoStart(event.updatedAt, fallback);
        }
        return coerceValidCalendarIsoStart(fallback, fallback);
    }
    const fromEvent = event.additionalColumnValues[columnId];
    if (fromEvent !== null && fromEvent !== undefined && String(fromEvent).trim() !== "") {
        return coerceValidCalendarIsoStart(String(fromEvent), fallback);
    }
    const fromJourney = event.eventContainer.additionalColumnValues[columnId];
    if (fromJourney !== null && fromJourney !== undefined && String(fromJourney).trim() !== "") {
        return coerceValidCalendarIsoStart(String(fromJourney), fallback);
    }
    return coerceValidCalendarIsoStart(fallback, fallback);
}

/** Same digit normalization as disposition tracker for matching call vs logged test ride journey. */
function normalizeEmotoradContactDigitsForCallsTestRideLookup(value: unknown): string {
    if (value === null || value === undefined) {
        return "";
    }
    return String(value).replace(/\D/g, "");
}

function isEmotoradCallsTestRideTakenStoredYesLike(value: unknown): boolean {
    if (value === null || value === undefined) {
        return false;
    }
    const s = String(value).trim();
    if (s === "") {
        return false;
    }
    if (s.toLowerCase() === "true") {
        return true;
    }
    return s === String(EmotoradYesNoEnum.YES);
}

function collectEmotoradLoggedTestRideLookupForCalls(testRideEvents: Array<EventWithEventContainer>): {
    journeyIdsWithLoggedTestRide: ReadonlySet<string>;
    normalizedContactsWithLoggedTestRide: ReadonlySet<string>;
} {
    const journeyIdsWithLoggedTestRide = new Set<string>();
    const normalizedContactsWithLoggedTestRide = new Set<string>();
    const loggedStatusId = String(EmotoradCallStatusEnum.LOGGED);
    for (const event of testRideEvents) {
        const status = event.additionalColumnValues[EMOTORAD__EVENT__TESTRIDES__STATUS__COLUMN_ID];
        if (String(status) !== loggedStatusId) {
            continue;
        }
        journeyIdsWithLoggedTestRide.add(String(event.eventContainer.id));
        const normalized = normalizeEmotoradContactDigitsForCallsTestRideLookup(
            event.eventContainer.additionalColumnValues[EMOTORAD__EVENT_CONTAINER__CONTACT_NUMBER__COLUMN_ID],
        );
        if (normalized.length > 0) {
            normalizedContactsWithLoggedTestRide.add(normalized);
        }
    }
    return {journeyIdsWithLoggedTestRide, normalizedContactsWithLoggedTestRide};
}

function applyEmotoradCallEventsSyntheticTestRideTakenColumn(
    callEvents: Array<EventWithEventContainer>,
    lookup: {
        journeyIdsWithLoggedTestRide: ReadonlySet<string>;
        normalizedContactsWithLoggedTestRide: ReadonlySet<string>;
    },
): Array<EventWithEventContainer> {
    return callEvents.map((event) => {
        const prior = event.additionalColumnValues[EMOTORAD__EVENT__CALLS__TEST_RIDE_TAKEN__COLUMN_ID];
        const journeyId = String(event.eventContainer.id);
        const contactNorm = normalizeEmotoradContactDigitsForCallsTestRideLookup(
            event.eventContainer.additionalColumnValues[EMOTORAD__EVENT_CONTAINER__CONTACT_NUMBER__COLUMN_ID],
        );
        const completedByJourney = lookup.journeyIdsWithLoggedTestRide.has(journeyId);
        const completedByContact = contactNorm.length > 0 && lookup.normalizedContactsWithLoggedTestRide.has(contactNorm);
        const testRideCompleted = completedByJourney === true || completedByContact === true;
        const finalYes = testRideCompleted === true || isEmotoradCallsTestRideTakenStoredYesLike(prior) === true;
        const value = finalYes ? EmotoradYesNoEnum.YES : EmotoradYesNoEnum.NO;
        return {
            ...event,
            additionalColumnValues: {
                ...event.additionalColumnValues,
                [EMOTORAD__EVENT__CALLS__TEST_RIDE_TAKEN__COLUMN_ID]: value,
            },
        };
    });
}

function isEmotoradCallsTestRideTakenFilterColumnId(columnId: string): boolean {
    return String(columnId) === String(EMOTORAD__EVENT__CALLS__TEST_RIDE_TAKEN__COLUMN_ID);
}

function eventTableAdditionalColumnRawToString(raw: unknown): string {
    if (raw === null || raw === undefined) {
        return "";
    }
    return String(raw);
}

function isAmaronFuelTypeColumnId(columnId: string): boolean {
    return (
        String(columnId) === String(AMARON__EVENT__ENQUIRY__FUEL_TYPE__COLUMN_ID) ||
        String(columnId) === String(AMARON__EVENT__SERVICE__FUEL_TYPE__COLUMN_ID) ||
        String(columnId) === String(AMARON__EVENT_CONTAINER__FUEL_TYPE__COLUMN_ID)
    );
}

type FilterInLineItem = FilterConfigurationLineItem & {
    operation: FilterOperation.in;
    values: Array<string>;
};

function isFilterInLineItem(lineItem: FilterConfigurationLineItem): lineItem is FilterInLineItem {
    return lineItem.operation === FilterOperation.in;
}

function isEmotoradCallsTestRideTakenFilterLineItem(lineItem: FilterConfigurationLineItem): boolean {
    return lineItem.operation === FilterOperation.in && isEmotoradCallsTestRideTakenFilterColumnId(lineItem.columnId);
}

function applyEmotoradCallsTestRideTakenClientFilter(events: Array<EventWithEventContainer>, filterConfiguration: FilterConfiguration): Array<EventWithEventContainer> {
    const lineItem = filterConfiguration.lineItems.find(isEmotoradCallsTestRideTakenFilterLineItem);
    if (lineItem === undefined || lineItem.operation !== FilterOperation.in || lineItem.values.length === 0) {
        return events;
    }
    const allowed = new Set(lineItem.values.map((value) => String(value)));
    return events.filter((event) => {
        const raw = event.additionalColumnValues[EMOTORAD__EVENT__CALLS__TEST_RIDE_TAKEN__COLUMN_ID];
        if (raw === undefined || raw === null) {
            return false;
        }
        return allowed.has(String(raw).trim());
    });
}

function isMalformedViewSearch(searchString: string): boolean {
    if (searchString === "" || searchString === "?") {
        return false;
    }
    const viewParamMatches = searchString.match(/[?&]view=/g);
    if (viewParamMatches !== null && viewParamMatches.length > 1) {
        return true;
    }
    const questionMarkCount = (searchString.match(/\?/g) ?? []).length;
    if (questionMarkCount > 1) {
        return true;
    }
    return false;
}

function getNormalizedViewFromSearch(searchString: string): (typeof VALID_VIEWS)[number] {
    try {
        const url = new URL("http://dummy" + (searchString.startsWith("?") ? searchString : `?${searchString}`));
        const viewParams = url.searchParams.getAll("view");
        const firstView = viewParams[0];
        if (firstView !== undefined && VALID_VIEWS.includes(firstView as (typeof VALID_VIEWS)[number])) {
            return firstView as (typeof VALID_VIEWS)[number];
        }
    } catch {
        /* ignore */
    }
    return "table";
}

function getNormalizedStatusIdFromSearch(searchString: string): string | undefined {
    try {
        const url = new URL("http://dummy" + (searchString.startsWith("?") ? searchString : `?${searchString}`));
        const rawStatusId = url.searchParams.get("statusId");
        const statusId = rawStatusId?.split(/[?&]/, 1)[0]?.trim();
        return statusId === undefined || statusId === "" ? undefined : statusId;
    } catch {
        return undefined;
    }
}

export const Route = createFileRoute(
    "/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/w/$workspaceId/ottopilot/_ottopilotScaffold/events/$eventTemplateId/",
)({
    validateSearch: (search: Record<string, unknown>) => eventsSearchSchema.parse(search),
    beforeLoad: ({location, params}) => {
        const searchString = (() => {
            try {
                const parsed = new URL(location.href, "http://dummy");
                return parsed.search;
            } catch {
                const idx = location.href.indexOf("?");
                return idx >= 0 ? location.href.slice(idx) : "";
            }
        })();
        if (isMalformedViewSearch(searchString) === true) {
            const normalizedView = getNormalizedViewFromSearch(searchString);
            const normalizedStatusId = getNormalizedStatusIdFromSearch(searchString);
            const workspaceId = typeof params.workspaceId === "string" ? params.workspaceId : "";
            const eventTemplateId = typeof params.eventTemplateId === "string" ? params.eventTemplateId : "";
            throw redirect({
                to: "/w/$workspaceId/ottopilot/events/$eventTemplateId",
                params: {workspaceId: workspaceId, eventTemplateId: eventTemplateId},
                search: {
                    view: normalizedView,
                    ...(normalizedStatusId === undefined ? {} : {statusId: normalizedStatusId}),
                },
                replace: true,
            });
        }
    },
    loader: async ({context, params}) => {
        const eventTemplateIdResult = uuidZodValidator.safeParse(params.eventTemplateId);
        if (eventTemplateIdResult.success !== true) {
            throw notFound();
        }
        const eventTemplateId = eventTemplateIdResult.data;

        const eventTemplates = context.eventTemplates;
        const eventTemplate: OttopilotEventTemplate | undefined = eventTemplates.find((eventTemplate) => eventTemplate.id === eventTemplateId);
        if (eventTemplate === undefined) {
            throw notFound();
        }
        if (
            isAmaronWorkspace(context.workspaceCapabilities) === true &&
            eventTemplateId === (AmaronEventTypes.Enquiry as Uuid) &&
            context.currentWorkspaceUser?.templateId === (AmaronRoles.Agent as Uuid)
        ) {
            throw redirect({
                to: "/w/$workspaceId/ottopilot",
                params: {workspaceId: context.workspace.id},
                replace: true,
            });
        }

        return {
            eventTemplateId: eventTemplateId,
            eventTemplate: eventTemplate,
        };
    },
    component: EventsIndexRouteComponent,
});

function EventsIndexRouteComponent() {
    const {eventTemplateId, eventTemplate} = Route.useLoaderData();

    return (
        <EventsRouteComponent
            dataSlice="active"
            eventTemplateId={eventTemplateId}
            eventTemplate={eventTemplate}
        />
    );
}

function _EventTableRowAdditionalActions(props: {
    row: EventWithEventContainer;
    eventTemplateId: Uuid;
    additionalButtonConfig: Array<OttopilotAdditionalButtonsConfig>;
    currentUserTemplateIds: ReadonlySet<Uuid>;
    workspaceId: Uuid;
    dataViewerQueryKeyPrefix: Array<unknown>;
    onOpenAdditionalButtonModal: (payload: {config: OttopilotAdditionalButtonsConfig; event: EventWithEventContainer}) => void;
    onOpenLeadDispositionModal?: (event: EventWithEventContainer) => void;
    onOpenAmaronDispositionModal?: (event: EventWithEventContainer) => void;
}) {
    const {workspace, workspaceCapabilities} = useWorkspace();
    const {archiveDropdownItem, archiveModals} = useEventArchiveAction({
        event: props.row,
        queryKeyPrefix: props.dataViewerQueryKeyPrefix,
        menuItemLabel: isEmotoradWorkspace(workspace) === true && props.eventTemplateId === (EmotoradEventTypes.sales as Uuid) ? "Delete" : undefined,
    });

    const visibleAdditionalConfigs = filterAdditionalButtonsVisibleForEvent({
        additionalButtons: props.additionalButtonConfig,
        event: props.row,
        currentUserTemplateIds: props.currentUserTemplateIds,
    });

    const configActions = visibleAdditionalConfigs.map((config) => (
        <EventAdditionalButtonTrigger
            key={config.id}
            config={config}
            event={props.row}
            layout="dropdownItem"
            currentUserTemplateIds={props.currentUserTemplateIds}
            workspaceId={props.workspaceId}
            onOpenModal={props.onOpenAdditionalButtonModal}
            shieldTableRowClick={true}
        />
    ));

    let duluxEditAction: ReactNode = null;
    if (props.eventTemplateId === (DuluxEventTypes.Calls as Uuid)) {
        const status = props.row.additionalColumnValues[DULUX__EVENT__CALLS__CALL_STATUS__COLUMN_ID];
        if (status === CallStatusEnum.SCHEDULED) {
            duluxEditAction = <DuluxScheduledCallTableEditButton event={props.row} />;
        }
    }
    if (duluxEditAction === null && props.eventTemplateId === (DuluxEventTypes.Appointments as Uuid)) {
        const status = props.row.additionalColumnValues[DULUX__EVENT__APPOINTMENT__APPOINTMENT_STATUS__COLUMN_ID];
        if (status === AppointmentStatusEnum.SCHEDULED) {
            duluxEditAction = <DuluxScheduledAppointmentTableEditButton event={props.row} />;
        }
    }

    let emotoradLeadDispositionAction: ReactNode = null;
    if (
        isEmotoradWorkspace(workspace) === true &&
        props.eventTemplateId === (EmotoradEventTypes.lead as Uuid) &&
        props.onOpenLeadDispositionModal !== undefined
    ) {
        emotoradLeadDispositionAction = (
            <AnankeIconButton
                type="button"
                spacing="compact"
                icon={AddIcon}
                label="Disposition"
                appearance="subtle"
                onClick={(e) => {
                    e.stopPropagation();
                    const native = e.nativeEvent as MouseEvent & {preventOnRowClicked?: boolean};
                    native.preventOnRowClicked = true;
                    props.onOpenLeadDispositionModal!(props.row);
                }}
            />
        );
    }

    let amaronDisposeAction: ReactNode = null;
    if (
        isAmaronWorkspace(workspaceCapabilities) === true &&
        props.eventTemplateId === (AmaronEventTypes.Enquiry as Uuid) &&
        props.onOpenAmaronDispositionModal !== undefined
    ) {
        amaronDisposeAction = (
            <AnankeDropdownItem
                onClick={(e) => {
                    e.stopPropagation();
                    const native = e.nativeEvent as MouseEvent & {preventOnRowClicked?: boolean};
                    native.preventOnRowClicked = true;
                    props.onOpenAmaronDispositionModal!(props.row);
                }}
            >
                Dispose
            </AnankeDropdownItem>
        );
    }

    if (
        configActions.length === 0 &&
        duluxEditAction === null &&
        archiveDropdownItem === null &&
        emotoradLeadDispositionAction === null &&
        amaronDisposeAction === null
    ) {
        return null;
    }

    return (
        <div className="flex items-center gap-1">
            {emotoradLeadDispositionAction}
            {duluxEditAction}
            {configActions.length > 0 || archiveDropdownItem !== null ? (
                <AnankeTempDropdownMenuWithIconButtonTrigger
                    iconButtonProps={{
                        type: "button",
                        icon: ShowMoreHorizontalIcon,
                        label: "More actions",
                        appearance: "subtle",
                        spacing: "compact",
                    }}
                >
                    <AnankeDropdownItemGroup>
                        {configActions}
                        {amaronDisposeAction}
                        {archiveDropdownItem}
                    </AnankeDropdownItemGroup>
                </AnankeTempDropdownMenuWithIconButtonTrigger>
            ) : amaronDisposeAction !== null ? (
                <AnankeTempDropdownMenuWithIconButtonTrigger
                    iconButtonProps={{
                        type: "button",
                        icon: ShowMoreHorizontalIcon,
                        label: "More actions",
                        appearance: "subtle",
                        spacing: "compact",
                    }}
                >
                    <AnankeDropdownItemGroup>{amaronDisposeAction}</AnankeDropdownItemGroup>
                </AnankeTempDropdownMenuWithIconButtonTrigger>
            ) : null}
            {archiveModals}
        </div>
    );
}

void _EventTableRowAdditionalActions;

export function EventsRouteComponent({
    dataSlice = "active",
    eventTemplateId,
    eventTemplate: eventTemplateFromLoader,
}: {
    dataSlice?: ModuleDataSlice;
    eventTemplateId: Uuid;
    eventTemplate: OttopilotEventTemplate;
}) {
    const getAdditionalColumns = useCapabilityUdoColumns();
    const search = useSearch({strict: false}) as {
        view?: "list" | "table" | "kanban" | "calendar" | "create";
        statusId?: string;
    };
    const isArchivedSlice = dataSlice === "archived";
    const isMobile = useCheckMobile();
    const [moreActionsDrawerOpen, setMoreActionsDrawerOpen] = useState(false);
    const [exportDrawerOpen, setExportDrawerOpen] = useState(false);

    const journeysModuleConfiguration = useOnlyEventContainerTemplate();
    const {intellsysBaseUrl} = useOttopilotConfiguration();
    const {heimdallUser, workspaceUser} = useWorkspaceUser();
    const {
        workspace,
        workspaceCapabilities,
        workspaceCapabilityAccessMatrix,
        userDefinedObjectOptionsByTemplateId,
    } = useWorkspace();
    const calendarOrchestrationEligibility = useMemo(
        () => resolveCalendarOrchestrationEligibility({workspaceCapabilities, workspaceCapabilityAccessMatrix}),
        [workspaceCapabilities, workspaceCapabilityAccessMatrix],
    );
    const queryClient = useQueryClient();
    const platformUserDetails = usePlatformUserDetails();
    const platformUserDetailsByUserId = useMemo(() => {
        return new Map(platformUserDetails.map((platformUser) => [platformUser.userId, platformUser]));
    }, [platformUserDetails]);
    const eventTemplate = useMemo(
        () => withAmaronEventArchiveStatusColumnConfig(workspaceCapabilities, eventTemplateFromLoader),
        [workspaceCapabilities, eventTemplateFromLoader],
    );
    const eventsPagePluralTitle =
        isAmaronWorkspace(workspaceCapabilities) === true
            ? normalizeAmaronCallsPluralDisplayLabel(eventTemplate.configuration.displayNameVariants.pluralSentenceCase)
            : eventTemplate.configuration.displayNameVariants.pluralSentenceCase;
    const eventSliceConfig = useMemo(
        () =>
            resolveEventModuleSliceConfig({
                slice: dataSlice,
                eventTemplate: eventTemplate,
                activePageTitle: eventsPagePluralTitle,
                activeRoutePath: `/w/${workspace.id}/ottopilot/events/${eventTemplateId}`,
            }),
        [dataSlice, eventTemplate, eventsPagePluralTitle, workspace.id, eventTemplateId],
    );
    const currentUserTemplateIds = useMemo(() => {
        return new Set<Uuid>([workspaceUser.templateId]);
    }, [workspaceUser.templateId]);
    const eventsDataViewerQueryKeyPrefix = useMemo(() => {
        return [uniqueUuid`7ca3b55c-03c6-431d-a657-81c6c9a35fe7`, eventSliceConfig.queryKeySegment, workspace.id, eventTemplateId];
    }, [eventTemplateId, eventSliceConfig.queryKeySegment, workspace.id]);
    const applyEventsApiFilterConfiguration = (filterConfiguration: FilterConfiguration): FilterConfiguration =>
        mergeModuleSliceMandatoryFilters(filterConfiguration, eventSliceConfig.mandatoryFilters);
    const navigate = useNavigate();
    const users = useUsers();
    const userTemplates = useUserTemplates();
    const btlActivities = useBtlActivities() as Array<BtlActivity>;
    const organizationalUnits = useOrganizationalUnits();
    const organizationalUnitTemplates = useOrganizationalUnitTemplates();
    const eventTemplates = useEventTemplates();
    const products = useProductCatalogue() ?? [];
    const productCatalogueTemplates = useProductCatalogueTemplates();
    const effectiveEventTemplateForViewPanel = useMemo(() => {
        if (isAmaronWorkspace(workspaceCapabilities) === true) {
            const amaronEventTemplate = eventTemplate.id === (AmaronEventTypes.Calls as Uuid)
                ? buildAmaronCallViewPanelTemplate(getAdditionalColumns, {eventTemplate, journeysModuleConfiguration})
                : eventTemplate.id === (AmaronEventTypes.Opportunity as Uuid)
                ? buildAmaronOpportunityViewPanelTemplate(getAdditionalColumns, {eventTemplate, journeysModuleConfiguration})
                : eventTemplate.id === (AmaronEventTypes.Service as Uuid)
                ? buildAmaronServiceEventTemplate({eventTemplate})
                : eventTemplate;
            return ensureAmaronEventAttributionViewPanel(amaronEventTemplate);
        }
        if (isEmotoradWorkspace(workspace) !== true) {
            return eventTemplate;
        }
        if (
            eventTemplate.id !== (EmotoradEventTypes.testride as Uuid) &&
            eventTemplate.id !== (EmotoradEventTypes.sales as Uuid) &&
            eventTemplate.id !== (EmotoradEventTypes.call as Uuid) &&
            eventTemplate.id !== (EmotoradEventTypes.walkin as Uuid) &&
            eventTemplate.id !== (EmotoradEventTypes.lead as Uuid)
        ) {
            return eventTemplate;
        }
        return buildEmotradLeadViewPanelTemplate(getAdditionalColumns, {
            eventTemplate: buildEmotradWalkinViewPanelTemplate(getAdditionalColumns, {
                eventTemplate: buildEmotradCallViewPanelTemplate(getAdditionalColumns, {
                    eventTemplate: buildEmotradSalesViewPanelTemplate(getAdditionalColumns, {
                        eventTemplate: buildEmotradTestrideViewPanelTemplate(getAdditionalColumns, {
                            eventTemplate,
                            journeysModuleConfiguration,
                        }),
                        journeysModuleConfiguration,
                    }),
                    journeysModuleConfiguration,
                }),
                journeysModuleConfiguration,
            }),
            journeysModuleConfiguration,
        });
    }, [getAdditionalColumns, workspace, workspaceCapabilities, eventTemplate, journeysModuleConfiguration]);
    const effectiveEventTemplateForEditPanel = useMemo(() => {
        if (isAmaronWorkspace(workspaceCapabilities) === true) {
            let amaronEventTemplate = eventTemplate;
            if (eventTemplate.id === (AmaronEventTypes.Calls as Uuid)) {
                amaronEventTemplate = buildAmaronCallEditPanelTemplate(getAdditionalColumns, {eventTemplate, journeysModuleConfiguration});
            }
            if (eventTemplate.id === (AmaronEventTypes.Enquiry as Uuid)) {
                amaronEventTemplate = buildAmaronEnquiryEditPanelTemplate({eventTemplate});
            }
            return ensureAmaronEventAttributionEditPanel(amaronEventTemplate);
        }
        if (isDuluxWorkspace(workspaceCapabilities) === true) {
            if (eventTemplate.id === (DuluxEventTypes.Calls as Uuid)) {
                return buildDuluxCallEditPanelTemplate({eventTemplate});
            }
            return eventTemplate;
        }
        if (isEmotoradWorkspace(workspace) !== true) {
            return eventTemplate;
        }
        if (
            eventTemplate.id !== (EmotoradEventTypes.testride as Uuid) &&
            eventTemplate.id !== (EmotoradEventTypes.sales as Uuid) &&
            eventTemplate.id !== (EmotoradEventTypes.call as Uuid) &&
            eventTemplate.id !== (EmotoradEventTypes.walkin as Uuid) &&
            eventTemplate.id !== (EmotoradEventTypes.lead as Uuid)
        ) {
            return eventTemplate;
        }
        return buildEmotradLeadEditPanelTemplate(getAdditionalColumns, {
            eventTemplate: buildEmotradWalkinEditPanelTemplate(getAdditionalColumns, {
                eventTemplate: buildEmotradCallEditPanelTemplate(getAdditionalColumns, {
                    eventTemplate: buildEmotradSalesEditPanelTemplate(getAdditionalColumns, {
                        eventTemplate: buildEmotradTestrideEditPanelTemplate(getAdditionalColumns, {
                            eventTemplate,
                            journeysModuleConfiguration,
                        }),
                        journeysModuleConfiguration,
                    }),
                    journeysModuleConfiguration,
                }),
                journeysModuleConfiguration,
            }),
            journeysModuleConfiguration,
        });
    }, [getAdditionalColumns, workspace, workspaceCapabilities, eventTemplate, journeysModuleConfiguration]);

    const effectiveEventTemplateForTableView = useMemo<OttopilotEventTemplate>(() => {
        if (isAmaronWorkspace(workspaceCapabilities) === true) {
            return buildAmaronServiceEventTemplate({eventTemplate});
        }
        if (isBobcardWorkspace(workspace) === true) {
            return buildBobcardApplicationsInitiatedTableColumns({eventTemplate});
        }
        if (isEmotoradWorkspace(workspace) !== true) {
            return buildEmotradLeadTableColumns(getAdditionalColumns, {
                eventTemplate: buildEmotradWalkinTableColumns(getAdditionalColumns, {
                    eventTemplate: buildEmotradCallTableColumns(getAdditionalColumns, {
                        eventTemplate: buildEmotradSalesTableColumns(getAdditionalColumns, {
                            eventTemplate: buildEmotradTestrideTableColumns(getAdditionalColumns, {
                                eventTemplate,
                                journeysModuleConfiguration,
                            }),
                            journeysModuleConfiguration,
                        }),
                        journeysModuleConfiguration,
                    }),
                    journeysModuleConfiguration,
                }),
                journeysModuleConfiguration,
            });
        }
        if (
            eventTemplate.id !== (EmotoradEventTypes.testride as Uuid) &&
            eventTemplate.id !== (EmotoradEventTypes.sales as Uuid) &&
            eventTemplate.id !== (EmotoradEventTypes.call as Uuid) &&
            eventTemplate.id !== (EmotoradEventTypes.walkin as Uuid) &&
            eventTemplate.id !== (EmotoradEventTypes.lead as Uuid)
        ) {
            return eventTemplate;
        }
        return buildEmotradLeadTableColumns(getAdditionalColumns, {
            eventTemplate: buildEmotradWalkinTableColumns(getAdditionalColumns, {
                eventTemplate: buildEmotradCallTableColumns(getAdditionalColumns, {
                    eventTemplate: buildEmotradSalesTableColumns(getAdditionalColumns, {
                        eventTemplate: buildEmotradTestrideTableColumns(getAdditionalColumns, {
                            eventTemplate,
                            journeysModuleConfiguration,
                        }),
                        journeysModuleConfiguration,
                    }),
                    journeysModuleConfiguration,
                }),
                journeysModuleConfiguration,
            }),
            journeysModuleConfiguration,
        });
    }, [getAdditionalColumns, workspace, eventTemplate, journeysModuleConfiguration]);

    const tableViewColumnsById = useMemo(
        () => new Map((effectiveEventTemplateForTableView.configuration.eventPage?.tableViewColumns ?? []).map((column) => [column.id, column])),
        [effectiveEventTemplateForTableView.configuration.eventPage?.tableViewColumns],
    );

    const resolveTableColumnField = (columnId: string): string => {
        return tableViewColumnsById.get(columnId as Uuid)?.field ?? columnId;
    };

    const resolveTableColumnConfig = (columnId: string) => {
        return tableViewColumnsById.get(columnId as Uuid);
    };

    const emotoradCreatedByRoleColumnIds = useMemo(
        () => new Set([String(EMOTORAD__EVENT__SALES__CREATED_BY_ROLE__COLUMN_ID), String(EMOTORAD__EVENT__ENQUIRY__CREATED_BY_ROLE__COLUMN_ID)]),
        [],
    );

    const emotoradCreatedByNameColumnIds = useMemo(
        () => new Set([String(EMOTORAD__EVENT__SALES__CREATED_BY_NAME__COLUMN_ID), String(EMOTORAD__EVENT__ENQUIRY__CREATED_BY_NAME__COLUMN_ID)]),
        [],
    );

    /** Valid UUID sentinel so Postgres `created_by IN (...)` never receives a non-uuid string. */
    const emotoradCreatedByNoMatchUserId = "00000000-0000-0000-0000-000000000000" as Uuid;

    const isEmotoradCreatedByRoleFilterLineItem = (lineItem: FilterConfigurationLineItem): boolean => {
        if (lineItem.operation !== FilterOperation.in) {
            return false;
        }
        const resolvedColumnId = resolveTableColumnField(lineItem.columnId);
        return emotoradCreatedByRoleColumnIds.has(String(lineItem.columnId)) === true || emotoradCreatedByRoleColumnIds.has(resolvedColumnId) === true;
    };

    const isEmotoradCreatedByNameFilterLineItem = (lineItem: FilterConfigurationLineItem): boolean => {
        if (lineItem.operation !== FilterOperation.in) {
            return false;
        }
        const resolvedColumnId = resolveTableColumnField(lineItem.columnId);
        return emotoradCreatedByNameColumnIds.has(String(lineItem.columnId)) === true || emotoradCreatedByNameColumnIds.has(resolvedColumnId) === true;
    };

    const isEmotoradCreatedByRoleEmptyFilterApplied = (filterConfiguration: FilterConfiguration): boolean => {
        const roleFilterLineItem = filterConfiguration.lineItems.find(isEmotoradCreatedByRoleFilterLineItem);
        if (roleFilterLineItem === undefined || roleFilterLineItem.operation !== FilterOperation.in) {
            return false;
        }
        if (roleFilterLineItem.values.length === 0) {
            return true;
        }
        const matchingUserIds = users.filter((user) => roleFilterLineItem.values.includes(String(user.templateId))).map((user) => String(user.userId));
        return matchingUserIds.length === 0;
    };

    const isEmotoradCreatedByNameEmptyFilterApplied = (filterConfiguration: FilterConfiguration): boolean => {
        const nameFilterLineItem = filterConfiguration.lineItems.find(isEmotoradCreatedByNameFilterLineItem);
        if (nameFilterLineItem === undefined || nameFilterLineItem.operation !== FilterOperation.in) {
            return false;
        }
        return nameFilterLineItem.values.length === 0;
    };

    const isEmotoradCreatedBySyntheticEmptyFilterApplied = (filterConfiguration: FilterConfiguration): boolean =>
        isEmotoradCreatedByRoleEmptyFilterApplied(filterConfiguration) === true || isEmotoradCreatedByNameEmptyFilterApplied(filterConfiguration) === true;

    const isEmotoradCustomerZoneFilterLineItem = (lineItem: FilterConfigurationLineItem): boolean => {
        if (lineItem.operation !== FilterOperation.in) {
            return false;
        }
        const resolvedColumnId = resolveTableColumnField(lineItem.columnId);
        return (
            String(lineItem.columnId) === String(EMOTORAD__EVENT_CONTAINER__CUSTOMER_ZONE__COLUMN_ID) ||
            resolvedColumnId === String(EMOTORAD__EVENT_CONTAINER__CUSTOMER_ZONE__COLUMN_ID)
        );
    };

    const isEmotoradCustomerZoneEmptyFilterApplied = (filterConfiguration: FilterConfiguration): boolean => {
        const zoneFilterLineItem = filterConfiguration.lineItems.find(isEmotoradCustomerZoneFilterLineItem);
        if (zoneFilterLineItem === undefined || zoneFilterLineItem.operation !== FilterOperation.in) {
            return false;
        }
        return zoneFilterLineItem.values.length === 0;
    };

    const isEmotoradSyntheticEmptyFilterApplied = (filterConfiguration: FilterConfiguration): boolean =>
        isEmotoradCreatedBySyntheticEmptyFilterApplied(filterConfiguration) === true || isEmotoradCustomerZoneEmptyFilterApplied(filterConfiguration) === true;

    const fetchEmotoradTemplateEventsForFilters = async (): Promise<Array<EventWithEventContainer>> => {
        const eventsResult = await getEventsWithEventContainers({
            accessToken: heimdallUser.accessToken,
            intellsysBaseUrl: intellsysBaseUrl,
            filterConfiguration: {
                revisionId: generateUuid(),
                lineItems: [
                    {
                        columnId: "templateId",
                        operation: FilterOperation.equals,
                        value: String(eventTemplateId),
                    },
                ],
            },
            sortConfiguration: {
                revisionId: generateUuid(),
                lineItems: [],
            },
            searchConfiguration: {
                revisionId: generateUuid(),
                searchValue: "",
                searchColumns: [],
            },
            limit: 10000,
            offset: 0,
            workspaceId: workspace.id,
            eventTemplateId: eventTemplateId,
            eventTemplateAdditionalColumnIds: getAdditionalColumns(eventTemplate).map((col) => col.id),
            eventContainerAdditionalColumnIds: getAdditionalColumns(journeysModuleConfiguration).map((col) => col.id),
        });
        if (eventsResult.success !== true) {
            throw eventsResult.error;
        }
        if (isEmotoradJourneysCustomer === true) {
            return eventsResult.data.map((event) => emotoradEventSyntheticFunction(event, eventTemplate, journeysModuleConfiguration));
        }
        return eventsResult.data;
    };

    const loadEmotoradLoggedTestRideLookup = async (): Promise<
        ReturnType<typeof collectEmotoradLoggedTestRideLookupForCalls> | null
    > => {
        const testRideTemplateId = EmotoradEventTypes.testride as Uuid;
        const eventContainerAdditionalColumnIds = getAdditionalColumns(journeysModuleConfiguration).map((col) => col.id);
        const testRidesResult = await getEventsWithEventContainers({
            accessToken: heimdallUser.accessToken,
            intellsysBaseUrl: intellsysBaseUrl,
            filterConfiguration: {
                revisionId: generateUuid(),
                lineItems: [
                    {
                        columnId: "templateId",
                        operation: FilterOperation.equals,
                        value: String(testRideTemplateId),
                    },
                ],
            },
            sortConfiguration: {revisionId: generateUuid(), lineItems: []},
            searchConfiguration: {revisionId: generateUuid(), searchValue: "", searchColumns: []},
            limit: 10000,
            offset: 0,
            workspaceId: workspace.id,
            eventTemplateId: eventTemplateId,
            eventTemplateAdditionalColumnIds: getAdditionalColumns(eventTemplate).map((col) => col.id),
            eventContainerAdditionalColumnIds: eventContainerAdditionalColumnIds,
        });
        if (testRidesResult.success !== true) {
            return null;
        }
        const syntheticTestRides =
            isEmotoradJourneysCustomer === true
                ? testRidesResult.data.map((event) => emotoradEventSyntheticFunction(event, eventTemplate, journeysModuleConfiguration))
                : testRidesResult.data;
        return collectEmotoradLoggedTestRideLookupForCalls(syntheticTestRides);
    };

    const applyEmotoradCallEventsSyntheticTestRideTakenToEvents = async (callEvents: Array<EventWithEventContainer>): Promise<Array<EventWithEventContainer>> => {
        const loggedTestRideLookup = await loadEmotoradLoggedTestRideLookup();
        return loggedTestRideLookup === null
            ? callEvents
            : applyEmotoradCallEventsSyntheticTestRideTakenColumn(callEvents, loggedTestRideLookup);
    };

    const fetchEmotoradCallEventsWithSyntheticTestRideTaken = async (props: {
        filterConfiguration: FilterConfiguration;
        searchConfiguration?: SearchConfiguration;
        limit?: number;
        offset?: number;
    }): Promise<Array<EventWithEventContainer>> => {
        const mappedFilterConfiguration = mapFilterConfigurationToFields(props.filterConfiguration);
        const eventsResult = await getEventsWithEventContainers({
            accessToken: heimdallUser.accessToken,
            intellsysBaseUrl: intellsysBaseUrl,
            filterConfiguration: {
                ...mappedFilterConfiguration,
                lineItems: [
                    ...mappedFilterConfiguration.lineItems,
                    {
                        columnId: "templateId",
                        operation: FilterOperation.equals,
                        value: eventTemplateId,
                    },
                ],
            },
            sortConfiguration: {revisionId: generateUuid(), lineItems: []},
            searchConfiguration: props.searchConfiguration ?? {
                revisionId: generateUuid(),
                searchValue: "",
                searchColumns: [],
            },
            limit: props.limit ?? 10000,
            offset: props.offset ?? 0,
            workspaceId: workspace.id,
            eventTemplateId: eventTemplateId,
            eventTemplateAdditionalColumnIds: getAdditionalColumns(eventTemplate).map((col) => col.id),
            eventContainerAdditionalColumnIds: getAdditionalColumns(journeysModuleConfiguration).map((col) => col.id),
        });
        if (eventsResult.success !== true) {
            throw eventsResult.error;
        }
        let events =
            isEmotoradJourneysCustomer === true
                ? eventsResult.data.map((event) => emotoradEventSyntheticFunction(event, eventTemplate, journeysModuleConfiguration))
                : isAmaronCallTemplate === true
                  ? eventsResult.data.map((event) => amaronCallEventSyntheticFunction(event, eventTemplate))
                  : eventsResult.data;
        return applyEmotoradCallEventsSyntheticTestRideTakenToEvents(events);
    };

    const fetchOrchestratedEmotoradCallEventsWithSyntheticTestRideTaken = async (props: {
        filterConfiguration: FilterConfiguration;
        searchConfiguration?: SearchConfiguration;
        limit?: number;
        offset?: number;
    }): Promise<Array<EventWithEventContainer>> => {
        const mappedFilterConfiguration = mapFilterConfigurationToFields(props.filterConfiguration);
        const eventsResult = await getOrchestratedEventsWithEventContainers({
            accessToken: heimdallUser.accessToken,
            columns: null,
            eventContainerAdditionalColumnIds: getAdditionalColumns(journeysModuleConfiguration).map((col) => col.id),
            eventTemplateAdditionalColumnIds: getAdditionalColumns(eventTemplate).map((col) => col.id),
            filterConfiguration: {
                ...mappedFilterConfiguration,
                lineItems: [
                    ...mappedFilterConfiguration.lineItems,
                    {
                        columnId: "templateId",
                        operation: FilterOperation.equals,
                        value: eventTemplateId,
                    },
                ],
            },
            intellsysBaseUrl,
            limit: props.limit ?? 10000,
            maxRowsPerDay: null,
            maxRowsPerDayColumn: null,
            offset: props.offset ?? 0,
            searchConfiguration: props.searchConfiguration ?? {
                revisionId: generateUuid(),
                searchValue: "",
                searchColumns: [],
            },
            sortConfiguration: {revisionId: generateUuid(), lineItems: []},
            templateId: eventTemplateId,
            workspaceId: workspace.id,
        });
        if (eventsResult.success !== true) {
            throw eventsResult.error;
        }
        const events =
            isEmotoradJourneysCustomer === true
                ? eventsResult.data.map((event) =>
                      emotoradEventSyntheticFunction(event, eventTemplate, journeysModuleConfiguration),
                  )
                : eventsResult.data;
        return applyEmotoradCallEventsSyntheticTestRideTakenToEvents(events);
    };

    const getEmotoradCallsTestRideTakenFilterValues = async (
        filterConfiguration: FilterConfiguration,
        searchConfiguration?: SearchConfiguration,
    ): Promise<Array<string>> => {
        const events = await fetchEmotoradCallEventsWithSyntheticTestRideTaken({
            filterConfiguration: filterConfiguration,
            searchConfiguration: searchConfiguration,
            limit: 10000,
            offset: 0,
        });
        const values = new Set<string>();
        for (const option of getEmotoradYesNoSelectOptions()) {
            values.add(String(option.id));
        }
        for (const event of events) {
            const raw = event.additionalColumnValues[EMOTORAD__EVENT__CALLS__TEST_RIDE_TAKEN__COLUMN_ID];
            if (raw !== null && raw !== undefined && String(raw).trim() !== "") {
                values.add(String(raw).trim());
            }
        }
        return Array.from(values).sort((left, right) => {
            const leftLabel = Object.values(emotoradYesNoOptions).find((option) => String(option.id) === left)?.displayName ?? left;
            const rightLabel = Object.values(emotoradYesNoOptions).find((option) => String(option.id) === right)?.displayName ?? right;
            return leftLabel.localeCompare(rightLabel);
        });
    };

    const getEmotoradEventCreatorRoleTemplateIds = async (): Promise<Array<string>> => {
        const events = await fetchEmotoradTemplateEventsForFilters();
        const templateIds = new Set<string>();
        for (const event of events) {
            const createdByUser = users.find((item) => item.userId === event.createdBy);
            if (createdByUser?.templateId === undefined || createdByUser.templateId === null) {
                continue;
            }
            templateIds.add(String(createdByUser.templateId));
        }
        return Array.from(templateIds).sort((left, right) => {
            const leftName = userTemplates.find((item) => String(item.id) === left)?.displayName ?? left;
            const rightName = userTemplates.find((item) => String(item.id) === right)?.displayName ?? right;
            return leftName.localeCompare(rightName);
        });
    };

    const getEmotoradEventCreatorUserIds = async (): Promise<Array<string>> => {
        const events = await fetchEmotoradTemplateEventsForFilters();
        const userIds = new Set<string>();
        for (const event of events) {
            if (event.createdBy !== undefined && event.createdBy !== null) {
                userIds.add(String(event.createdBy));
            }
        }
        return Array.from(userIds).sort((left, right) => {
            const leftName = getUserDisplayName(left as Uuid, users, userTemplates, platformUserDetailsByUserId);
            const rightName = getUserDisplayName(right as Uuid, users, userTemplates, platformUserDetailsByUserId);
            return leftName.localeCompare(rightName);
        });
    };

    const resolveEmotoradSalesCustomerZone = (event: EventWithEventContainer): string => {
        const rawZone = event.eventContainer.additionalColumnValues[EMOTORAD__EVENT_CONTAINER__CUSTOMER_ZONE__COLUMN_ID];
        if (rawZone !== undefined && rawZone !== null && String(rawZone).trim() !== "") {
            return normalizeEmotoradCustomerZone(String(rawZone));
        }
        const rawDealer = event.eventContainer.additionalColumnValues[EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_NAME__COLUMN_ID];
        const dealerId = typeof rawDealer === "string" && rawDealer.trim() !== "" ? (rawDealer as Uuid) : null;
        const fromOuTree = resolveAssignedDealerZoneFromOuAncestorTree(dealerId);
        if (fromOuTree !== "") {
            return normalizeEmotoradCustomerZone(fromOuTree);
        }
        return normalizeEmotoradCustomerZone(getDealerFieldByLabel(dealerId, ["Zone", "Dealer Zone"]));
    };

    const getEmotoradSalesCustomerZoneFilterValues = async (): Promise<Array<string>> => {
        const events = await fetchEmotoradTemplateEventsForFilters();
        const zones = new Set<string>();
        for (const event of events) {
            const zone = resolveEmotoradSalesCustomerZone(event);
            if (zone !== "") {
                zones.add(zone);
            }
        }
        return Array.from(zones).sort((left, right) => left.localeCompare(right));
    };

    const getEmotoradEventUpdaterUserIds = async (): Promise<Array<string>> => {
        const events = await fetchEmotoradTemplateEventsForFilters();
        const userIds = new Set<string>();
        for (const event of events) {
            if (event.updatedBy !== undefined && event.updatedBy !== null) {
                userIds.add(String(event.updatedBy));
            }
        }
        return Array.from(userIds).sort((left, right) => {
            const leftName = getUserDisplayName(left as Uuid, users, userTemplates, platformUserDetailsByUserId);
            const rightName = getUserDisplayName(right as Uuid, users, userTemplates, platformUserDetailsByUserId);
            return leftName.localeCompare(rightName);
        });
    };

    const EVENT_AON_CONFIGS: Array<AonWorkspaceConfig> = [emotoradJourneyAonConfig, emotoradEnquiryAonConfig];

    const mapFilterConfigurationToFields = (filterConfiguration: FilterConfiguration): FilterConfiguration => ({
        ...filterConfiguration,
        lineItems: filterConfiguration.lineItems.flatMap((lineItem) => {
            if (isEmotoradCreatedByRoleFilterLineItem(lineItem) === true && isFilterInLineItem(lineItem) === true) {
                const selectedTemplateIds = lineItem.values;
                if (selectedTemplateIds.length === 0) {
                    return [];
                }
                const matchingUserIds = users.filter((user) => selectedTemplateIds.includes(String(user.templateId))).map((user) => String(user.userId));
                if (matchingUserIds.length === 0) {
                    return [
                        {
                            columnId: "createdBy",
                            operation: FilterOperation.in,
                            values: [String(emotoradCreatedByNoMatchUserId)],
                        },
                    ];
                }
                return [
                    {
                        columnId: "createdBy",
                        operation: FilterOperation.in,
                        values: matchingUserIds,
                    },
                ];
            }
            if (isEmotoradCreatedByNameFilterLineItem(lineItem) === true && isFilterInLineItem(lineItem) === true) {
                const selectedUserIds = lineItem.values;
                if (selectedUserIds.length === 0) {
                    return [];
                }
                return [
                    {
                        columnId: "createdBy",
                        operation: FilterOperation.in,
                        values: selectedUserIds,
                    },
                ];
            }
            if (isEmotoradCustomerZoneFilterLineItem(lineItem) === true && isFilterInLineItem(lineItem) === true) {
                const resolvedCustomerZoneColumnId = resolveTableColumnField(lineItem.columnId);
                const selectedZones = lineItem.values;
                if (selectedZones.length === 0) {
                    return [];
                }
                return [
                    {
                        columnId: resolvedCustomerZoneColumnId,
                        operation: FilterOperation.in,
                        values: expandEmotoradCustomerZoneFilterValues(selectedZones),
                    },
                ];
            }
            if (isEmotoradComputedJourneyFilterLineItem(lineItem) === true && isFilterInLineItem(lineItem) === true) {
                const selectedValues = lineItem.values;
                if (selectedValues.length === 0) {
                    return [];
                }
                const matchingDealerIds = findEmotoradDealerIdsMatchingFieldFilter(resolveTableColumnField(lineItem.columnId), selectedValues);
                if (matchingDealerIds.length === 0) {
                    return [
                        {
                            columnId: EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_NAME__COLUMN_ID,
                            operation: FilterOperation.in,
                            values: [String(emotoradCreatedByNoMatchUserId)],
                        },
                    ];
                }
                return [
                    {
                        columnId: EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_NAME__COLUMN_ID,
                        operation: FilterOperation.in,
                        values: matchingDealerIds,
                    },
                ];
            }
            if (isEmotoradCallsTestRideTakenFilterLineItem(lineItem) === true) {
                return [];
            }
            let mappedLineItems: Array<FilterConfigurationLineItem> = [lineItem];
            for (const aonConfig of EVENT_AON_CONFIGS) {
                mappedLineItems = mappedLineItems.flatMap((candidate) => mapAonFilterLineItemForApi(aonConfig, candidate));
            }
            return mappedLineItems.map((mappedLineItem) => {
                const isAonRangesLineItem = EVENT_AON_CONFIGS.some((aonConfig) => isAonFilterRangesLineItem(aonConfig, mappedLineItem));
                if (isAonRangesLineItem === true) {
                    return mappedLineItem;
                }
                return {
                    ...mappedLineItem,
                    columnId: resolveTableColumnField(mappedLineItem.columnId),
                };
            });
        }),
    });

    const mapSortConfigurationToFields = (sortConfiguration: SortConfiguration): SortConfiguration => ({
        ...sortConfiguration,
        lineItems: sortConfiguration.lineItems.map((lineItem) => ({
            ...lineItem,
            columnId: resolveTableColumnField(lineItem.columnId),
        })),
    });

    const hasEventCalendarConfig = eventTemplate.configuration.eventPage.calendarConfig !== undefined;
    const initialViewId =
        search.view === "list"
            ? EVENTS_LIST_VIEW_ID
            : search.view === "kanban"
              ? EVENTS_KANBAN_VIEW_ID
              : search.view === "calendar" && hasEventCalendarConfig
                ? EVENTS_CALENDAR_VIEW_ID
                : EVENTS_TABLE_VIEW_ID;
    // Sync controller view when URL search.view changes (e.g. sidebar Table/Kanban link)
    // This effect must run AFTER eventsDataViewerController is defined below, so we reference it via a ref.
    // We use a separate useEffect after the controller is created (see below, after controller definition).

    const getOuDisplayName = useMemo(() => {
        return (ouId: Uuid): string => {
            const ou = organizationalUnits.find((unit) => unit.id === ouId);
            if (ou === undefined) {
                return "-";
            }
            const template = organizationalUnitTemplates.find((t) => t.id === ou.templateId);
            if (template === undefined) {
                return "-";
            }
            const nameColumn = template.additionalColumns.find((col) => col.displayName === "Name");
            if (nameColumn === undefined) {
                return "-";
            }
            const nameValue = ou.additionalColumnValues[nameColumn.id];
            return typeof nameValue === "string" && nameValue !== "" ? nameValue : "-";
        };
    }, [organizationalUnits, organizationalUnitTemplates]);
    const getDealerFieldByLabel = useMemo(() => {
        return (dealerId: Uuid | null, labels: ReadonlyArray<string>): string => {
            if (dealerId === null) return "";
            const dealerOu = organizationalUnits.find((unit) => unit.id === dealerId);
            if (dealerOu === undefined) return "";
            const template = organizationalUnitTemplates.find((item) => item.id === dealerOu.templateId);
            if (template === undefined) return "";
            for (const label of labels) {
                const matchingColumn = template.additionalColumns.find((column) => {
                    return column.displayName.trim().toLowerCase() === label.toLowerCase();
                });
                if (matchingColumn === undefined) continue;
                const rawValue = dealerOu.additionalColumnValues[matchingColumn.id];
                if (rawValue !== null && rawValue !== undefined && String(rawValue).trim() !== "") {
                    return String(rawValue);
                }
            }
            return "";
        };
    }, [organizationalUnits, organizationalUnitTemplates]);
    const resolveDealerFieldLikeLifecycleOverview = useMemo(() => {
        const findOrganizationalUnitByPossibleId = (raw: unknown) => {
            if (typeof raw !== "string" || raw.trim() === "") return undefined;
            return organizationalUnits.find((unit) => unit.id === (raw as Uuid));
        };
        const getOrganizationalUnitFieldValueByDisplayNames = (
            unit: (typeof organizationalUnits)[number],
            displayNames: ReadonlyArray<string>,
        ): string | null => {
            const template = organizationalUnitTemplates.find((item) => item.id === unit.templateId);
            if (template === undefined) return null;
            for (const displayName of displayNames) {
                const matchingColumn = template.additionalColumns.find((column) => {
                    return column.displayName.trim().toLowerCase() === displayName.toLowerCase();
                });
                if (matchingColumn === undefined) continue;
                const rawValue = unit.additionalColumnValues[matchingColumn.id];
                if (typeof rawValue === "string" && rawValue.trim() !== "") return rawValue.trim();
            }
            return null;
        };
        const getOrganizationalUnitFieldValueByHeuristics = (
            unit: (typeof organizationalUnits)[number],
            field: "contact" | "email" | "pincode",
        ): string | null => {
            const template = organizationalUnitTemplates.find((item) => item.id === unit.templateId);
            if (template === undefined) return null;
            const fieldKeywords: Record<typeof field, ReadonlyArray<string>> = {
                contact: ["contact", "phone", "mobile", "whatsapp", "number"],
                email: ["email", "mail"],
                pincode: ["pincode", "pin code", "pin", "zip"],
            };
            const isLikelyValueForField = (value: string): boolean => {
                const normalized = value.trim();
                if (normalized === "") return false;
                if (field === "email") return normalized.includes("@");
                if (field === "pincode") return /^\d{5,8}$/.test(normalized.replace(/\s+/g, ""));
                const digitsOnly = normalized.replace(/[^\d]/g, "");
                return digitsOnly.length >= 8;
            };
            const keywords = fieldKeywords[field] ?? [];
            for (const column of template.additionalColumns) {
                const displayName = column.displayName.trim().toLowerCase();
                const matchesKeyword = keywords.some((keyword) => displayName.includes(keyword));
                if (matchesKeyword !== true) continue;
                const rawValue = unit.additionalColumnValues[column.id];
                if (typeof rawValue === "string" && rawValue.trim() !== "") return rawValue.trim();
            }
            for (const column of template.additionalColumns) {
                const rawValue = unit.additionalColumnValues[column.id];
                if (typeof rawValue !== "string") continue;
                const normalized = rawValue.trim();
                if (isLikelyValueForField(normalized) === true) return normalized;
            }
            return null;
        };
        const extractDealershipIdsFromUserValue = (raw: unknown): Uuid[] => {
            if (typeof raw === "string" && raw.trim() !== "") return [raw as Uuid];
            if (Array.isArray(raw) === true) {
                const ids = raw
                    .map((item) => {
                        if (typeof item === "string" && item.trim() !== "") return item as Uuid;
                        if (typeof item === "object" && item !== null) {
                            const obj = item as Record<string, unknown>;
                            const candidate = obj.id ?? obj.value;
                            if (typeof candidate === "string" && candidate.trim() !== "") return candidate as Uuid;
                        }
                        return null;
                    })
                    .filter((item): item is Uuid => item !== null);
                return [...new Set(ids)];
            }
            if (typeof raw === "object" && raw !== null) {
                const obj = raw as Record<string, unknown>;
                const candidate = obj.id ?? obj.value;
                if (typeof candidate === "string" && candidate.trim() !== "") return [candidate as Uuid];
            }
            return [];
        };
        const getFieldFromDealershipUsers = (dealerOuId: Uuid | null, field: "contact" | "email" | "pincode"): string | null => {
            if (dealerOuId === null) return null;
            const fieldColumnId =
                field === "contact"
                    ? EMOTORAD__USER__CONTACT_NUMBER__COLUMN_ID
                    : field === "email"
                      ? EMOTORAD__USER__EMAIL__COLUMN_ID
                      : EMOTORAD__USER__ADDRESS_PINCODE__COLUMN_ID;
            for (const user of users) {
                const dealershipRaw = user.additionalColumnValues[EMOTORAD__USER__DEALERSHIP__COLUMN_ID];
                const dealershipIds = extractDealershipIdsFromUserValue(dealershipRaw);
                if (dealershipIds.includes(dealerOuId) !== true) continue;
                const rawValue = user.additionalColumnValues[fieldColumnId];
                if (typeof rawValue === "string" && rawValue.trim() !== "") return rawValue.trim();
            }
            return null;
        };
        return (event: EventWithEventContainer, field: "contact" | "email"): string => {
            const looksLikeUuidString = (s: string): boolean => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s.trim());
            const sanitizeDealerFieldDisplay = (value: string | null): string => {
                if (value === null) {
                    return "";
                }
                const trimmed = value.trim();
                if (trimmed === "") {
                    return "";
                }
                if (looksLikeUuidString(trimmed) === true) {
                    return "";
                }
                return trimmed;
            };
            const assignedDealerRaw = event.eventContainer.additionalColumnValues[EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_NAME__COLUMN_ID];
            const assignedDealerOu =
                findOrganizationalUnitByPossibleId(assignedDealerRaw) ??
                findOrganizationalUnitByPossibleId(getOptionalEventContainerOrganizationalUnitId(event.eventContainer, journeysModuleConfiguration)) ??
                findOrganizationalUnitByPossibleId(event.eventContainer.additionalColumnValues[EMOTORAD__EVENT_CONTAINER__DEALER_PINCODE__COLUMN_ID]) ??
                findOrganizationalUnitByPossibleId(event.eventContainer.additionalColumnValues[EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_CONTACT__COLUMN_ID]) ??
                findOrganizationalUnitByPossibleId(event.eventContainer.additionalColumnValues[EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_EMAIL__COLUMN_ID]);
            const assignedDealerId =
                assignedDealerOu?.id ?? (typeof assignedDealerRaw === "string" && assignedDealerRaw.trim() !== "" ? (assignedDealerRaw as Uuid) : null);
            const rawJourneyValue =
                field === "contact"
                    ? event.eventContainer.additionalColumnValues[EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_CONTACT__COLUMN_ID]
                    : event.eventContainer.additionalColumnValues[EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_EMAIL__COLUMN_ID];
            if (rawJourneyValue !== null && rawJourneyValue !== undefined && String(rawJourneyValue).trim() !== "") {
                const trimmedRaw = String(rawJourneyValue).trim();
                const rawValueOu = findOrganizationalUnitByPossibleId(rawJourneyValue);
                // Same as contact/email in the dealer pincode path: only use raw when it is a real string, not a UUID wrongly stored in the column.
                if (rawValueOu === undefined && looksLikeUuidString(trimmedRaw) !== true) {
                    return sanitizeDealerFieldDisplay(trimmedRaw);
                }
            }
            const displayNames =
                field === "contact"
                    ? ["Contact Number", "Phone Number", "Mobile Number", "Dealer Contact", "Dealer Contact Number"]
                    : ["Email", "Email Address", "Dealer Email"];
            if (assignedDealerOu !== undefined) {
                const visited = new Set<Uuid>();
                let current: (typeof organizationalUnits)[number] | undefined = assignedDealerOu;
                while (current !== undefined && visited.has(current.id) === false) {
                    visited.add(current.id);
                    const byDisplayName = getOrganizationalUnitFieldValueByDisplayNames(current, displayNames);
                    if (byDisplayName !== null) {
                        return sanitizeDealerFieldDisplay(byDisplayName);
                    }
                    const byHeuristic = getOrganizationalUnitFieldValueByHeuristics(current, field);
                    if (byHeuristic !== null) {
                        return sanitizeDealerFieldDisplay(byHeuristic);
                    }
                    current = current.parentId !== null ? organizationalUnits.find((unit) => unit.id === current?.parentId) : undefined;
                }
            }
            return sanitizeDealerFieldDisplay(getFieldFromDealershipUsers(assignedDealerId, field));
        };
    }, [organizationalUnits, organizationalUnitTemplates, users]);

    const resolveEmotoradAssignedDealerId = (event: EventWithEventContainer): Uuid | null => {
        const raw = event.eventContainer.additionalColumnValues[EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_NAME__COLUMN_ID];
        if (typeof raw !== "string" || raw.trim() === "") {
            return null;
        }
        return raw as Uuid;
    };

    const resolveEmotoradDealerCity = (event: EventWithEventContainer): string =>
        getDealerFieldByLabel(resolveEmotoradAssignedDealerId(event), ["City", "Dealer City"]);

    const resolveEmotoradDealerState = (event: EventWithEventContainer): string =>
        getDealerFieldByLabel(resolveEmotoradAssignedDealerId(event), ["State", "Dealer State"]);

    const resolveEmotoradDealerZone = (event: EventWithEventContainer): string => {
        const dealerId = resolveEmotoradAssignedDealerId(event);
        const fromOuTree = resolveAssignedDealerZoneFromOuAncestorTree(dealerId);
        if (fromOuTree !== "") {
            return normalizeEmotoradCustomerZone(fromOuTree);
        }
        return normalizeEmotoradCustomerZone(getDealerFieldByLabel(dealerId, ["Zone", "Dealer Zone", "Zone Name"]));
    };

    const resolveEmotoradDealerContact = (event: EventWithEventContainer): string => resolveDealerFieldLikeLifecycleOverview(event, "contact");

    const resolveEmotoradDealerEmail = (event: EventWithEventContainer): string => resolveDealerFieldLikeLifecycleOverview(event, "email");

    const emotoradComputedJourneyFilterColumnIds = useMemo(
        () =>
            new Set([
                String(EMOTORAD__EVENT_CONTAINER__DEALER_CITY__COLUMN_ID),
                String(EMOTORAD__EVENT_CONTAINER__DEALER_STATE__COLUMN_ID),
                String(EMOTORAD__EVENT_CONTAINER__DEALER_ZONE__COLUMN_ID),
                String(EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_CONTACT__COLUMN_ID),
                String(EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_EMAIL__COLUMN_ID),
            ]),
        [],
    );

    const getEmotoradComputedColumnFilterValues = async (resolveDisplayValue: (event: EventWithEventContainer) => string): Promise<Array<string>> => {
        const events = await fetchEmotoradTemplateEventsForFilters();
        const values = new Set<string>();
        for (const event of events) {
            const value = resolveDisplayValue(event).trim();
            if (value !== "") {
                values.add(value);
            }
        }
        return sanitizeEmotoradFilterColumnValues(Array.from(values).sort((left, right) => left.localeCompare(right)));
    };

    const getEmotoradAdditionalColumnFilterValues = async (columnId: string): Promise<Array<string>> => {
        const isJourneyColumn = getAdditionalColumns(journeysModuleConfiguration).some((col) => col.id === columnId);
        if (isJourneyColumn === true) {
            const journeysColumnValuesResult = await getEventContainerColumnValues({
                workspaceId: workspace.id,
                columnId: columnId,
                intellsysBaseUrl: intellsysBaseUrl,
                accessToken: heimdallUser.accessToken,
                templateId: journeysModuleConfiguration.id,
            });
            if (journeysColumnValuesResult.success !== true) {
                throw journeysColumnValuesResult.error;
            }
            return sanitizeEmotoradFilterColumnValues(journeysColumnValuesResult.data);
        }
        const eventColumnValuesResult = await getEvent_ColumnValues({
            workspaceId: workspace.id,
            columnId: columnId,
            intellsysBaseUrl: intellsysBaseUrl,
            accessToken: heimdallUser.accessToken,
            templateId: eventTemplateId,
        });
        if (eventColumnValuesResult.success !== true) {
            throw eventColumnValuesResult.error;
        }
        return sanitizeEmotoradFilterColumnValues(eventColumnValuesResult.data);
    };

    const resolveEmotoradSyntheticDispositionFilterLevel = (columnId: string): "l1" | "l2" | "l3" | undefined => {
        if (columnId === String(EMOTORAD__EVENT__CALL__L1_DISPOSITION__COLUMN_ID) || columnId === String(EMOTORAD__EVENT__WALKIN__L1_DISPOSITION__COLUMN_ID)) {
            return "l1";
        }
        if (columnId === String(EMOTORAD__EVENT__CALL__L2_DISPOSITION__COLUMN_ID) || columnId === String(EMOTORAD__EVENT__WALKIN__L2_DISPOSITION__COLUMN_ID)) {
            return "l2";
        }
        if (columnId === String(EMOTORAD__EVENT__CALL__L3_DISPOSITION__COLUMN_ID) || columnId === String(EMOTORAD__EVENT__WALKIN__L3_DISPOSITION__COLUMN_ID)) {
            return "l3";
        }
        return undefined;
    };

    const getEmotoradDealerContactOrEmailForDealerOu = (dealerOuId: Uuid, field: "contact" | "email"): string => {
        const stubEvent = {
            eventContainer: {
                additionalColumnValues: {
                    [EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_NAME__COLUMN_ID]: dealerOuId,
                },
            },
        } as unknown as EventWithEventContainer;
        return resolveDealerFieldLikeLifecycleOverview(stubEvent, field);
    };

    const getEmotoradDealerFieldValueForOu = (dealerOuId: Uuid, columnId: string): string => {
        if (columnId === String(EMOTORAD__EVENT_CONTAINER__DEALER_CITY__COLUMN_ID)) {
            return getDealerFieldByLabel(dealerOuId, ["City", "Dealer City"]);
        }
        if (columnId === String(EMOTORAD__EVENT_CONTAINER__DEALER_STATE__COLUMN_ID)) {
            return getDealerFieldByLabel(dealerOuId, ["State", "Dealer State"]);
        }
        if (columnId === String(EMOTORAD__EVENT_CONTAINER__DEALER_ZONE__COLUMN_ID)) {
            const fromOuTree = resolveAssignedDealerZoneFromOuAncestorTree(dealerOuId);
            if (fromOuTree !== "") {
                return normalizeEmotoradCustomerZone(fromOuTree);
            }
            return normalizeEmotoradCustomerZone(getDealerFieldByLabel(dealerOuId, ["Zone", "Dealer Zone", "Zone Name"]));
        }
        if (columnId === String(EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_CONTACT__COLUMN_ID)) {
            return getEmotoradDealerContactOrEmailForDealerOu(dealerOuId, "contact");
        }
        if (columnId === String(EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_EMAIL__COLUMN_ID)) {
            return getEmotoradDealerContactOrEmailForDealerOu(dealerOuId, "email");
        }
        return "";
    };

    const dealerOuZoneMatchesSelectedValues = (ouZoneValue: string, selectedZones: ReadonlyArray<string>): boolean => {
        const trimmedOu = ouZoneValue.trim();
        if (trimmedOu === "") {
            return false;
        }
        for (const selected of selectedZones) {
            const expanded = expandEmotoradCustomerZoneFilterValues([selected]);
            if (expanded.some((zone) => zone.toLowerCase() === trimmedOu.toLowerCase()) === true) {
                return true;
            }
        }
        return false;
    };

    const findEmotoradDealerIdsMatchingFieldFilter = (columnId: string, selectedValues: ReadonlyArray<string>): Array<string> => {
        if (selectedValues.length === 0) {
            return [];
        }
        const matchingDealerIds = new Set<string>();
        for (const ou of organizationalUnits) {
            const fieldValue = getEmotoradDealerFieldValueForOu(ou.id, columnId);
            if (fieldValue.trim() === "") {
                continue;
            }
            const matches =
                columnId === String(EMOTORAD__EVENT_CONTAINER__DEALER_ZONE__COLUMN_ID)
                    ? dealerOuZoneMatchesSelectedValues(fieldValue, selectedValues)
                    : selectedValues.some((selected) => fieldValue.trim().toLowerCase() === selected.trim().toLowerCase());
            if (matches === true) {
                matchingDealerIds.add(String(ou.id));
            }
        }
        return Array.from(matchingDealerIds);
    };

    const isEmotoradComputedJourneyFilterLineItem = (lineItem: FilterConfigurationLineItem): boolean => {
        if (lineItem.operation !== FilterOperation.in) {
            return false;
        }
        const resolvedColumnId = resolveTableColumnField(lineItem.columnId);
        return (
            emotoradComputedJourneyFilterColumnIds.has(String(lineItem.columnId)) === true ||
            emotoradComputedJourneyFilterColumnIds.has(resolvedColumnId) === true
        );
    };

    const isEmotoradComputedJourneyFilterEmptyFilterApplied = (filterConfiguration: FilterConfiguration): boolean => {
        const filterLineItem = filterConfiguration.lineItems.find(isEmotoradComputedJourneyFilterLineItem);
        if (filterLineItem === undefined || filterLineItem.operation !== FilterOperation.in) {
            return false;
        }
        return filterLineItem.values.length === 0;
    };

    const resolveDealerKamRmValue = useMemo(() => {
        return (event: EventWithEventContainer, field: "kam" | "rm"): string => {
            const assignedDealerRaw = event.eventContainer.additionalColumnValues[EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_NAME__COLUMN_ID];
            const assignedDealerId = typeof assignedDealerRaw === "string" && assignedDealerRaw.trim() !== "" ? (assignedDealerRaw as Uuid) : null;
            if (assignedDealerId === null) return "";
            const fieldLabels = field === "kam" ? ["KAM", "KAM Name", "Kam Name"] : ["RM", "RM Name", "Rm Name", "ZSM", "ZSM Name", "Zsm Name"];
            const fieldKeywords = field === "kam" ? ["kam", "key account"] : ["rm", "zsm", "regional", "relationship"];

            const getFromOu = (ouId: Uuid): string | null => {
                const ou = organizationalUnits.find((unit) => unit.id === ouId);
                if (ou === undefined) return null;
                const template = organizationalUnitTemplates.find((item) => item.id === ou.templateId);
                if (template === undefined) return null;
                for (const label of fieldLabels) {
                    const column = template.additionalColumns.find((item) => {
                        return item.displayName.trim().toLowerCase() === label.toLowerCase();
                    });
                    if (column === undefined) continue;
                    const raw = ou.additionalColumnValues[column.id];
                    if (raw !== null && raw !== undefined && String(raw).trim() !== "") return String(raw);
                }
                for (const column of template.additionalColumns) {
                    const normalizedLabel = column.displayName.trim().toLowerCase();
                    if (fieldKeywords.some((keyword) => normalizedLabel.includes(keyword)) !== true) continue;
                    const raw = ou.additionalColumnValues[column.id];
                    if (raw !== null && raw !== undefined && String(raw).trim() !== "") return String(raw);
                }
                return null;
            };

            const visited = new Set<Uuid>();
            let currentId: Uuid | null = assignedDealerId;
            while (currentId !== null && visited.has(currentId) === false) {
                visited.add(currentId);
                const value = getFromOu(currentId);
                if (value !== null) return value;
                const currentOu = organizationalUnits.find((unit) => unit.id === currentId);
                currentId = currentOu?.parentId ?? null;
            }

            for (const user of users) {
                const linkedDealerships = user.additionalColumnValues[EMOTORAD__USER__DEALERSHIP__COLUMN_ID];
                const linkedIds = Array.isArray(linkedDealerships)
                    ? linkedDealerships
                          .map((item) => (typeof item === "string" ? item : ((item as Record<string, unknown>).id ?? (item as Record<string, unknown>).value)))
                          .filter((item): item is string => typeof item === "string")
                    : [
                          typeof linkedDealerships === "string"
                              ? linkedDealerships
                              : ((linkedDealerships as Record<string, unknown> | null)?.id ?? (linkedDealerships as Record<string, unknown> | null)?.value),
                      ].filter((item): item is string => typeof item === "string");
                if (linkedIds.includes(assignedDealerId) !== true) continue;
                const template = userTemplates.find((item) => item.id === user.templateId);
                const templateName = template?.displayName?.toLowerCase() ?? "";
                if (
                    (field === "kam" && templateName.includes("kam") === false) ||
                    (field === "rm" && templateName.includes("rm") === false && templateName.includes("zsm") === false)
                ) {
                    continue;
                }
                const name = getUserDisplayName(user.userId, users, userTemplates, platformUserDetailsByUserId);
                if (name !== "?" && name !== "-") return name;
            }
            const {kamUserId, rmUserId} = resolveKamRmUserIdsFromDealershipReportingChain(assignedDealerId, users, userTemplates);
            const resolvedUserId = field === "kam" ? kamUserId : rmUserId;
            if (resolvedUserId !== null) {
                const name = getUserDisplayName(resolvedUserId, users, userTemplates, platformUserDetailsByUserId);
                if (name !== "?" && name !== "-") return name;
            }
            return "";
        };
    }, [organizationalUnits, organizationalUnitTemplates, platformUserDetailsByUserId, users, userTemplates]);

    /**
     * Assigned dealer zone: walk from the dealership OU (leaf) up `parentId` until we find an OU whose
     * template is {@link EmotoradOus.Zone}, then use its Name column; otherwise use the first non-empty
     * Zone / Dealer Zone field on any ancestor (including the dealership).
     */
    const resolveAssignedDealerZoneFromOuAncestorTree = useMemo(() => {
        const zoneTemplateId = EmotoradOus.Zone as Uuid;
        const zoneColumnLabels = ["Zone", "Dealer Zone", "Zone Name"] as const;

        const ouNameFromNameColumn = (ou: (typeof organizationalUnits)[number]): string => {
            const template = organizationalUnitTemplates.find((t) => t.id === ou.templateId);
            if (template === undefined) return "";
            const nameColumn = template.additionalColumns.find((col) => col.displayName.trim().toLowerCase() === "name");
            if (nameColumn === undefined) return "";
            const nameValue = ou.additionalColumnValues[nameColumn.id];
            return typeof nameValue === "string" && nameValue.trim() !== "" ? nameValue.trim() : "";
        };

        const zoneColumnValueOnOu = (ou: (typeof organizationalUnits)[number]): string | null => {
            const template = organizationalUnitTemplates.find((t) => t.id === ou.templateId);
            if (template === undefined) return null;
            for (const label of zoneColumnLabels) {
                const column = template.additionalColumns.find((c) => c.displayName.trim().toLowerCase() === label.toLowerCase());
                if (column === undefined) continue;
                const value = ou.additionalColumnValues[column.id];
                if (value !== null && value !== undefined && String(value).trim() !== "") {
                    return String(value).trim();
                }
            }
            return null;
        };

        return (dealerOuId: Uuid | null): string => {
            if (dealerOuId === null) return "";
            const visited = new Set<Uuid>();
            let current: (typeof organizationalUnits)[number] | undefined = organizationalUnits.find((unit) => unit.id === dealerOuId);
            for (;;) {
                if (current === undefined) break;
                if (visited.has(current.id) === true) break;
                const ou = current;
                visited.add(ou.id);
                if (ou.templateId === zoneTemplateId) {
                    const zoneName = ouNameFromNameColumn(ou);
                    if (zoneName !== "") return zoneName;
                }
                current = ou.parentId !== null ? organizationalUnits.find((unit) => unit.id === ou.parentId) : undefined;
            }
            visited.clear();
            current = organizationalUnits.find((unit) => unit.id === dealerOuId);
            for (;;) {
                if (current === undefined) break;
                if (visited.has(current.id) === true) break;
                const ou = current;
                visited.add(ou.id);
                const fromColumn = zoneColumnValueOnOu(ou);
                if (fromColumn !== null) return fromColumn;
                current = ou.parentId !== null ? organizationalUnits.find((unit) => unit.id === ou.parentId) : undefined;
            }
            return "";
        };
    }, [organizationalUnits, organizationalUnitTemplates]);

    const {showFlag} = useAnankeFlags();
    const [additionalButtonModal, setAdditionalButtonModal] = useState<{
        config: OttopilotAdditionalButtonsConfig;
        event: EventWithEventContainer;
    } | null>(null);
    const [leadDispositionModalEvent, setLeadDispositionModalEvent] = useState<EventWithEventContainer | null>(null);
    const {openNotesPanel, closeNotesPanel, listNotesPanelChrome, notesPanelContext} = useListNotesPanel();
    const restoreEventTargetsRef = useRef<Array<EventWithEventContainer> | null>(null);
    const [restoreEventTargets, setRestoreEventTargetsState] = useState<Array<EventWithEventContainer> | null>(null);
    const setRestoreEventTargets = useCallback((targets: Array<EventWithEventContainer> | null) => {
        restoreEventTargetsRef.current = targets;
        setRestoreEventTargetsState(targets);
    }, []);
    const [isRestoreEventModalOpen, setIsRestoreEventModalOpen] = useState(false);
    const [showRestoreSuccessToast, setShowRestoreSuccessToast] = useState(false);
    const [isListCreateDrawerOpen, setListCreateDrawerOpen] = useState(false);
    const baseWorkspaceMobileNavigationConfiguration = useRequiredWorkspaceMobileNavigationConfiguration();
    const workspaceMobileNavigationConfiguration = useMemo(
        () =>
            resolveWorkspaceListCreateItemsConfiguration({
                workspace,
                configuration: baseWorkspaceMobileNavigationConfiguration,
            }),
        [workspace, baseWorkspaceMobileNavigationConfiguration],
    );
    const workspaceListSupport = useMemo(
        () => resolveWorkspaceListSupport(workspace, workspaceCapabilities),
        [workspace, workspaceCapabilities],
    );
    const registryEventsListConfig = resolveEventsListConfig(workspaceListSupport);
    const isRegistryListEventView = isRegistryListEventTemplate(workspaceListSupport, eventTemplate.id);
    const emotoradEventsListConfig = getEmotoradListModuleConfig("events");
    const listEventCreateDrawerItems = useMemo(() => {
        if (isRegistryListEventView === true && workspaceListSupport !== null) {
            if (registryEventsListConfig?.includeEventCreateDrawer !== true) {
                return [] as const;
            }
            return workspaceListSupport.getListCreateItems({
                workspaceMobileNavigationConfiguration,
                eventTemplateId: eventTemplate.id,
            });
        }
        if (emotoradEventsListConfig.includeEventCreateDrawer !== true) {
            return [] as const;
        }
        return getEmotoradListEventCreateItems(workspaceMobileNavigationConfiguration);
    }, [
        workspaceListSupport,
        isRegistryListEventView,
        registryEventsListConfig?.includeEventCreateDrawer,
        workspaceMobileNavigationConfiguration,
        eventTemplate.id,
        emotoradEventsListConfig.includeEventCreateDrawer,
    ]);
    const configuredEventSearchColumns = eventTemplate.configuration.eventPage.ottopilotDataViewerConfig?.searchColumns;
    const eventSearchPlaceholder = eventTemplate.configuration.eventPage.ottopilotDataViewerConfig?.searchPlaceholder;
    const isEmotoradJourneysCustomer = isEmotoradWorkspace(workspace) === true || isEmotoradJourneyModuleConfiguration(journeysModuleConfiguration);
    const resolvedNotesEventContainerSourceLabelForEventPanels =
        isEmotoradWorkspace(workspace) === true && isEmotoradJourneyModuleConfiguration(journeysModuleConfiguration)
            ? "Lifecycle Overview"
            : journeysModuleConfiguration.configuration.moduleDisplayNameVariants.singularSentenceCase;
    const isEmotoradSalesTemplate = isEmotoradJourneysCustomer === true && eventTemplate.id === (EmotoradEventTypes.sales as Uuid);
    const isEmotoradTestrideTemplate = isEmotoradJourneysCustomer === true && eventTemplate.id === (EmotoradEventTypes.testride as Uuid);
    const isEmotoradCallTemplate = isEmotoradJourneysCustomer === true && eventTemplate.id === (EmotoradEventTypes.call as Uuid);
    const isEmotoradWalkinTemplate = isEmotoradJourneysCustomer === true && eventTemplate.id === (EmotoradEventTypes.walkin as Uuid);
    const isEmotoradLeadsTemplate = isEmotoradJourneysCustomer === true && eventTemplate.id === (EmotoradEventTypes.lead as Uuid);
    const isAmaronCallTemplate = isAmaronWorkspace(workspaceCapabilities) === true && eventTemplate.id === (AmaronEventTypes.Calls as Uuid);
    const isAmaronEnquiryTemplate = isAmaronWorkspace(workspaceCapabilities) === true && eventTemplate.id === (AmaronEventTypes.Enquiry as Uuid);
    const isAmaronServiceTemplate = isAmaronWorkspace(workspaceCapabilities) === true && eventTemplate.id === (AmaronEventTypes.Service as Uuid);
    const isAmaronListSupportedTemplate = isAmaronCallTemplate === true || isAmaronEnquiryTemplate === true || isAmaronServiceTemplate === true;
    const leadOwnerColumnId = journeysModuleConfiguration.configuration.eventContainersPage.leadOwnerColumnId;
    const eventScopeFilterConfig = useMemo(() => {
        const configuredScopeFilter = eventTemplate.configuration.eventPage.ottopilotDataViewerConfig?.scopeFilter;
        if (configuredScopeFilter !== undefined) {
            return configuredScopeFilter;
        }
        if (isAmaronListSupportedTemplate !== true || leadOwnerColumnId === undefined) {
            return undefined;
        }
        return getAmaronEventsPageScopeFilterConfig(leadOwnerColumnId);
    }, [
        eventTemplate.configuration.eventPage.ottopilotDataViewerConfig?.scopeFilter,
        isAmaronListSupportedTemplate,
        leadOwnerColumnId,
    ]);
    const isDuluxCallTemplate = isDuluxWorkspace(workspaceCapabilities) === true && eventTemplate.id === (DuluxEventTypes.Calls as Uuid);
    const isDuluxAppointmentsListTemplate = isDuluxWorkspace(workspaceCapabilities) === true && eventTemplate.id === (DuluxEventTypes.Appointments as Uuid);
    const isDuluxEnquiryTemplate = isDuluxWorkspace(workspaceCapabilities) === true && eventTemplate.id === (DuluxEventTypes.Enquiry as Uuid);
    const isDuluxOnboardingTemplate = isDuluxWorkspace(workspaceCapabilities) === true && eventTemplate.id === (DuluxEventTypes.Onboarding as Uuid);
    const isDuluxListSupportedTemplate =
        isDuluxCallTemplate === true || isDuluxAppointmentsListTemplate === true || isDuluxEnquiryTemplate === true || isDuluxOnboardingTemplate === true;
    const eventSearchColumns = useMemo((): Array<string> => {
        const configured = configuredEventSearchColumns ?? EMPTY_EVENT_SEARCH_COLUMNS;
        if (isAmaronCallTemplate === true) {
            if (configured.length === 0) {
                return [...AMARON_CALLS_FALLBACK_SEARCH_COLUMN_IDS];
            }
            return Array.from(new Set([...configured, ...AMARON_CALLS_FALLBACK_SEARCH_COLUMN_IDS]));
        }
        if (isDuluxWorkspace(workspaceCapabilities) === true) {
            return resolveDuluxEventSearchColumns(configured, eventTemplate.id);
        }
        return configured;
    }, [configuredEventSearchColumns, eventTemplate.id, isAmaronCallTemplate, workspace]);
    const eventSearchPlaceholderResolved =
        eventSearchPlaceholder ?? (isDuluxWorkspace(workspaceCapabilities) === true ? getDuluxEventSearchPlaceholder(eventTemplate.id) : undefined);
    const isRegistryListSupportedTemplate = isRegistryListEventView === true;
    const shouldUseEmotoradExplicitColumns =
        isEmotoradSalesTemplate === true ||
        isEmotoradTestrideTemplate === true ||
        isEmotoradCallTemplate === true ||
        isEmotoradWalkinTemplate === true ||
        isEmotoradLeadsTemplate === true;
    const isEmotoradListSupportedTemplate = shouldUseEmotoradExplicitColumns === true;
    const emotoradEventKanbanColumnConfig = useMemo(() => {
        if (isEmotoradListSupportedTemplate !== true) {
            return eventTemplate.configuration.eventPage.kanbanBoardPageView.columnConfig;
        }
        return withKanbanAccentTextColors(eventTemplate.configuration.eventPage.kanbanBoardPageView.columnConfig);
    }, [eventTemplate.configuration.eventPage.kanbanBoardPageView.columnConfig, isEmotoradListSupportedTemplate]);
    const getListStatusPillType = (statusLabel: string): "Active" | "Archived" | "Deleted" => {
        const normalized = statusLabel.trim().toLowerCase();
        if (normalized.includes("delete")) {
            return "Deleted";
        }
        if (
            normalized.includes("cancel") ||
            normalized.includes("archive") ||
            normalized.includes("reject") ||
            normalized.includes("duplicate") ||
            normalized.includes("non-engaged")
        ) {
            return "Archived";
        }
        return "Active";
    };
    const resolveEmotoradEventListStatus = (event: EventWithEventContainer): ListCardStatus => {
        const resolveNameFromRecord = (rawValue: unknown, record: Record<string, {displayName: string}>): string => {
            if (rawValue === null || rawValue === undefined || String(rawValue).trim() === "") {
                return "";
            }
            const raw = String(rawValue).trim();
            return record[raw]?.displayName ?? raw;
        };
        const formatEmotoradEventStatusValue = (rawValue: unknown): string => {
            if (rawValue === null || rawValue === undefined || String(rawValue).trim() === "") {
                return "";
            }
            return formatEmotoradEventCalendarStatusIdForDisplay(String(rawValue).trim());
        };

        let statusName = "";
        let statusValue: unknown;
        if (isEmotoradCallTemplate === true) {
            statusValue = event.additionalColumnValues[EMOTORAD__EVENT__CALLS__STATUS__COLUMN_ID];
            statusName = formatEmotoradEventStatusValue(statusValue);
        } else if (isEmotoradWalkinTemplate === true) {
            statusValue = event.additionalColumnValues[EMOTORAD__EVENT__WALKINS__STATUS__COLUMN_ID];
            statusName = formatEmotoradEventStatusValue(statusValue);
        } else if (isEmotoradTestrideTemplate === true) {
            statusValue = event.additionalColumnValues[EMOTORAD__EVENT__TESTRIDES__STATUS__COLUMN_ID];
            statusName = formatEmotoradEventStatusValue(statusValue);
        } else if (isEmotoradSalesTemplate === true) {
            statusValue = event.additionalColumnValues[EMOTORAD__EVENT__SALES__STATUS__COLUMN_ID];
            statusName = formatEmotoradEventStatusValue(statusValue);
        } else if (isEmotoradLeadsTemplate === true) {
            const leadStatusRaw = event.additionalColumnValues[EMOTORAD__EVENT__ENQUIRY__LEAD_STATUS__COLUMN_ID];
            const digitalLeadStatusRaw = event.additionalColumnValues[EMOTORAD__EVENT__ENQUIRY__DIGITAL_LEAD_STATUS__COLUMN_ID];
            statusValue = leadStatusRaw ?? digitalLeadStatusRaw;
            statusName = formatEmotoradEventStatusValue(leadStatusRaw) || resolveNameFromRecord(digitalLeadStatusRaw, emotoradEnquiryDigitalLeadStatuses);
        }
        if (statusName === "") {
            statusName = "Active";
        }
        return resolveListCardStatusFromColumnConfig({
            statusValue,
            columnConfig: emotoradEventKanbanColumnConfig,
            fallbackDisplayName: statusName,
            fallbackPillType: getListStatusPillType,
            resolveDisplayName: ({matchedColumn}) => {
                if (matchedColumn !== undefined) {
                    return matchedColumn.displayName;
                }
                return statusName !== "" ? statusName : "";
            },
        });
    };

    const eventsDataViewerControllerRef = useRef<OttopilotDataViewerController<EventWithEventContainer> | null>(null);

    const activeEventListModuleConfig =
        isRegistryListSupportedTemplate === true ? registryEventsListConfig : isEmotoradListSupportedTemplate === true ? emotoradEventsListConfig : null;

    // Amaron list cards always expose Dispose in the default footer; mount the modal for every
    // Amaron list event template (Calls / Enquiry / Service), not only Enquiry — otherwise
    // Dispose sets modal state and nothing renders (agents are redirected away from Enquiry).
    const showEventDispositionAction = isEmotoradLeadsTemplate === true || isAmaronListSupportedTemplate === true;

    const showEventListFooterActions = isEmotoradListSupportedTemplate === true || isRegistryListSupportedTemplate === true;

    const additionalButtonConfigRef = useRef<Array<OttopilotAdditionalButtonsConfig>>([]);

    const journeyRowActionGlobalConstraints = useMemo(
        () =>
            resolveEventContainerEditPanelGlobalConstraints({
                workspace,
                workspaceCapabilities,
                editPanel: journeysModuleConfiguration.configuration.eventContainersPage.editPanel,
            }),
        [workspace, workspaceCapabilities, journeysModuleConfiguration.configuration.eventContainersPage.editPanel],
    );

    const renderEventMoreMenuItems = useCallback(
        (row: EventWithEventContainer) =>
            renderEventTableAdditionalMenuItems({
                row,
                additionalButtonConfig: additionalButtonConfigRef.current,
                currentUserTemplateIds,
                workspaceId: workspace.id,
                onOpenAdditionalButtonModal: setAdditionalButtonModal,
                globalConstraints: journeyRowActionGlobalConstraints,
            }),
        [currentUserTemplateIds, workspace.id, journeyRowActionGlobalConstraints],
    );

    const scheduleCallDisableConfig = useMemo(
        () =>
            resolveEventContainerScheduleCallDisableConfig({
                workspace,
                workspaceCapabilities,
                context: "events",
            }),
        [workspace, workspaceCapabilities],
    );

    const isEventListCardFooterRestricted = useCallback(
        (event: EventWithEventContainer) =>
            isListCardFooterRestrictedByGlobalConstraints(event.eventContainer, journeyRowActionGlobalConstraints),
        [journeyRowActionGlobalConstraints],
    );

    const eventRowEngagement = useEventRowEngagement({
        workspaceId: workspace.id,
        navigate,
        isArchivedView: isArchivedSlice,
        pageEventTemplate: eventTemplate,
        listModuleConfig: activeEventListModuleConfig,
        showDisposition: showEventDispositionAction,
        showNotes: showEventListFooterActions,
        includeCreateDrawer:
            (isRegistryListSupportedTemplate === true && registryEventsListConfig?.includeEventCreateDrawer === true) ||
            (isEmotoradListSupportedTemplate === true && emotoradEventsListConfig.includeEventCreateDrawer === true),
        listCreateDrawerItemsCount: listEventCreateDrawerItems.length,
        scheduleRoute:
            (isRegistryListSupportedTemplate === true ? workspaceListSupport?.getEventScheduleRoute(eventTemplate.id, workspace.id) : undefined) ??
            (isEmotoradListSupportedTemplate === true ? emotoradEventsListConfig.scheduleRoute : undefined),
        onScheduleSuccess: () => {
            void invalidateOttopilotDataViewerQueries(queryClient, eventsDataViewerQueryKeyPrefix);
        },
        queryKeyPrefix: eventsDataViewerQueryKeyPrefix,
        controllerRef: eventsDataViewerControllerRef,
        dispositionEventId: leadDispositionModalEvent?.id ?? null,
        notesPanelEventContainerId:
            notesPanelContext?.eventContainerId ?? null,
        openNotesPanel: openNotesPanel,
        closeNotesPanel,
        notesEventContainerSourceLabel: resolvedNotesEventContainerSourceLabelForEventPanels,
        isCreateDrawerOpen: isListCreateDrawerOpen,
        onOpenDisposition: setLeadDispositionModalEvent,
        onCloseDisposition: () => setLeadDispositionModalEvent(null),
        onCreateDrawerOpenChange: setListCreateDrawerOpen,
        archiveMenuItemLabel: isEmotoradSalesTemplate === true ? "Delete" : "Archive",
        renderMoreMenuItems: renderEventMoreMenuItems,
        globalConstraints: journeyRowActionGlobalConstraints,
        scheduleCallDisableConfig,
    });

    const effectiveEventRowActions = useMemo(() => {
        const isDuluxAppointmentsTemplate =
            isDuluxWorkspace(workspaceCapabilities) === true &&
            eventTemplateId === (DuluxEventTypes.Appointments as Uuid);
        const isDuluxCallsTemplate =
            isDuluxWorkspace(workspaceCapabilities) === true &&
            eventTemplateId === (DuluxEventTypes.Calls as Uuid);
        const isAmaronCallsTemplate =
            isAmaronWorkspace(workspaceCapabilities) === true &&
            eventTemplateId === (AmaronEventTypes.Calls as Uuid);

        if (
            isDuluxAppointmentsTemplate !== true &&
            isDuluxCallsTemplate !== true &&
            isAmaronCallsTemplate !== true
        ) {
            return eventRowEngagement.tableRowActions;
        }

        const base = eventRowEngagement.tableRowActions;
        return {
            ...base,
            ...(isDuluxCallsTemplate === true
                ? {
                      menuItemLabels: {
                          ...base.menuItemLabels,
                          logCall: "Log",
                      },
                      toolbarItemLabels: {
                          ...base.toolbarItemLabels,
                          logCall: "Log",
                      },
                      onLogCall: (event: EventWithEventContainer) => {
                          if (isDuluxScheduledCallEvent(event) === true) {
                              void navigate({
                                  to: `/w/${workspace.id}/ottopilot/dulux/call/log?eventId=${event.id}&eventContainerId=${event.eventContainer.id}`,
                              });
                              return;
                          }
                          base.onLogCall?.(event);
                      },
                  }
                : {}),
            ...(isAmaronCallsTemplate === true
                ? {
                      menuItemLabels: {
                          ...base.menuItemLabels,
                          logCall: "Log",
                      },
                      toolbarItemLabels: {
                          ...base.toolbarItemLabels,
                          logCall: "Log",
                      },
                      isMenuItemVisible: (item: TableRowActionsMenuItem, event: EventWithEventContainer) => {
                          if (item === "logCall" && isAmaronCancelledCallEvent(event) === true) {
                              return false;
                          }
                          return base.isMenuItemVisible?.(item, event) ?? true;
                      },
                      isToolbarItemVisible: (item: TableRowActionsToolbarItem, event: EventWithEventContainer) => {
                          if (item === "logCall" && isAmaronCancelledCallEvent(event) === true) {
                              return false;
                          }
                          return base.isToolbarItemVisible?.(item, event) ?? true;
                      },
                      onLogCall: (event: EventWithEventContainer) => {
                          if (isAmaronCancelledCallEvent(event) === true) {
                              return;
                          }
                          if (isAmaronScheduledCallEvent(event) === true) {
                              void navigate({
                                  to: `/w/${workspace.id}/ottopilot/amaron/call/log?eventId=${event.id}&eventContainerId=${event.eventContainer.id}`,
                              });
                              return;
                          }
                          base.onLogCall?.(event);
                      },
                  }
                : {}),
            ...(isDuluxAppointmentsTemplate === true
                ? {
                      onEdit: (event: EventWithEventContainer) => {
                          if (isDuluxScheduledAppointmentEvent(event) === true) {
                              void navigate({
                                  to: `/w/${workspace.id}/ottopilot/dulux/appointment/edit?eventId=${event.id}`,
                              });
                              return;
                          }
                          base.onEdit?.(event);
                      },
                  }
                : {}),
        };
    }, [eventRowEngagement.tableRowActions, eventTemplateId, navigate, workspace, workspaceCapabilities]);

    const sharedEventRowEngagementHandlers = useMemo(() => {
        const isDuluxCallsTemplate =
            isDuluxWorkspace(workspaceCapabilities) === true &&
            eventTemplateId === (DuluxEventTypes.Calls as Uuid);
        const isAmaronCallsTemplate =
            isAmaronWorkspace(workspaceCapabilities) === true &&
            eventTemplateId === (AmaronEventTypes.Calls as Uuid);
        if (isDuluxCallsTemplate !== true && isAmaronCallsTemplate !== true) {
            return eventRowEngagement.handlers;
        }
        return {
            ...eventRowEngagement.handlers,
            openLogCall: (event: EventWithEventContainer) => {
                if (isAmaronCallsTemplate === true) {
                    if (shouldHideAmaronCallLogAction(event) === true) {
                        return;
                    }
                    if (isAmaronScheduledCallEvent(event) === true) {
                        void navigate({
                            to: `/w/${workspace.id}/ottopilot/amaron/call/log?eventId=${event.id}&eventContainerId=${event.eventContainer.id}`,
                        });
                        return;
                    }
                    eventRowEngagement.handlers.openLogCall(event);
                    return;
                }
                if (isDuluxScheduledCallEvent(event) === true) {
                    void navigate({
                        to: `/w/${workspace.id}/ottopilot/dulux/call/log?eventId=${event.id}&eventContainerId=${event.eventContainer.id}`,
                    });
                    return;
                }
                eventRowEngagement.handlers.openLogCall(event);
            },
        };
    }, [eventRowEngagement.handlers, eventTemplateId, navigate, workspace, workspaceCapabilities]);
    const sharedEventFooterBindings = eventRowEngagement.footerBindings;

    const amaronAssignedOuListCardFields = useMemo((): AssignedOuListCardFields | undefined => {
        if (isAmaronWorkspace(workspaceCapabilities) !== true) {
            return undefined;
        }
        return {
            getAssignedOuColumnLabel: (journey) => getAmaronJourneyListCardAssignedOuColumnLabel(journey, journeysModuleConfiguration, organizationalUnits, organizationalUnitTemplates),
            getAssignedOuName: (journey) => getAmaronJourneyListCardAssignedOuName(journey, journeysModuleConfiguration, organizationalUnits, organizationalUnitTemplates),
        };
    }, [workspace, organizationalUnits, organizationalUnitTemplates]);

    const emotoradEventsListPresentation = useEmotoradEventListCardPresentation({
        eventTemplate,
        workspaceId: workspace.id,
        navigate,
        listModuleConfig: emotoradEventsListConfig,
        notesEventContainerSourceLabel: journeysModuleConfiguration.configuration.moduleDisplayNameVariants.singularSentenceCase,
        getOuDisplayName: (ouId) => {
            const label = getOuDisplayName(ouId);
            return label === "-" ? null : label;
        },
        resolveEventListStatus: resolveEmotoradEventListStatus,
        onDisposeRequest: setLeadDispositionModalEvent,
        onOpenCreateDrawer: () => {
            setListCreateDrawerOpen(true);
        },
        openNotesPanel,
        rowEngagement: sharedEventRowEngagementHandlers,
        sharedFooterBindings: sharedEventFooterBindings,
    });

    const registryEventsListPresentation = useConfiguredEventListCardPresentation({
        eventTemplate,
        workspaceId: workspace.id,
        navigate,
        columnIds: workspaceListSupport?.journeyColumnIds ?? {
            contactNumber: AMARON__EVENT_CONTAINER__CONTACT_NUMBER__COLUMN_ID,
            remarks: AMARON__EVENT_CONTAINER__REMARKS__COLUMN_ID,
            leadStatus: AMARON__EVENT_CONTAINER__LEAD_STATUS__COLUMN_ID,
        },
        listConfig: registryEventsListConfig ?? {
            enabled: false,
            showFooter: false,
            scheduleRoute: "",
            includeEventCreateDrawer: false,
        },
        resolveScheduleRoute:
            isAmaronWorkspace(workspaceCapabilities) === true ? undefined : (eventTemplateId) => workspaceListSupport?.getEventScheduleRoute(eventTemplateId, workspace.id) ?? "",
        resolveEventStatus: (event) =>
            workspaceListSupport?.resolveEventListStatus(event, eventTemplate.id) ??
            (isAmaronWorkspace(workspaceCapabilities) === true
                ? {name: "Active", pillType: "Active"}
                : {name: "Active", lozengeStyle: listStatusPillTypeToLozengeStyle("Active")}),
        onDisposeRequest: setLeadDispositionModalEvent,
        onOpenCreateDrawer: () => {
            setListCreateDrawerOpen(true);
        },
        openNotesPanel,
        notesEventContainerSourceLabel: journeysModuleConfiguration.configuration.moduleDisplayNameVariants.singularSentenceCase,
        journeysDisplayNameOttopilotStringTemplate: journeysModuleConfiguration.configuration.displayNameTemplate,
        assignedOuListCardFields: amaronAssignedOuListCardFields,
        funnelOptions:
            isAmaronWorkspace(workspaceCapabilities) === true
                ? createAmaronJourneyListFunnelOptions((event: EventWithEventContainer) => event.eventContainer.additionalColumnValues)
                : undefined,
        rowEngagement: sharedEventRowEngagementHandlers,
        sharedFooterBindings: sharedEventFooterBindings,
        isListCardFooterRestricted: isEventListCardFooterRestricted,
        scheduleCallDisableConfig,
    });

    const eventsListPresentation = isRegistryListSupportedTemplate === true ? registryEventsListPresentation : emotoradEventsListPresentation;

    const closeLeadDispositionPanel = useCallback(() => {
        setLeadDispositionModalEvent(null);
    }, []);

    const leadDispositionPanelChrome = useMemo(() => {
        if (leadDispositionModalEvent === null) {
            return null;
        }
        if (isEmotoradListSupportedTemplate === true) {
            return (
                <EmotoradLeadDispositionModal
                    event={leadDispositionModalEvent}
                    eventContainerTemplate={journeysModuleConfiguration}
                    queryKeyPrefix={eventsDataViewerQueryKeyPrefix}
                    onClose={closeLeadDispositionPanel}
                />
            );
        }
        if (isAmaronListSupportedTemplate === true) {
            return (
                <AmaronLeadDispositionModal
                    event={leadDispositionModalEvent}
                    eventContainerTemplate={journeysModuleConfiguration}
                    triggerConfiguration={AMARON_DEFAULT_JOURNEY_TRIGGER_CONFIG}
                    queryKeyPrefix={eventsDataViewerQueryKeyPrefix}
                    onClose={closeLeadDispositionPanel}
                />
            );
        }
        if (
            isRegistryListSupportedTemplate === true &&
            workspaceListSupport !== null &&
            isDuluxWorkspace(workspaceCapabilities) === true
        ) {
            return (
                <DuluxLeadDispositionModal
                    event={leadDispositionModalEvent}
                    eventContainerTemplate={journeysModuleConfiguration}
                    triggerConfiguration={workspaceListSupport.disposition.triggerConfiguration}
                    queryKeyPrefix={eventsDataViewerQueryKeyPrefix}
                    onClose={closeLeadDispositionPanel}
                />
            );
        }
        if (
            isRegistryListSupportedTemplate === true &&
            workspaceListSupport !== null &&
            isDuluxWorkspace(workspaceCapabilities) !== true &&
            isAmaronWorkspace(workspaceCapabilities) !== true
        ) {
            return (
                <EventContainerListDispositionModal
                    event={leadDispositionModalEvent}
                    eventContainerTemplate={journeysModuleConfiguration}
                    triggerConfiguration={workspaceListSupport.disposition.triggerConfiguration}
                    dispositionConfiguration={workspaceListSupport.disposition.dispositionConfiguration}
                    dispositionUiLevels={workspaceListSupport.disposition.dispositionUiLevels}
                    getLeadDisplayName={workspaceListSupport.disposition.getLeadDisplayName}
                    queryKeyPrefix={eventsDataViewerQueryKeyPrefix}
                    onClose={closeLeadDispositionPanel}
                />
            );
        }
        return null;
    }, [
        leadDispositionModalEvent,
        isEmotoradListSupportedTemplate,
        isAmaronListSupportedTemplate,
        isRegistryListSupportedTemplate,
        workspaceListSupport,
        workspace,
        journeysModuleConfiguration,
        eventsDataViewerQueryKeyPrefix,
        closeLeadDispositionPanel,
    ]);

    const emotoradExplicitVisibleColumnIds = isEmotoradSalesTemplate
        ? EMOTORAD_SALES_EVENT_TABLE_VISIBLE_COLUMN_IDS
        : isEmotoradTestrideTemplate
          ? EMOTORAD_TESTRIDE_EVENT_TABLE_VISIBLE_COLUMN_IDS
          : isEmotoradCallTemplate
            ? EMOTORAD_CALLS_EVENT_TABLE_VISIBLE_COLUMN_IDS
            : isEmotoradWalkinTemplate
              ? EMOTORAD_WALKINS_EVENT_TABLE_VISIBLE_COLUMN_IDS
              : isEmotoradLeadsTemplate
                ? EMOTORAD_LEADS_EVENT_TABLE_VISIBLE_COLUMN_IDS
                : null;

    const isBobcardApplicationsInitiatedTemplate =
        isBobcardWorkspace(workspace) === true && eventTemplateId === (BobcardEventTypes.applicationsInitiated as Uuid);

    const bobcardExplicitVisibleColumnIds = isBobcardApplicationsInitiatedTemplate === true ? BOBCARD_ENQUIRY_EVENT_TABLE_VISIBLE_COLUMN_IDS : null;

    const amaronExplicitVisibleColumnIds =
        isAmaronCallTemplate === true
            ? AMARON_CALLS_EVENT_TABLE_VISIBLE_COLUMN_IDS
            : isAmaronEnquiryTemplate === true
              ? AMARON_ENQUIRY_EVENT_TABLE_VISIBLE_COLUMN_IDS
              : isAmaronServiceTemplate === true
                ? AMARON_SERVICE_EVENT_TABLE_VISIBLE_COLUMN_IDS
                : null;

    const registryExplicitVisibleColumnIds = workspaceListSupport?.getEventTableVisibleColumnIds(eventTemplate.id) ?? null;

    const workspaceExplicitOrderedEventColumnIds =
        emotoradExplicitVisibleColumnIds ?? bobcardExplicitVisibleColumnIds ?? amaronExplicitVisibleColumnIds ?? registryExplicitVisibleColumnIds;

    const emotoradEventDefaultVisibleFilterColumnIds = useMemo(() => {
        if (shouldUseEmotoradExplicitColumns !== true || workspaceExplicitOrderedEventColumnIds === null) {
            return [] as Array<string>;
        }
        const preferred = isEmotoradSalesTemplate
            ? EMOTORAD_SALES_EVENT_DEFAULT_VISIBLE_FILTER_COLUMN_IDS
            : isEmotoradCallTemplate
              ? EMOTORAD_CALLS_EVENT_DEFAULT_VISIBLE_FILTER_COLUMN_IDS
              : isEmotoradWalkinTemplate
                ? EMOTORAD_WALKINS_EVENT_DEFAULT_VISIBLE_FILTER_COLUMN_IDS
                : isEmotoradLeadsTemplate
                  ? EMOTORAD_LEADS_EVENT_DEFAULT_VISIBLE_FILTER_COLUMN_IDS
                  : isEmotoradTestrideTemplate
                    ? EMOTORAD_TESTRIDE_EVENT_DEFAULT_VISIBLE_FILTER_COLUMN_IDS
                    : [];
        if (preferred.length === 0) {
            return [];
        }
        return intersectEmotoradPreferredVisibleFilterColumnIdsWithModule(preferred, workspaceExplicitOrderedEventColumnIds);
    }, [
        shouldUseEmotoradExplicitColumns,
        workspaceExplicitOrderedEventColumnIds,
        isEmotoradSalesTemplate,
        isEmotoradCallTemplate,
        isEmotoradWalkinTemplate,
        isEmotoradLeadsTemplate,
        isEmotoradTestrideTemplate,
    ]);

    const shouldSkipEventTableViewColumnsForExplicitOrder =
        shouldUseEmotoradExplicitColumns === true || isBobcardApplicationsInitiatedTemplate === true || isRegistryListSupportedTemplate === true;

    const eventsInitialVisibleFilterColumnIds = useMemo((): Array<string> => {
        if (eventTemplate.configuration.eventPage.tableViewColumns !== undefined && shouldSkipEventTableViewColumnsForExplicitOrder !== true) {
            const first = eventTemplate.configuration.eventPage.tableViewColumns[0];
            return first !== undefined ? [first.id] : [];
        }
        if (shouldSkipEventTableViewColumnsForExplicitOrder === true) {
            if (isBobcardApplicationsInitiatedTemplate === true) {
                const id = BOBCARD_ENQUIRY_EVENT_TABLE_VISIBLE_COLUMN_IDS[0];
                return id !== undefined ? [id] : [];
            }
            if (
                isRegistryListSupportedTemplate === true
                && isAmaronWorkspace(workspaceCapabilities) === true
            ) {
                return [...getAmaronDefaultVisibleFilterColumnIdsForEventTemplate(eventTemplateId)];
            }
            if (isRegistryListSupportedTemplate === true && workspaceExplicitOrderedEventColumnIds !== null) {
                const id = workspaceExplicitOrderedEventColumnIds[0];
                return id !== undefined ? [id] : [];
            }
            if (emotoradEventDefaultVisibleFilterColumnIds.length > 0) {
                return emotoradEventDefaultVisibleFilterColumnIds;
            }
            if (shouldUseEmotoradExplicitColumns === true && workspaceExplicitOrderedEventColumnIds !== null) {
                const first = workspaceExplicitOrderedEventColumnIds[0];
                return first !== undefined ? [first] : [];
            }
            return [];
        }
        const fallbackColumnId = getAdditionalColumns(journeysModuleConfiguration)[0]?.id ?? getAdditionalColumns(eventTemplate)[0]?.id ?? "";
        return fallbackColumnId !== "" ? [fallbackColumnId] : [];
    }, [getAdditionalColumns, eventTemplate.configuration.eventPage.tableViewColumns, eventTemplate, shouldSkipEventTableViewColumnsForExplicitOrder, isBobcardApplicationsInitiatedTemplate, isRegistryListSupportedTemplate, workspaceExplicitOrderedEventColumnIds, emotoradEventDefaultVisibleFilterColumnIds, shouldUseEmotoradExplicitColumns, journeysModuleConfiguration, workspaceCapabilities]);

    const amaronEventStatusNavConfigForInitialFilter = useMemo(
        () => (isAmaronWorkspace(workspaceCapabilities) === true ? getAmaronEventTemplateStatusNavConfig(eventTemplateId) : null),
        [workspace, eventTemplateId],
    );

    const duluxEventStatusNavConfigForInitialFilter = useMemo(
        () => (isDuluxWorkspace(workspaceCapabilities) === true && isDuluxListSupportedTemplate === true ? getDuluxEventTemplateStatusNavConfig(eventTemplateId) : null),
        [eventTemplateId, isDuluxListSupportedTemplate, workspace],
    );

    const eventsInitialFilterConfiguration = useMemo(
        (): FilterConfiguration =>
            buildEventModuleInitialFilterConfiguration({
                slice: dataSlice,
                eventTemplate: eventTemplate,
            }),
        [dataSlice, eventTemplate],
    );

    const applyDuluxEventsInitialFilterOverlay = useCallback(
        (filterConfiguration: FilterConfiguration): FilterConfiguration => {
            if (duluxEventStatusNavConfigForInitialFilter === null) {
                return filterConfiguration;
            }
            const statusId = resolveModuleStatusIdForFilter({
                statusIdFromSearch: search.statusId,
                validStatusIds: getDuluxModuleStatusNavValidStatusIds(duluxEventStatusNavConfigForInitialFilter),
                ignoreStatusIdSearchParam: eventSliceConfig.ignoreStatusIdSearchParam,
            });
            return mergeModuleStatusFilterIntoFilterConfiguration(filterConfiguration, duluxEventStatusNavConfigForInitialFilter, statusId, undefined, true);
        },
        [duluxEventStatusNavConfigForInitialFilter, eventSliceConfig.ignoreStatusIdSearchParam, search.statusId],
    );

    const eventsInitialSortConfiguration = useMemo(
        (): SortConfiguration => ({
            revisionId: generateUuid(),
            lineItems: [
                {
                    columnId: "createdAt",
                    direction: SortDirection.descending,
                },
            ],
        }),
        [eventTemplateId],
    );

    const eventsInitialSearchConfiguration = useMemo(
        (): SearchConfiguration => ({
            revisionId: generateUuid(),
            searchValue: "",
            searchColumns: eventSearchColumns,
        }),
        [eventSearchColumns],
    );

    const eventsInitialVisibleFiltersConfiguration = useMemo(
        (): VisibleFiltersConfiguration => ({
            revisionId: generateUuid(),
            lineItems: eventsInitialVisibleFilterColumnIds,
        }),
        [eventsInitialVisibleFilterColumnIds],
    );

    const buildDispositionLevelCustomFilter = (level: "l1" | "l2" | "l3", targetColumnId: string) => {
        return (args: {
            controller: {
                setFilterConfiguration: (updater: (prev: FilterConfiguration) => FilterConfiguration) => void;
            };
            ephemeralValue: {
                revisionId: Uuid;
                lineItems: Array<string> | null;
            };
            column: {
                id: string;
                displayName: string;
            };
            closePopup: () => void;
        }) => {
            const selectedItems = args.ephemeralValue.lineItems;
            if (selectedItems === null || selectedItems.length === 0) {
                args.controller.setFilterConfiguration((prev) => ({
                    revisionId: generateUuid(),
                    lineItems: prev.lineItems.filter((lineItem) => lineItem.columnId !== targetColumnId),
                }));
                args.closePopup();
                return;
            }
            const dispositionIds = getEmotoradDispositionIdsForLevels(
                level === "l1" ? {l1Values: selectedItems} : level === "l2" ? {l2Values: selectedItems} : {l3Values: selectedItems},
            ).map((id) => String(id));
            args.controller.setFilterConfiguration((prev) => ({
                revisionId: generateUuid(),
                lineItems: [
                    ...prev.lineItems.filter((lineItem) => lineItem.columnId !== targetColumnId),
                    {
                        columnId: targetColumnId,
                        operation: FilterOperation.in,
                        values: dispositionIds,
                    },
                ],
            }));
            args.closePopup();
        };
    };

    const buildEventAonCustomFilterForColumn = (config: AonWorkspaceConfig, aonDisplayColumnId: string) => buildAonCustomFilter(config, aonDisplayColumnId);

    const queryEventAonFilterValuesFromJourneyCreatedAt = async (): Promise<Array<string>> => {
        const journeysColumnValuesResult = await getEventContainerColumnValues({
            workspaceId: workspace.id,
            columnId: "createdAt",
            intellsysBaseUrl: intellsysBaseUrl,
            accessToken: heimdallUser.accessToken,
            templateId: journeysModuleConfiguration.id,
        });
        if (journeysColumnValuesResult.success !== true) {
            return [];
        }
        return buildDistinctAonFilterValuesFromCreatedAtColumnValues(journeysColumnValuesResult.data);
    };

    const eventJourneyScopeFilterConfig = useMemo(() => {
        if (journeysModuleConfiguration.configuration.statusColumnConfig === undefined) {
            return undefined;
        }
        const bobcardGroups = getBobcardJourneyScopeFilterStatusIdsForEvents(workspace);
        if (bobcardGroups !== null) {
            return {
                statusColumnId: journeysModuleConfiguration.configuration.statusColumnConfig.columnId,
                activeStatusIds: bobcardGroups.activeStatusIds,
                inactiveStatusIds: bobcardGroups.inactiveStatusIds,
            };
        }
        const amaronGroups = getAmaronJourneyScopeFilterStatusIdsForEvents(workspaceCapabilities);
        if (amaronGroups !== null) {
            return {
                statusColumnId: journeysModuleConfiguration.configuration.statusColumnConfig.columnId,
                activeStatusIds: amaronGroups.activeStatusIds,
                inactiveStatusIds: amaronGroups.inactiveStatusIds,
            };
        }
        const emotoradGroups = getEmotoradJourneyScopeFilterStatusIdsForEvents(workspace);
        if (emotoradGroups === null) {
            return undefined;
        }
        return {
            statusColumnId: journeysModuleConfiguration.configuration.statusColumnConfig.columnId,
            activeStatusIds: emotoradGroups.activeStatusIds,
            inactiveStatusIds: emotoradGroups.inactiveStatusIds,
        };
    }, [workspace, journeysModuleConfiguration.configuration.statusColumnConfig]);

    const applyAmaronEventsInitialFilterOverlay = useCallback(
        (filterConfiguration: FilterConfiguration): FilterConfiguration => {
            let next = filterConfiguration;
            if (amaronEventStatusNavConfigForInitialFilter !== null) {
                const statusId = resolveModuleStatusIdForFilter({
                    statusIdFromSearch: search.statusId,
                    validStatusIds: getAmaronModuleStatusNavValidStatusIds(amaronEventStatusNavConfigForInitialFilter),
                    ignoreStatusIdSearchParam: eventSliceConfig.ignoreStatusIdSearchParam,
                });
                next = mergeModuleStatusFilterIntoFilterConfiguration(
                    next,
                    amaronEventStatusNavConfigForInitialFilter,
                    statusId,
                    undefined,
                    true,
                );
            }
            if (isAmaronListSupportedTemplate !== true || eventJourneyScopeFilterConfig === undefined) {
                return next;
            }
            if (getJourneyScopeSelectionFromFilter(next, eventJourneyScopeFilterConfig) !== "all") {
                return next;
            }
            return applyJourneyScopeSelectionToFilterConfiguration(next, eventJourneyScopeFilterConfig, "active");
        },
        [
            amaronEventStatusNavConfigForInitialFilter,
            eventJourneyScopeFilterConfig,
            eventSliceConfig.ignoreStatusIdSearchParam,
            isAmaronListSupportedTemplate,
            search.statusId,
        ],
    );

    const additionalButtonConfig = useMemo(() => {
        let buttons: Array<OttopilotAdditionalButtonsConfig> = [];
        if (eventTemplate.configuration.eventPage.additionalButtons !== undefined) {
            buttons = [...eventTemplate.configuration.eventPage.additionalButtons];
        } else if (isDuluxWorkspace(workspaceCapabilities) === true) {
            if (eventTemplateId === (DuluxEventTypes.Calls as Uuid)) {
                buttons = [...DULUX__CALLS_EVENT__ADDITIONAL_BUTTONS];
            } else if (eventTemplateId === (DuluxEventTypes.Appointments as Uuid)) {
                buttons = [...DULUX__APPOINTMENTS_EVENT__ADDITIONAL_BUTTONS];
            }
        } else if (isAmaronWorkspace(workspaceCapabilities) === true) {
            if (eventTemplateId === (AmaronEventTypes.Calls as Uuid)) {
                buttons = [...AMARON__CALLS_EVENT__ADDITIONAL_BUTTONS];
            } else if (eventTemplateId === (AmaronEventTypes.Service as Uuid)) {
                buttons = [...AMARON__SERVICE_EVENT__ADDITIONAL_BUTTONS];
            }
        } else if (isDhiraaguWorkspace(workspace) === true) {
            buttons = [...DHIRAAGU__CALL_EVENT__ADDITIONAL_BUTTONS];
        }

        const shouldStripAdditionalLogButton =
            (isAmaronWorkspace(workspaceCapabilities) === true &&
                eventTemplateId === (AmaronEventTypes.Calls as Uuid)) ||
            (isDuluxWorkspace(workspaceCapabilities) === true &&
                eventTemplateId === (DuluxEventTypes.Calls as Uuid));
        if (shouldStripAdditionalLogButton === true) {
            // Shared row "Log" covers scheduled/logged; keep Cancel only in additional buttons.
            // Must also strip Log when workspace config supplies `eventPage.additionalButtons`.
            buttons = buttons.filter((button) => button.buttonKind !== OttopilotAdditionalButtonKind.log);
        }

        return buttons;
    }, [
        eventTemplate.configuration.eventPage.additionalButtons,
        eventTemplateId,
        workspace,
        workspaceCapabilities,
    ]);
    additionalButtonConfigRef.current = additionalButtonConfig;
    const eventCalendarConfig = eventTemplate.configuration.eventPage.calendarConfig;
    const eventsCalendarRowColumns = useMemo((): Array<string> | null => {
        if (eventCalendarConfig === undefined) {
            return null;
        }
        const columns = new Set<string>(["id", "createdAt", "updatedAt", "status", "disposition", "eventContainerId"]);
        if (eventCalendarConfig.calendarStartDateColumnId !== undefined) {
            columns.add(String(eventCalendarConfig.calendarStartDateColumnId));
        }
        const eventTypeColumnId = eventCalendarConfig.eventTypeSource?.columnId;
        if (
            eventTypeColumnId !== undefined &&
            eventTypeColumnId !== "status" &&
            eventTypeColumnId !== "disposition"
        ) {
            columns.add(String(eventTypeColumnId));
        }
        const titleTemplate = eventCalendarConfig.calendarTitleOttopilotStringTemplate;
        if (typeof titleTemplate === "string") {
            for (const match of titleTemplate.matchAll(
                /[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}/gi,
            )) {
                columns.add(match[0].toLowerCase());
            }
        }
        return [...columns];
    }, [eventCalendarConfig]);
    const amaronEventCalendarDisplayMetrics = useMemo(
        () =>
            resolveAmaronEventCalendarDisplayMetrics(workspaceCapabilities, {
                eventTemplateId: eventTemplateId,
                statusId: search.statusId,
            }),
        [eventTemplateId, search.statusId, workspaceCapabilities],
    );
    const getEventCalendarType = useMemo(() => {
        if (eventCalendarConfig === undefined) {
            return (): string | undefined => undefined;
        }
        return (event: EventWithEventContainer): string | undefined => {
            const source = eventCalendarConfig.eventTypeSource;
            if (source.columnType === "OttopilotEvent") {
                if (source.columnId === "status") {
                    return String(event.status);
                }
                const value = event.additionalColumnValues[source.columnId as Uuid];
                return value === undefined || value === null ? undefined : String(value);
            }
            if (source.columnId === "status") {
                return String(event.eventContainer.status);
            }
            const value = event.eventContainer.additionalColumnValues[source.columnId as Uuid];
            return value === undefined || value === null ? undefined : String(value);
        };
    }, [eventCalendarConfig]);
    const amaronEventCalendarColorModule = useMemo((): AmaronCalendarColorModule | undefined => {
        return resolveAmaronEventStatusColorModule(eventTemplate.id);
    }, [eventTemplate.id]);
    const eventCalendarLegendItems = useMemo(() => {
        if (amaronEventCalendarColorModule !== undefined) {
            return getAmaronCalendarLegendItems(amaronEventCalendarColorModule);
        }
        if (eventCalendarConfig === undefined) {
            return [];
        }
        return resolveCalendarLegendItems(eventCalendarConfig, eventCalendarConfig);
    }, [amaronEventCalendarColorModule, eventCalendarConfig]);
    const getEventCalendarTypeLabel = useMemo(() => {
        return (type: string) => {
            if (amaronEventCalendarColorModule !== undefined) {
                return resolveAmaronCalendarEventTypeLabel(amaronEventCalendarColorModule, type);
            }
            if (eventCalendarConfig === undefined) {
                return type;
            }
            const label = resolveCalendarEventTypeLabel(type, eventCalendarConfig, eventCalendarConfig, type);
            if (label !== type) {
                return label;
            }
            if (isEmotoradListSupportedTemplate === true) {
                return formatEmotoradEventCalendarStatusIdForDisplay(type);
            }
            return label;
        };
    }, [amaronEventCalendarColorModule, eventCalendarConfig, isEmotoradListSupportedTemplate]);
    const getEventCalendarTypeColor = useMemo(() => {
        if (amaronEventCalendarColorModule !== undefined) {
            return createAmaronCalendarGetEventColor(amaronEventCalendarColorModule, (type) => {
                if (eventCalendarConfig === undefined) {
                    return {};
                }
                return resolveCalendarEventTypeColor(type, eventCalendarConfig, eventCalendarConfig);
            });
        }
        return (type: string | undefined) => {
            if (eventCalendarConfig === undefined) {
                return {};
            }
            return resolveCalendarEventTypeColor(type, eventCalendarConfig, eventCalendarConfig);
        };
    }, [amaronEventCalendarColorModule, eventCalendarConfig]);
    const eventCalendarAllDayStyle = useMemo(() => {
        if (eventCalendarConfig === undefined) {
            return {mode: "fixedGreen"} as const;
        }
        return resolveCalendarAllDayStyle(eventCalendarConfig, eventCalendarConfig);
    }, [eventCalendarConfig]);
    const eventCalendarGetEventColorForView = useMemo(
        () => wrapGetEventColorForArchivedModuleSlice(getEventCalendarTypeColor, isArchivedSlice),
        [getEventCalendarTypeColor, isArchivedSlice],
    );
    const eventCalendarAllDayStyleForView = useMemo(
        () => resolveCalendarAllDayStyleForModuleSlice(eventCalendarAllDayStyle, isArchivedSlice),
        [eventCalendarAllDayStyle, isArchivedSlice],
    );
    const eventCalendarScheduleEmptyState = useMemo(() => {
        if (eventCalendarConfig === undefined) {
            return undefined;
        }
        const fallback =
            isEmotoradSalesTemplate === true
                ? EMOTORAD_SALES_CALENDAR_SCHEDULE_EMPTY_STATE
                : isEmotoradLeadsTemplate === true
                  ? EMOTORAD_LEADS_CALENDAR_SCHEDULE_EMPTY_STATE
                  : isEmotoradJourneysCustomer === true
                    ? EMOTORAD_CALENDAR_SCHEDULE_EMPTY_STATE
                    : DEFAULT_CALENDAR_SCHEDULE_EMPTY_STATE;
        return resolveCalendarScheduleEmptyState(eventCalendarConfig, fallback);
    }, [eventCalendarConfig, isEmotoradJourneysCustomer, isEmotoradLeadsTemplate, isEmotoradSalesTemplate]);
    const onEventCalendarCreateClick = useMemo(() => {
        const fallbackFormId =
            isEmotoradSalesTemplate === true
                ? EMOTORAD__SALES__SCHEDULE__FORM__ID
                : isEmotoradJourneysCustomer === true
                  ? EMOTORAD__LEAD__CREATE__FORM__ID
                  : undefined;
        const formId = eventCalendarScheduleEmptyState?.createFormId ?? fallbackFormId;
        if (formId === undefined) {
            return undefined;
        }
        return () => {
            void navigate({
                to: resolveEventFormCreateHref({
                    workspaceId: workspace.id,
                    eventTemplateId,
                    formId,
                }),
            });
        };
    }, [eventCalendarScheduleEmptyState?.createFormId, eventTemplateId, isEmotoradJourneysCustomer, navigate, workspace.id]);
    const eventArchivedKanbanStatusIds = useMemo(() => resolveEventArchivedKanbanStatusIds(eventTemplate), [eventTemplate]);
    const eventKanbanColumnConfig = useMemo(() => {
        const amaronKanbanColumnConfig = isAmaronListSupportedTemplate === true ? getAmaronEventKanbanColumnConfig(eventTemplate.id) : null;
        let baseConfig =
            amaronKanbanColumnConfig !== null
                ? amaronKanbanColumnConfig
                : eventTemplate.configuration.eventPage.kanbanBoardPageView.columnConfig;
        if (amaronKanbanColumnConfig === null) {
            if (isAmaronEnquiryTemplate === true) {
                baseConfig = filterAmaronEnquiryKanbanColumnConfig(baseConfig);
            } else if (isAmaronCallTemplate === true) {
                baseConfig = filterAmaronCallKanbanColumnConfig(baseConfig);
            } else if (isAmaronServiceTemplate === true) {
                baseConfig = filterAmaronServiceKanbanColumnConfig(baseConfig);
            }
        }
        baseConfig = filterKanbanColumnsForDataSlice(baseConfig, dataSlice, eventArchivedKanbanStatusIds);
        if (
            isEmotoradWorkspace(workspace) !== true &&
            isAmaronWorkspace(workspaceCapabilities) !== true &&
            isDuluxWorkspace(workspaceCapabilities) !== true
        ) {
            return baseConfig;
        }
        return filterKanbanColumnsForModuleStatusId(baseConfig, eventSliceConfig.ignoreStatusIdSearchParam === true ? undefined : search.statusId);
    }, [
        dataSlice,
        eventArchivedKanbanStatusIds,
        eventTemplate.configuration.eventPage.kanbanBoardPageView.columnConfig,
        eventTemplate.id,
        isAmaronListSupportedTemplate,
        isAmaronCallTemplate,
        isAmaronEnquiryTemplate,
        isAmaronServiceTemplate,
        eventSliceConfig.ignoreStatusIdSearchParam,
        search.statusId,
        workspace,
    ]);
    const renderEventsTableNoDataEmptyState = useMemo(
        () =>
            isArchivedSlice === true
                ? (props: {viewportBoundsElement: HTMLElement | null}) => <TableNoArchivedDataEmptyState viewportBoundsElement={props.viewportBoundsElement} />
                : undefined,
        [isArchivedSlice],
    );

    const hideEventsViewPanelEditFooter = useMemo(() => {
        if ((isAmaronCallTemplate === true || isAmaronEnquiryTemplate === true) && isArchivedSlice !== true) {
            return effectiveEventTemplateForEditPanel.configuration.eventPage.editPanel === undefined;
        }
        if (isDuluxCallTemplate === true && isArchivedSlice !== true) {
            return effectiveEventTemplateForEditPanel.configuration.eventPage.editPanel === undefined;
        }
        return true;
    }, [effectiveEventTemplateForEditPanel, isAmaronCallTemplate, isAmaronEnquiryTemplate, isArchivedSlice, isDuluxCallTemplate]);

    const prepareEventsCalendarBackendFilterConfiguration = (
        filterConfiguration: FilterConfiguration,
    ): FilterConfiguration => {
        const filterConfigurationForBackend =
            isAmaronCallTemplate === true
                ? {
                      ...filterConfiguration,
                      lineItems: filterConfiguration.lineItems.filter(
                          (lineItem) => isAmaronEventCallsDispositionUiFilterColumn(lineItem.columnId) !== true,
                      ),
                  }
                : filterConfiguration;
        const mappedFilterConfiguration = mapFilterConfigurationToFields(
            applyEventsApiFilterConfiguration(filterConfigurationForBackend),
        );
        return {
            ...mappedFilterConfiguration,
            lineItems: [
                ...mappedFilterConfiguration.lineItems,
                {
                    columnId: "status",
                    operation: FilterOperation.notEquals,
                    value: OttopilotEventStatus.archived,
                },
                {
                    columnId: "templateId",
                    operation: FilterOperation.equals,
                    value: eventTemplateId,
                },
            ],
        };
    };

    const nativeCsvRowExportRef = useRef<(id: Uuid) => void>(() => undefined);
    const eventsDataViewerController = useOttopilotDataViewerController<EventWithEventContainer>({
        rowDisplayNameVariants: {
            singularSentenceCase: "Event",
            pluralSentenceCase: "Events",
            singularLowerCase: "event",
            pluralLowerCase: "events",
        },
        queryKeyPrefix: eventsDataViewerQueryKeyPrefix,
        hideEditPanelFooter: hideEventsViewPanelEditFooter,
        hideReadPanelEditMenuItem: isAmaronWorkspace(workspaceCapabilities) === true,
        searchColumns: eventSearchColumns,
        searchPlaceholder: eventSearchPlaceholderResolved,
        scopeFilterConfig: eventScopeFilterConfig,
        journeyScopeFilterConfig: eventJourneyScopeFilterConfig,
        filterClearConfig: {
            preservedFilterLineItemColumnIds: eventSliceConfig.preservedFilterColumnIds,
        },
        applyInitialFilterOverlay:
            isAmaronWorkspace(workspaceCapabilities) === true
                ? applyAmaronEventsInitialFilterOverlay
                : isDuluxWorkspace(workspaceCapabilities) === true
                  ? applyDuluxEventsInitialFilterOverlay
                  : undefined,
        defaultCreatedAtMonthFilter: isArchivedSlice !== true,
        calendarDisplayMetrics: amaronEventCalendarDisplayMetrics,
        mapCalendarDisplayMetricFilterColumnId: (metric) =>
            metric.columnRef.kind === "journeyAdditionalColumn"
                ? `eventContainer.${metric.columnRef.columnId}`
                : resolveCalendarDisplayMetricFilterColumnId(metric.columnRef),
        calendarDisplayMetricContext: {statusId: search.statusId},
        dateRangeFieldIds:
            eventCalendarConfig !== undefined && amaronEventCalendarDisplayMetrics === undefined
                ? {
                      startDateColumnId: "createdAt",
                      endDateColumnId: "createdAt",
                  }
                : undefined,
        calendarRowColumns: eventsCalendarRowColumns,
        calendarDataSource: "orchestration",
        calendarOrchestrationEligibility: calendarOrchestrationEligibility,
        calendarYearDayCountsQueryFn: calendarOrchestrationEligibility !== "allowed" ? null : async ({filterConfiguration, searchConfiguration, dateColumnId}) => {
            if (
                isEmotoradSyntheticEmptyFilterApplied(filterConfiguration) === true ||
                isEmotoradComputedJourneyFilterEmptyFilterApplied(filterConfiguration) === true
            ) {
                return [];
            }
            if (
                isEmotoradCallTemplate === true &&
                filterConfiguration.lineItems.some(isEmotoradCallsTestRideTakenFilterLineItem)
            ) {
                throw new Error("Year view is unavailable with the selected derived filter. Clear that filter to continue.");
            }
            const mappedFilterConfiguration = prepareEventsCalendarBackendFilterConfiguration(filterConfiguration);
            const result = await countOrchestratedEventsByFilterConfiguration({
                accessToken: heimdallUser.accessToken,
                bucket: "day",
                column: dateColumnId,
                filterConfiguration: mappedFilterConfiguration,
                intellsysBaseUrl: intellsysBaseUrl,
                searchConfiguration: searchConfiguration,
                templateId: eventTemplateId,
                workspaceId: workspace.id,
                eventTemplateAdditionalColumnIds: getAdditionalColumns(eventTemplate).map((col) => col.id),
                eventContainerAdditionalColumnIds: getAdditionalColumns(journeysModuleConfiguration).map((col) => col.id),
            });
            if (result.success !== true) {
                throw result.error;
            }
            return result.data;
        },
        rowsQueryFn: async ({
            filterConfiguration,
            limit,
            offset,
            sortConfiguration,
            searchConfiguration,
        }) => {
            if (
                isEmotoradSyntheticEmptyFilterApplied(filterConfiguration) === true ||
                isEmotoradComputedJourneyFilterEmptyFilterApplied(filterConfiguration) === true
            ) {
                return [];
            }
            const hasCallsTestRideTakenFilter =
                isEmotoradCallTemplate === true && filterConfiguration.lineItems.some(isEmotoradCallsTestRideTakenFilterLineItem);
            if (hasCallsTestRideTakenFilter === true) {
                const allEvents = await fetchEmotoradCallEventsWithSyntheticTestRideTaken({
                    filterConfiguration: filterConfiguration,
                    searchConfiguration: searchConfiguration,
                    limit: 10000,
                    offset: 0,
                });
                const filtered = applyEmotoradCallsTestRideTakenClientFilter(allEvents, filterConfiguration);
                return filtered.slice(offset, offset + limit);
            }
            const filterConfigurationForBackend =
                isAmaronCallTemplate === true
                    ? {
                          ...filterConfiguration,
                          lineItems: filterConfiguration.lineItems.filter(
                              (lineItem) => isAmaronEventCallsDispositionUiFilterColumn(lineItem.columnId) !== true,
                          ),
                      }
                    : filterConfiguration;
            const mappedFilterConfiguration = mapFilterConfigurationToFields(applyEventsApiFilterConfiguration(filterConfigurationForBackend));
            const mappedSortConfiguration = mapSortConfigurationToFields(sortConfiguration);
            const eventsResult = await getEventsWithEventContainers({
                accessToken: heimdallUser.accessToken,
                intellsysBaseUrl: intellsysBaseUrl,
                filterConfiguration: {
                    ...mappedFilterConfiguration,
                    lineItems: [
                        ...mappedFilterConfiguration.lineItems,
                        {
                            columnId: "status",
                            operation: FilterOperation.notEquals,
                            value: OttopilotEventStatus.archived,
                        },
                        {
                            columnId: "templateId",
                            operation: FilterOperation.equals,
                            value: eventTemplateId,
                        },
                    ],
                },
                sortConfiguration: mappedSortConfiguration,
                searchConfiguration: searchConfiguration,
                limit: limit,
                offset: offset,
                workspaceId: workspace.id,
                eventTemplateId: eventTemplateId,
                eventTemplateAdditionalColumnIds: getAdditionalColumns(eventTemplate).map((col) => col.id),
                eventContainerAdditionalColumnIds: getAdditionalColumns(journeysModuleConfiguration).map((col) => col.id),
            });
            if (eventsResult.success !== true) {
                throw eventsResult.error;
            }
            let events =
                isEmotoradJourneysCustomer === true
                    ? eventsResult.data.map((event) => emotoradEventSyntheticFunction(event, eventTemplate, journeysModuleConfiguration))
                    : isAmaronCallTemplate === true
                      ? eventsResult.data.map((event) => amaronCallEventSyntheticFunction(event, eventTemplate))
                      : eventsResult.data;
            if (isEmotoradCallTemplate === true) {
                events = await applyEmotoradCallEventsSyntheticTestRideTakenToEvents(events);
            }
            const calendarCfg = eventTemplate.configuration.eventPage.calendarConfig;
            if (calendarCfg !== undefined) {
                const calendarTitleTemplate = calendarCfg.calendarTitleOttopilotStringTemplate;
                return events.map((event) => {
                    const merged: Record<Uuid, unknown> = {
                        ...event.eventContainer.additionalColumnValues,
                        ...event.additionalColumnValues,
                    };
                    const rawTitle =
                        calendarTitleTemplate !== undefined && calendarTitleTemplate.trim() !== ""
                            ? processOttopilotStringTemplate(calendarTitleTemplate, merged)
                            : isAmaronWorkspace(workspaceCapabilities) === true
                              ? processOttopilotStringTemplate(journeysModuleConfiguration.configuration.displayNameTemplate, merged)
                              : eventTemplate.configuration.displayNameVariants.singularSentenceCase;
                    const title = isEmotoradWorkspace(workspace) === true ? formatEmotoradCalendarEventTitle(rawTitle, merged) : rawTitle;
                    const calendarRow = {
                        ...event,
                        title: title.trim() !== "" ? title : eventTemplate.configuration.displayNameVariants.singularSentenceCase,
                        isAlldayEvent: false,
                    };
                    if (amaronEventCalendarDisplayMetrics !== undefined) {
                        return calendarRow;
                    }
                    const startDate = resolveEventCalendarIsoStart(event, "createdAt", event.createdAt);
                    return {
                        ...calendarRow,
                        startDate: startDate,
                        endDate: startDate,
                    };
                });
            }

            return events;
        },
        calendarRowsQueryFn: async ({
            filterConfiguration,
            limit,
            offset,
            sortConfiguration,
            searchConfiguration,
            columns,
            maxRowsPerDay,
            maxRowsPerDayColumn,
        }) => {
            if (
                isEmotoradSyntheticEmptyFilterApplied(filterConfiguration) === true ||
                isEmotoradComputedJourneyFilterEmptyFilterApplied(filterConfiguration) === true
            ) {
                return [];
            }
            const hasCallsTestRideTakenFilter =
                isEmotoradCallTemplate === true &&
                filterConfiguration.lineItems.some(isEmotoradCallsTestRideTakenFilterLineItem);
            if (hasCallsTestRideTakenFilter === true) {
                const allEvents = await fetchOrchestratedEmotoradCallEventsWithSyntheticTestRideTaken({
                    filterConfiguration,
                    searchConfiguration,
                    limit: 10000,
                    offset: 0,
                });
                return applyEmotoradCallsTestRideTakenClientFilter(allEvents, filterConfiguration).slice(
                    offset,
                    offset + limit,
                );
            }
            const mappedFilterConfiguration = prepareEventsCalendarBackendFilterConfiguration(filterConfiguration);
            const mappedSortConfiguration = mapSortConfigurationToFields(sortConfiguration);
            const eventsResult = await getOrchestratedEventsWithEventContainers({
                accessToken: heimdallUser.accessToken,
                columns: columns ?? null,
                eventContainerAdditionalColumnIds: getAdditionalColumns(journeysModuleConfiguration).map((col) => col.id),
                eventTemplateAdditionalColumnIds: getAdditionalColumns(eventTemplate).map((col) => col.id),
                filterConfiguration: mappedFilterConfiguration,
                intellsysBaseUrl,
                limit,
                maxRowsPerDay: maxRowsPerDay ?? null,
                maxRowsPerDayColumn: maxRowsPerDayColumn ?? null,
                offset,
                searchConfiguration,
                sortConfiguration: mappedSortConfiguration,
                templateId: eventTemplateId,
                workspaceId: workspace.id,
            });
            if (eventsResult.success !== true) {
                throw eventsResult.error;
            }
            let events =
                isEmotoradJourneysCustomer === true
                    ? eventsResult.data.map((event) =>
                          emotoradEventSyntheticFunction(event, eventTemplate, journeysModuleConfiguration),
                      )
                    : isAmaronCallTemplate === true
                      ? eventsResult.data.map((event) => amaronCallEventSyntheticFunction(event, eventTemplate))
                      : eventsResult.data;
            if (isEmotoradCallTemplate === true) {
                events = await applyEmotoradCallEventsSyntheticTestRideTakenToEvents(events);
            }
            const calendarCfg = eventTemplate.configuration.eventPage.calendarConfig;
            if (calendarCfg === undefined) {
                return events;
            }
            const calendarTitleTemplate = calendarCfg.calendarTitleOttopilotStringTemplate;
            return events.map((event) => {
                const merged: Record<Uuid, unknown> = {
                    ...event.eventContainer.additionalColumnValues,
                    ...event.additionalColumnValues,
                };
                const rawTitle =
                    calendarTitleTemplate !== undefined && calendarTitleTemplate.trim() !== ""
                        ? processOttopilotStringTemplate(calendarTitleTemplate, merged)
                        : isAmaronWorkspace(workspaceCapabilities) === true
                          ? processOttopilotStringTemplate(
                                journeysModuleConfiguration.configuration.displayNameTemplate,
                                merged,
                            )
                          : eventTemplate.configuration.displayNameVariants.singularSentenceCase;
                const title =
                    isEmotoradWorkspace(workspace) === true
                        ? formatEmotoradCalendarEventTitle(rawTitle, merged)
                        : rawTitle;
                const calendarRow = {
                    ...event,
                    title:
                        title.trim() !== ""
                            ? title
                            : eventTemplate.configuration.displayNameVariants.singularSentenceCase,
                    isAlldayEvent: false,
                };
                if (amaronEventCalendarDisplayMetrics !== undefined) {
                    return calendarRow;
                }
                const startDate = resolveEventCalendarIsoStart(event, "createdAt", event.createdAt);
                return {...calendarRow, startDate, endDate: startDate};
            });
        },
        initialSortConfiguration: {
            revisionId: generateUuid(),
            lineItems: [
                {
                    columnId: "createdAt",
                    direction: SortDirection.descending,
                },
            ],
        },
        canEditRow: (event: EventWithEventContainer) => {
            if (isArchivedSlice === true) {
                return false;
            }
            if (eventTemplateId === (DuluxEventTypes.Calls as Uuid)) {
                return isDuluxScheduledCallEvent(event) === true;
            }
            if (eventTemplateId === (DuluxEventTypes.Appointments as Uuid)) {
                return isDuluxScheduledAppointmentEvent(event) === true;
            }
            if (eventTemplateId === (DuluxEventTypes.Onboarding as Uuid)) {
                return isDuluxNotOnboardedOnboardingEvent(event) === true;
            }
            if (
                eventTemplateId === (DuluxEventTypes.Enquiry as Uuid) ||
                eventTemplateId === (AmaronEventTypes.Service as Uuid)
            ) {
                return false;
            } else if (eventTemplateId === (BobcardEventTypes.applicationsInitiated as Uuid)) {
                return false;
            } else if (eventTemplateId === (EmotoradEventTypes.sales as Uuid)) {
                const salesStatus = String(event?.additionalColumnValues?.[EMOTORAD__EVENT__SALES__STATUS__COLUMN_ID] ?? "");
                if (salesStatus === String(EmotoradSalesStatusEnum.VERIFIED)) {
                    return false;
                }
            }
            return true;
        },
        rowCountQueryFn: async ({filterConfiguration, searchConfiguration}) => {
            if (
                isEmotoradSyntheticEmptyFilterApplied(filterConfiguration) === true ||
                isEmotoradComputedJourneyFilterEmptyFilterApplied(filterConfiguration) === true
            ) {
                return 0 as Integer;
            }
            const filterConfigurationForBackend =
                isAmaronCallTemplate === true
                    ? {
                          ...filterConfiguration,
                          lineItems: filterConfiguration.lineItems.filter(
                              (lineItem) => isAmaronEventCallsDispositionUiFilterColumn(lineItem.columnId) !== true,
                          ),
                      }
                    : filterConfiguration;
            const mappedFilterConfiguration = mapFilterConfigurationToFields(applyEventsApiFilterConfiguration(filterConfigurationForBackend));
            const eventsLengthResult = await getEvents_Length({
                accessToken: heimdallUser.accessToken,
                intellsysBaseUrl: intellsysBaseUrl,
                filterConfiguration: {
                    ...mappedFilterConfiguration,
                    lineItems: [
                        ...mappedFilterConfiguration.lineItems,
                        {
                            columnId: "status",
                            operation: FilterOperation.notEquals,
                            value: OttopilotEventStatus.archived,
                        },
                        {
                            columnId: "templateId",
                            operation: FilterOperation.equals,
                            value: eventTemplateId,
                        },
                    ],
                },
                searchConfiguration: searchConfiguration,
                workspaceId: workspace.id,
                eventTemplateId: eventTemplateId,
                eventTemplateAdditionalColumnIds: getAdditionalColumns(eventTemplate).map((col) => col.id),
                eventContainerAdditionalColumnIds: getAdditionalColumns(journeysModuleConfiguration).map((col) => col.id),
            });
            if (eventsLengthResult.success !== true) {
                throw eventsLengthResult.error;
            }
            const eventsLength = eventsLengthResult.data;

            return eventsLength;
        },
        columnValuesQueryFn: async ({columnId, filterConfiguration, searchConfiguration}) => {
            const tableViewColumn = resolveTableColumnConfig(columnId);
            const resolvedColumnId = resolveTableColumnField(columnId);
            if (
                isAmaronWorkspace(workspaceCapabilities) === true &&
                amaronEventStatusNavConfigForInitialFilter !== null &&
                String(resolvedColumnId) === String(amaronEventStatusNavConfigForInitialFilter.statusColumnId)
            ) {
                const activeStatusId = resolveModuleStatusIdForFilter({
                    statusIdFromSearch: search.statusId,
                    validStatusIds: getAmaronModuleStatusNavValidStatusIds(amaronEventStatusNavConfigForInitialFilter),
                    ignoreStatusIdSearchParam: eventSliceConfig.ignoreStatusIdSearchParam,
                });
                return resolveAmaronModuleStatusFilterPickerValues({
                    statusNavConfig: amaronEventStatusNavConfigForInitialFilter,
                    activeStatusId,
                });
            }
            const finalizeColumnValues = (values: Array<string>): Array<string> =>
                isEmotoradActionPlatformColumnId(resolvedColumnId) === true ? sanitizeEmotoradFilterColumnValues(values) : values;
            if (emotoradCreatedByRoleColumnIds.has(resolvedColumnId) === true) {
                return getEmotoradEventCreatorRoleTemplateIds();
            }
            if (emotoradCreatedByNameColumnIds.has(resolvedColumnId) === true) {
                return getEmotoradEventCreatorUserIds();
            }
            if (resolvedColumnId === "updatedBy") {
                return getEmotoradEventUpdaterUserIds();
            }
            if (resolvedColumnId === String(EMOTORAD__EVENT_CONTAINER__CUSTOMER_ZONE__COLUMN_ID)) {
                return getEmotoradSalesCustomerZoneFilterValues();
            }
            if (resolvedColumnId === String(EMOTORAD__EVENT_CONTAINER__DEALER_CITY__COLUMN_ID)) {
                return getEmotoradComputedColumnFilterValues(resolveEmotoradDealerCity);
            }
            if (resolvedColumnId === String(EMOTORAD__EVENT_CONTAINER__DEALER_STATE__COLUMN_ID)) {
                return getEmotoradComputedColumnFilterValues(resolveEmotoradDealerState);
            }
            if (resolvedColumnId === String(EMOTORAD__EVENT_CONTAINER__DEALER_ZONE__COLUMN_ID)) {
                return getEmotoradComputedColumnFilterValues(resolveEmotoradDealerZone);
            }
            if (resolvedColumnId === String(EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_CONTACT__COLUMN_ID)) {
                return getEmotoradComputedColumnFilterValues(resolveEmotoradDealerContact);
            }
            if (resolvedColumnId === String(EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_EMAIL__COLUMN_ID)) {
                return getEmotoradComputedColumnFilterValues(resolveEmotoradDealerEmail);
            }
            if (isEmotoradCallTemplate === true && isEmotoradCallsTestRideTakenFilterColumnId(resolvedColumnId) === true) {
                if (filterConfiguration === undefined) {
                    return [];
                }
                return getEmotoradCallsTestRideTakenFilterValues(filterConfiguration, searchConfiguration);
            }
            if (isEmotoradJourneysCustomer === true) {
                const syntheticDispositionLevel = resolveEmotoradSyntheticDispositionFilterLevel(resolvedColumnId);
                if (syntheticDispositionLevel !== undefined) {
                    return getEmotoradUniqueDispositionLevelValues(syntheticDispositionLevel);
                }
                if (resolvedColumnId === "createdBy") {
                    return getEmotoradEventCreatorUserIds();
                }
                if (resolvedColumnId === "updatedBy") {
                    return getEmotoradEventUpdaterUserIds();
                }
                return getEmotoradAdditionalColumnFilterValues(resolvedColumnId);
            }
            const eventColumnValuesQueryArgs = {
                workspaceId: workspace.id,
                eventTemplateId: eventTemplateId,
                columnId: resolvedColumnId,
                intellsysBaseUrl: intellsysBaseUrl,
                accessToken: heimdallUser.accessToken,
            };
            const eventContainerColumnValuesQueryArgs = {
                workspaceId: workspace.id,
                eventContainerTemplateId: journeysModuleConfiguration.id,
                columnId: resolvedColumnId,
                intellsysBaseUrl: intellsysBaseUrl,
                accessToken: heimdallUser.accessToken,
            };

            if (tableViewColumn?.columnType === "OttopilotEvent") {
                return finalizeColumnValues(await getEventColumnValuesFromTemplateEvents(eventColumnValuesQueryArgs));
            }

            if (tableViewColumn?.columnType === "OttopilotEventContainer") {
                return finalizeColumnValues(await getEventContainerColumnValuesFromTemplateEvents(eventContainerColumnValuesQueryArgs));
            }

            const isEventColumn = getAdditionalColumns(eventTemplate).some((col) => col.id === resolvedColumnId);
            if (isEventColumn) {
                return finalizeColumnValues(await getEventColumnValuesFromTemplateEvents(eventColumnValuesQueryArgs));
            }

            const isJourneyColumn = getAdditionalColumns(journeysModuleConfiguration).some((col) => col.id === resolvedColumnId);
            if (isJourneyColumn) {
                return finalizeColumnValues(await getEventContainerColumnValuesFromTemplateEvents(eventContainerColumnValuesQueryArgs));
            }

            const journeysColumnValuesResult = await getEventContainerColumnValues({
                workspaceId: workspace.id,
                columnId: resolvedColumnId,
                intellsysBaseUrl: intellsysBaseUrl,
                accessToken: heimdallUser.accessToken,
                templateId: journeysModuleConfiguration.id,
            });
            if (journeysColumnValuesResult.success !== true) {
                throw journeysColumnValuesResult.error;
            }
            return finalizeColumnValues(journeysColumnValuesResult.data);
        },
        columns: {
            revisionId: uniqueUuid`70d624af-ff1d-455b-94b6-16ac8afb2fa5`,
            lineItems: ((): Array<OttopilotColumn<EventWithEventContainer>> => {
                const finalizeScaffoldEventColumns = (columns: Array<OttopilotColumn<EventWithEventContainer>>): Array<OttopilotColumn<EventWithEventContainer>> => {
                    let result = columns;
                    if (amaronEventStatusNavConfigForInitialFilter !== null) {
                        const activeStatusId = resolveModuleStatusIdForFilter({
                            statusIdFromSearch: search.statusId,
                            validStatusIds: getAmaronModuleStatusNavValidStatusIds(amaronEventStatusNavConfigForInitialFilter),
                            ignoreStatusIdSearchParam: eventSliceConfig.ignoreStatusIdSearchParam,
                        });
                        result = applyAmaronModuleStatusFilterVisibilityToColumns(result, {
                            statusNavConfig: amaronEventStatusNavConfigForInitialFilter,
                            activeStatusId,
                        });
                    }
                    if (isDuluxWorkspace(workspaceCapabilities) === true && duluxEventStatusNavConfigForInitialFilter !== null) {
                        const duluxActiveStatusId = resolveModuleStatusIdForFilter({
                            statusIdFromSearch: search.statusId,
                            validStatusIds: getDuluxModuleStatusNavValidStatusIds(duluxEventStatusNavConfigForInitialFilter),
                            ignoreStatusIdSearchParam: eventSliceConfig.ignoreStatusIdSearchParam,
                        });
                        result = applyModuleStatusFilterVisibilityToColumns(result, {
                            statusNavConfig: duluxEventStatusNavConfigForInitialFilter,
                            activeStatusId: duluxActiveStatusId,
                            moduleStatusFilterScaffold: DULUX_MODULE_STATUS_FILTER_SCAFFOLD,
                        });
                    }
                    if (isDuluxWorkspace(workspaceCapabilities) === true && isDuluxListSupportedTemplate === true) {
                        result = applyDuluxEventTableColumnFiltering(result, {
                            users,
                            userTemplates,
                            platformUserDetailsByUserId,
                            updatedByValuesQueryFn: getEmotoradEventUpdaterUserIds,
                        });
                    }
                    return result;
                };

                if (isAmaronWorkspace(workspaceCapabilities) && eventTemplate.id === (AmaronEventTypes.Calls as Uuid)) {
                    const eventAdditionalColumnById = new Map(getAdditionalColumns(eventTemplate).map((column) => [column.id, column]));
                    const journeyAdditionalColumnById = new Map(getAdditionalColumns(journeysModuleConfiguration).map((column) => [column.id, column]));
                    return finalizeScaffoldEventColumns(
                        buildAmaronCallTableColumns({
                            eventTemplate,
                            eventTemplates,
                            users,
                            userTemplates,
                            organizationalUnits,
                            organizationalUnitTemplates,
                            btlActivities,
                            products,
                            eventContainerTemplate: journeysModuleConfiguration,
                            journeyAdditionalColumnById,
                            eventAdditionalColumnById,
                            platformUserDetailsByUserId,
                        }),
                    );
                }

                const generatedLineItems: Array<OttopilotColumn<EventWithEventContainer>> =
                    eventTemplate.configuration.eventPage.tableViewColumns !== undefined && shouldSkipEventTableViewColumnsForExplicitOrder !== true
                        ? eventTableViewColumnsToOttopilotColumns(getAdditionalColumns, eventTemplate.configuration.eventPage.tableViewColumns, {
                              userDefinedObjectOptionsByTemplateId,
                              users: users,
                              userTemplates: userTemplates,
                              btlActivities: btlActivities,
                              formatDateTime: formatDateTime,
                              getOuDisplayName: getOuDisplayName,
                              getProductCatalogueDisplayName: (productCatalogueId: Uuid) => {
                                  const productCatalogue = products.find((p) => p.id === productCatalogueId);
                                  return productCatalogue === undefined ? productCatalogueId : getProductCatalogueDisplayName(productCatalogue, productCatalogueTemplates);
                              },
                              eventTemplate: eventTemplate,
                              eventContainerAdditionalColumns: getAdditionalColumns(journeysModuleConfiguration),
                              platformUserDetailsByUserId,
                              eventColumnFieldIdsResolvedAsUserIds:
                                  isDuluxWorkspace(workspaceCapabilities) === true
                                      ? new Set(DULUX_EVENT_TEMPLATE_EVENT_OWNER_COLUMN_IDS.map((columnId) => String(columnId)))
                                      : undefined,
                          })
                        : [
                              // ...(eventTemplateId === DhiraaguEventTypes.call
                              //     ? [
                              //           {
                              //               id: "actionButtonDhiraaguCall",
                              //               type: OttopilotColumnType.string,
                              //               displayName: "Actions",
                              //               pinned: "right",
                              //               valueGetter: () => "",
                              //               cellRenderer: (row: EventWithEventContainer) => {
                              //                   const status = row.additionalColumnValues[
                              //                       DHIRAAGU__EVENT__CALLS__STATUS__COLUMN_ID
                              //                   ];
                              //                   if (status !== DhiraaguCallStatusEnum.SCHEDULED) {
                              //                       return <span>-</span>;
                              //                   }
                              //                   return <DhiraaguCallActionCell event={row} />;
                              //               },
                              //           },
                              //       ]
                              //     : []),
                              ...getAdditionalColumns(journeysModuleConfiguration).map((column): OttopilotColumn<EventWithEventContainer> => {
                                  // Get the first eventTemplate as a fallback (required by type but may not be used by all column types)
                                  const fallbackEventTemplate = eventTemplates[0];
                                  if (fallbackEventTemplate === undefined) {
                                      throw new Error("No event templates available");
                                  }
                                  const isTimestamp = column.type === UdoColumnType.timestamp;

                                  if (isTimestamp) {
                                      return {
                                          id: column.id,
                                          type: OttopilotColumnType.iso8601DateTime,
                                          displayName: column.displayName,
                                          valueGetter: (event) => {
                                              const value = event.eventContainer.additionalColumnValues[column.id];
                                              // Return undefined if value is null/undefined, otherwise cast to Iso8601DateTime
                                              return (value ?? undefined) as any;
                                          },
                                          valueFormatter: (value) => formatDateTime(value),
                                          sortingCapability: true,
                                          filteringCapability_contains: true,
                                      };
                                  }

                                  return {
                                      id: column.id,
                                      type: OttopilotColumnType.string,
                                      displayName: column.displayName,
                                      valueGetter: (event) => {
                                          const raw = event.eventContainer.additionalColumnValues[column.id];
                                          if (raw === null || raw === undefined) {
                                              return "";
                                          }
                                          return String(raw);
                                      },
                                      valueFormatter: (value) => {
                                          return getUserDefinedObjectAdditionalColumnDetails(column).stringValueGetter({
                                              value: value as never,
                                              users: users,
                                              userTemplates: userTemplates,
                                              platformUserDetailsByUserId,
                                              organizationalUnits: organizationalUnits,
                                              organizationalUnitTemplates: organizationalUnitTemplates,
                                              products: products,
                                          });
                                      },
                                      sortingCapability: true,
                                      filteringCapability_contains: true,
                                  };
                              }),
                              {
                                  id: "id",
                                  type: OttopilotColumnType.uuid,
                                  displayName: "ID",
                                  valueGetter: (event) => event.id,
                                  sortingCapability: false,
                                  filteringCapability_contains: false,
                              },
                              {
                                  id: "createdBy",
                                  type: OttopilotColumnType.uuid,
                                  displayName: "Created by",
                                  valueGetter: (event) => event.createdBy,
                                  valueFormatter: (value) => getUserDisplayName(value, users, userTemplates, platformUserDetailsByUserId),
                                  sortingCapability: false,
                                  filteringCapability_contains: false,
                              },
                              {
                                  id: "createdAt",
                                  type: OttopilotColumnType.iso8601DateTime,
                                  displayName: "Created at",
                                  valueGetter: (event) => event.createdAt,
                                  valueFormatter: (value) => formatDateTime(value),
                                  sortingCapability: true,
                                  filteringCapability_contains: false,
                              },
                              {
                                  id: "updatedBy",
                                  type: OttopilotColumnType.uuid,
                                  displayName: "Updated by",
                                  valueGetter: (event) => event.updatedBy,
                                  valueFormatter: (value) => getUserDisplayName(value, users, userTemplates, platformUserDetailsByUserId),
                                  sortingCapability: false,
                                  filteringCapability_contains: false,
                              },
                              {
                                  id: "updatedAt",
                                  type: OttopilotColumnType.iso8601DateTime,
                                  displayName: "Updated at",
                                  valueGetter: (event) => event.updatedAt,
                                  valueFormatter: (value) => formatDateTime(value),
                                  sortingCapability: true,
                                  filteringCapability_contains: false,
                              },
                              {
                                  id: "templateId",
                                  type: OttopilotColumnType.uuid,
                                  displayName: "Type",
                                  valueGetter: (event) => event.templateId,
                                  valueFormatter: (value) => eventTemplates.find((eventTemplate) => eventTemplate.id === value)?.displayName ?? "?",
                                  sortingCapability: false,
                                  filteringCapability_contains: false,
                              },
                              {
                                  id: "actionButton",
                                  type: OttopilotColumnType.string,
                                  displayName: "Actions",
                                  valueGetter: () => "",
                                  sortingCapability: false,
                                  filteringCapability_contains: false,
                              },
                              ...getAdditionalColumns(effectiveEventTemplateForTableView)
                                  .filter((eventColumn: {displayName: string}) => {
                                      const hasMatchingJourneyColumn = getAdditionalColumns(journeysModuleConfiguration).some(
                                          (journeyColumn) => journeyColumn.displayName === eventColumn.displayName,
                                      );
                                      return !hasMatchingJourneyColumn;
                                  })
                                  .map((column): OttopilotColumn<EventWithEventContainer> => {
                                      if (isDuluxWorkspace(workspaceCapabilities) === true && DULUX_EVENT_TEMPLATE_EVENT_OWNER_COLUMN_IDS.some((id) => id === column.id)) {
                                          return {
                                              id: column.id,
                                              type: OttopilotColumnType.string,
                                              displayName: column.displayName,
                                              valueGetter: (event) => {
                                                  const userId = event?.additionalColumnValues[column.id] as Uuid | undefined | null;
                                                  if (userId === undefined || userId === null) {
                                                      return "";
                                                  }
                                                  const trimmed = String(userId).trim();
                                                  return trimmed === "" ? "" : trimmed;
                                              },
                                              valueFormatter: (value: string) => {
                                                  if (value === "") {
                                                      return "-";
                                                  }
                                                  return getUserDisplayName(value as Uuid, users, userTemplates, platformUserDetailsByUserId);
                                              },
                                              sortingCapability: true,
                                              filteringCapability_contains: true,
                                          };
                                      }
                                      if (
                                          isDuluxWorkspace(workspaceCapabilities) === true &&
                                          (column.id === DULUX__EVENT__CALLS__CREATED_BY_ROLE__COLUMN_ID ||
                                              column.id === DULUX__EVENT__APPOINTMENT__CREATED_BY_ROLE__COLUMN_ID)
                                      ) {
                                          return {
                                              id: column.id,
                                              type: OttopilotColumnType.string,
                                              displayName: column.displayName,
                                              valueGetter: (event) => {
                                                  return event?.additionalColumnValues[column.id] as Uuid;
                                              },
                                              valueFormatter: (value) => {
                                                  if (value === null || value === undefined) {
                                                      return "-";
                                                  }
                                                  return userTemplates.find((userTemplate) => userTemplate.id === value)?.displayName ?? "-";
                                              },
                                          };
                                      }
                                      const isTimestamp = column.type === UdoColumnType.timestamp;

                                      if (isTimestamp) {
                                          return {
                                              id: column.id,
                                              type: OttopilotColumnType.iso8601DateTime,
                                              displayName: column.displayName,
                                              valueGetter: (event): Iso8601DateTime => {
                                                  const value = event.additionalColumnValues[column.id];
                                                  if (value === null || value === undefined) {
                                                      return "" as Iso8601DateTime;
                                                  }
                                                  return value as Iso8601DateTime;
                                              },
                                              valueFormatter: (value) => formatDateTime(value),
                                              sortingCapability: true,
                                              filteringCapability_contains: true,
                                          };
                                      }
                                      return {
                                          id: column.id,
                                          type: OttopilotColumnType.string,
                                          displayName: column.displayName,
                                          valueGetter: (event) => {
                                              return eventTableAdditionalColumnRawToString(event.additionalColumnValues[column.id]);
                                          },
                                          valueFormatter: (value) => {
                                              return getUserDefinedObjectAdditionalColumnDetails(column).stringValueGetter({
                                                  value: value as never,
                                                  users: users,
                                                  userTemplates: userTemplates,
                                                  platformUserDetailsByUserId,
                                                  organizationalUnits: organizationalUnits,
                                                  organizationalUnitTemplates: organizationalUnitTemplates,
                                                  products: products,
                                              });
                                          },
                                          sortingCapability: true,
                                          filteringCapability_contains: true,
                                      };
                                  }),
                          ];

                if (workspaceExplicitOrderedEventColumnIds === null) {
                    return finalizeScaffoldEventColumns(generatedLineItems);
                }

                const lineItemsWithEmotoradCallSynthetic = isEmotoradCallTemplate
                    ? [
                          ...generatedLineItems,
                          {
                              id: EMOTORAD__EVENT__CALL__L1_DISPOSITION__COLUMN_ID,
                              type: OttopilotColumnType.string,
                              displayName: "L1 Disposition",
                              valueGetter: (event: EventWithEventContainer) => String(event.additionalColumnValues[EMOTORAD__EVENT__CALL__L1_DISPOSITION__COLUMN_ID] ?? ""),
                              sortingCapability: true,
                              filteringCapability_in: {
                                  valuesQueryFn: async () => getEmotoradUniqueDispositionLevelValues("l1"),
                              },
                              customFilter: buildDispositionLevelCustomFilter("l1", EMOTORAD__EVENT__CALLS__CALL_DISPOSITION__COLUMN_ID),
                          },
                          {
                              id: EMOTORAD__EVENT__CALL__L2_DISPOSITION__COLUMN_ID,
                              type: OttopilotColumnType.string,
                              displayName: "L2 Disposition",
                              valueGetter: (event: EventWithEventContainer) => String(event.additionalColumnValues[EMOTORAD__EVENT__CALL__L2_DISPOSITION__COLUMN_ID] ?? ""),
                              sortingCapability: true,
                              filteringCapability_in: {
                                  valuesQueryFn: async () => getEmotoradUniqueDispositionLevelValues("l2"),
                              },
                              customFilter: buildDispositionLevelCustomFilter("l2", EMOTORAD__EVENT__CALLS__CALL_DISPOSITION__COLUMN_ID),
                          },
                          {
                              id: EMOTORAD__EVENT__CALL__L3_DISPOSITION__COLUMN_ID,
                              type: OttopilotColumnType.string,
                              displayName: "L3 Disposition",
                              valueGetter: (event: EventWithEventContainer) => String(event.additionalColumnValues[EMOTORAD__EVENT__CALL__L3_DISPOSITION__COLUMN_ID] ?? ""),
                              sortingCapability: true,
                              filteringCapability_in: {
                                  valuesQueryFn: async () => getEmotoradUniqueDispositionLevelValues("l3"),
                              },
                              customFilter: buildDispositionLevelCustomFilter("l3", EMOTORAD__EVENT__CALLS__CALL_DISPOSITION__COLUMN_ID),
                          },
                      ]
                    : generatedLineItems;

                const lineItemsWithEmotoradDispositionSynthetic = isEmotoradWalkinTemplate
                    ? [
                          ...lineItemsWithEmotoradCallSynthetic,
                          {
                              id: EMOTORAD__EVENT__WALKIN__L1_DISPOSITION__COLUMN_ID,
                              type: OttopilotColumnType.string,
                              displayName: "L1 Disposition",
                              valueGetter: (event: EventWithEventContainer) =>
                                  String(event.additionalColumnValues[EMOTORAD__EVENT__WALKIN__L1_DISPOSITION__COLUMN_ID] ?? ""),
                              sortingCapability: true,
                              filteringCapability_in: {
                                  valuesQueryFn: async () => getEmotoradUniqueDispositionLevelValues("l1"),
                              },
                              customFilter: buildDispositionLevelCustomFilter("l1", EMOTORAD__EVENT__WALKINS__DISPOSITION__COLUMN_ID),
                          },
                          {
                              id: EMOTORAD__EVENT__WALKIN__L2_DISPOSITION__COLUMN_ID,
                              type: OttopilotColumnType.string,
                              displayName: "L2 Disposition",
                              valueGetter: (event: EventWithEventContainer) =>
                                  String(event.additionalColumnValues[EMOTORAD__EVENT__WALKIN__L2_DISPOSITION__COLUMN_ID] ?? ""),
                              sortingCapability: true,
                              filteringCapability_in: {
                                  valuesQueryFn: async () => getEmotoradUniqueDispositionLevelValues("l2"),
                              },
                              customFilter: buildDispositionLevelCustomFilter("l2", EMOTORAD__EVENT__WALKINS__DISPOSITION__COLUMN_ID),
                          },
                          {
                              id: EMOTORAD__EVENT__WALKIN__L3_DISPOSITION__COLUMN_ID,
                              type: OttopilotColumnType.string,
                              displayName: "L3 Disposition",
                              valueGetter: (event: EventWithEventContainer) =>
                                  String(event.additionalColumnValues[EMOTORAD__EVENT__WALKIN__L3_DISPOSITION__COLUMN_ID] ?? ""),
                              sortingCapability: true,
                              filteringCapability_in: {
                                  valuesQueryFn: async () => getEmotoradUniqueDispositionLevelValues("l3"),
                              },
                              customFilter: buildDispositionLevelCustomFilter("l3", EMOTORAD__EVENT__WALKINS__DISPOSITION__COLUMN_ID),
                          },
                      ]
                    : lineItemsWithEmotoradCallSynthetic;

                const isEmotoradSourceChannelValuePresent = (raw: unknown): boolean => raw !== null && raw !== undefined && String(raw).trim() !== "";
                const resolveEmotoradSourceChannelRawValue = (event: EventWithEventContainer): unknown => {
                    const journey = event.eventContainer.additionalColumnValues[EMOTORAD__EVENT_CONTAINER__SOURCE_CHANNEL__COLUMN_ID];
                    const sales = event.additionalColumnValues[EMOTORAD__EVENT__SALES__SOURCE_CHANNEL__COLUMN_ID];
                    const enquiry = event.additionalColumnValues[EMOTORAD__EVENT__ENQUIRY__SOURCE_CHANNEL__COLUMN_ID];
                    if (event.templateId === (EmotoradEventTypes.sales as Uuid)) {
                        if (isEmotoradSourceChannelValuePresent(sales)) return sales;
                        if (isEmotoradSourceChannelValuePresent(journey)) return journey;
                        return enquiry;
                    }
                    // Lead edit panel uses columnType "Auto" for Source Channel(s), which prefers
                    // {@link journeysModuleConfiguration} over event columns — writes land on journey first.
                    if (event.templateId === (EmotoradEventTypes.lead as Uuid)) {
                        if (isEmotoradSourceChannelValuePresent(journey)) return journey;
                        if (isEmotoradSourceChannelValuePresent(enquiry)) return enquiry;
                        return sales;
                    }
                    if (isEmotoradSourceChannelValuePresent(journey)) return journey;
                    if (isEmotoradSourceChannelValuePresent(enquiry)) return enquiry;
                    return sales;
                };

                const formatEmotoradSourceChannelValueForDisplay = (raw: string): string => {
                    if (raw === "") {
                        return "";
                    }
                    const journeyColumn = getAdditionalColumns(journeysModuleConfiguration).find((c) => c.id === EMOTORAD__EVENT_CONTAINER__SOURCE_CHANNEL__COLUMN_ID);
                    const eventColumn = eventTemplates
                        .flatMap((t) => getAdditionalColumns(t))
                        .find((c) => c.id === EMOTORAD__EVENT__SALES__SOURCE_CHANNEL__COLUMN_ID || c.id === EMOTORAD__EVENT__ENQUIRY__SOURCE_CHANNEL__COLUMN_ID);
                    const column = journeyColumn ?? eventColumn;
                    const options = getAdditionalColumnOptions(column, userDefinedObjectOptionsByTemplateId) ?? [];
                    if (options.length > 0) {
                        const option = options.find((o) => o.id === raw);
                        if (option !== undefined) {
                            return option.displayName;
                        }
                    }
                    return raw;
                };

                const formatEmotoradPreferredColorProductIdForDisplay = (productId: string): string => {
                    if (productId === "") {
                        return "-";
                    }
                    const product = products?.find((p) => p.id === productId);
                    if (product === undefined) {
                        return productId;
                    }
                    const colorName = product.additionalColumnValues[EMOTORAD__PRODUCT_CATALOGUE__COLOR_NAME__COLUMN_ID];
                    if (typeof colorName === "string" && colorName.trim() !== "") {
                        return colorName;
                    }
                    return getProductCatalogueDisplayName(product, productCatalogueTemplates);
                };

                const lineItemsWithEmotoradJourneyDispositionSynthetic = isEmotoradWorkspace(workspace)
                    ? [
                          ...lineItemsWithEmotoradDispositionSynthetic,
                          {
                              id: EMOTORAD__EVENT_CONTAINER__L1_DISPOSITION__COLUMN_ID,
                              type: OttopilotColumnType.string,
                              displayName: "L1 Disposition",
                              valueGetter: (event: EventWithEventContainer) =>
                                  String(event.eventContainer.additionalColumnValues[EMOTORAD__EVENT_CONTAINER__L1_DISPOSITION__COLUMN_ID] ?? ""),
                              sortingCapability: true,
                              filteringCapability_in: {
                                  valuesQueryFn: async () => getEmotoradUniqueDispositionLevelValues("l1"),
                              },
                              customFilter: buildDispositionLevelCustomFilter("l1", "disposition"),
                          },
                          {
                              id: EMOTORAD__EVENT_CONTAINER__L2_DISPOSITION__COLUMN_ID,
                              type: OttopilotColumnType.string,
                              displayName: "L2 Disposition",
                              valueGetter: (event: EventWithEventContainer) =>
                                  String(event.eventContainer.additionalColumnValues[EMOTORAD__EVENT_CONTAINER__L2_DISPOSITION__COLUMN_ID] ?? ""),
                              sortingCapability: true,
                              filteringCapability_in: {
                                  valuesQueryFn: async () => getEmotoradUniqueDispositionLevelValues("l2"),
                              },
                              customFilter: buildDispositionLevelCustomFilter("l2", "disposition"),
                          },
                          {
                              id: EMOTORAD__EVENT_CONTAINER__L3_DISPOSITION__COLUMN_ID,
                              type: OttopilotColumnType.string,
                              displayName: "L3 Disposition",
                              valueGetter: (event: EventWithEventContainer) =>
                                  String(event.eventContainer.additionalColumnValues[EMOTORAD__EVENT_CONTAINER__L3_DISPOSITION__COLUMN_ID] ?? ""),
                              sortingCapability: true,
                              filteringCapability_in: {
                                  valuesQueryFn: async () => getEmotoradUniqueDispositionLevelValues("l3"),
                              },
                              customFilter: buildDispositionLevelCustomFilter("l3", "disposition"),
                          },
                          {
                              id: EMOTORAD__EVENT_CONTAINER__AON__COLUMN_ID as Uuid,
                              type: OttopilotColumnType.string,
                              displayName: "AON",
                              valueGetter: (event: EventWithEventContainer) =>
                                  String(event.eventContainer.additionalColumnValues[EMOTORAD__EVENT_CONTAINER__AON__COLUMN_ID as Uuid] ?? ""),
                              sortingCapability: true,
                              filteringCapability_contains: false,
                              filteringCapability_in: {
                                  valuesQueryFn: queryEventAonFilterValuesFromJourneyCreatedAt,
                              },
                              customFilter: buildEventAonCustomFilterForColumn(emotoradJourneyAonConfig, String(EMOTORAD__EVENT_CONTAINER__AON__COLUMN_ID)),
                          },
                          {
                              id: EMOTORAD__EVENT__ENQUIRY__AON__COLUMN_ID,
                              type: OttopilotColumnType.string,
                              displayName: "AON",
                              valueGetter: (event: EventWithEventContainer) => String(event.additionalColumnValues[EMOTORAD__EVENT__ENQUIRY__AON__COLUMN_ID] ?? ""),
                              sortingCapability: true,
                              filteringCapability_contains: false,
                              filteringCapability_in: {
                                  valuesQueryFn: queryEventAonFilterValuesFromJourneyCreatedAt,
                              },
                              customFilter: buildEventAonCustomFilterForColumn(emotoradEnquiryAonConfig, String(EMOTORAD__EVENT__ENQUIRY__AON__COLUMN_ID)),
                          },
                          {
                              id: EMOTORAD__EVENT__ENQUIRY__CREATED_BY_NAME__COLUMN_ID,
                              type: OttopilotColumnType.string,
                              displayName: "Created by",
                              valueGetter: (event: EventWithEventContainer) =>
                                  String(event.additionalColumnValues[EMOTORAD__EVENT__ENQUIRY__CREATED_BY_NAME__COLUMN_ID] ?? ""),
                              sortingCapability: true,
                              filteringCapability_contains: true,
                          },
                          {
                              id: EMOTORAD__EVENT__CALLS__TEST_RIDE_TAKEN__COLUMN_ID,
                              type: OttopilotColumnType.string,
                              displayName: "Test ride taken?",
                              valueGetter: (event: EventWithEventContainer) => {
                                  const raw = event.additionalColumnValues[EMOTORAD__EVENT__CALLS__TEST_RIDE_TAKEN__COLUMN_ID];
                                  if (raw === undefined || raw === null || String(raw).trim() === "") {
                                      return "";
                                  }
                                  return String(raw).trim();
                              },
                              valueFormatter: (value: string) => {
                                  if (value === "") {
                                      return "";
                                  }
                                  const yesNo = Object.values(emotoradYesNoOptions).find((o) => String(o.id) === value);
                                  return yesNo !== undefined ? yesNo.displayName : value;
                              },
                              sortingCapability: true,
                              filteringCapability_contains: true,
                          },
                          {
                              id: EMOTORAD__EVENT_CONTAINER__CALLS_COUNT__COLUMN_ID,
                              type: OttopilotColumnType.string,
                              displayName: "Calls count",
                              valueGetter: (event: EventWithEventContainer) =>
                                  String(event.eventContainer.additionalColumnValues[EMOTORAD__EVENT_CONTAINER__CALLS_COUNT__COLUMN_ID] ?? ""),
                              sortingCapability: true,
                              filteringCapability_contains: true,
                          },
                          {
                              id: EMOTORAD__EVENT__WALKINS__TEST_RIDE_TAKEN__COLUMN_ID,
                              type: OttopilotColumnType.string,
                              displayName: "Test ride taken?",
                              valueGetter: (event: EventWithEventContainer) => {
                                  const raw = event.additionalColumnValues[EMOTORAD__EVENT__WALKINS__TEST_RIDE_TAKEN__COLUMN_ID];
                                  if (raw === undefined || raw === null || String(raw).trim() === "") {
                                      return "";
                                  }
                                  return String(raw).trim();
                              },
                              valueFormatter: (value: string) => {
                                  if (value === "") {
                                      return "";
                                  }
                                  const yesNo = Object.values(emotoradYesNoOptions).find((o) => String(o.id) === value);
                                  return yesNo !== undefined ? yesNo.displayName : value;
                              },
                              sortingCapability: true,
                              filteringCapability_contains: true,
                          },
                          {
                              id: EMOTORAD__EVENT__SALES__CREATED_BY_NAME__COLUMN_ID,
                              type: OttopilotColumnType.string,
                              displayName: "Created by",
                              valueGetter: (event: EventWithEventContainer) =>
                                  String(event.additionalColumnValues[EMOTORAD__EVENT__SALES__CREATED_BY_NAME__COLUMN_ID] ?? ""),
                              sortingCapability: true,
                              filteringCapability_contains: true,
                          },
                          {
                              id: EMOTORAD__EVENT__SALES__STATUS__COLUMN_ID,
                              type: OttopilotColumnType.string,
                              displayName: "Sales Status",
                              valueGetter: (event: EventWithEventContainer) => {
                                  const raw = event.additionalColumnValues[EMOTORAD__EVENT__SALES__STATUS__COLUMN_ID];
                                  if (raw === undefined || raw === null) {
                                      return "";
                                  }
                                  return String(raw);
                              },
                              valueFormatter: (value: string) => {
                                  if (value === "") {
                                      return "?";
                                  }
                                  return formatEmotoradSalesStatusIdForDisplay(value);
                              },
                              sortingCapability: true,
                              filteringCapability_contains: true,
                          },
                          {
                              id: EMOTORAD__EVENT_CONTAINER__MODEL__COLUMN_ID,
                              type: OttopilotColumnType.string,
                              displayName: "Model",
                              valueGetter: (event: EventWithEventContainer) => {
                                  const raw =
                                      event.eventContainer.additionalColumnValues[EMOTORAD__EVENT_CONTAINER__MODEL__COLUMN_ID] ??
                                      event.additionalColumnValues[EMOTORAD__EVENT__SALES__MODEL__COLUMN_ID];
                                  if (raw === undefined || raw === null) {
                                      return "";
                                  }
                                  return String(raw);
                              },
                              valueFormatter: (value: string) => {
                                  if (value === "") {
                                      return "-";
                                  }
                                  const product = products?.find((p) => p.id === value);
                                  return product === undefined ? value : getProductCatalogueDisplayName(product, productCatalogueTemplates);
                              },
                              sortingCapability: true,
                              filteringCapability_contains: true,
                          },
                          {
                              id: EMOTORAD__EVENT_CONTAINER__MODEL_VARIANT__COLUMN_ID,
                              type: OttopilotColumnType.string,
                              displayName: "Model Variant",
                              valueGetter: (event: EventWithEventContainer) => {
                                  const raw =
                                      event.eventContainer.additionalColumnValues[EMOTORAD__EVENT_CONTAINER__MODEL_VARIANT__COLUMN_ID] ??
                                      event.additionalColumnValues[EMOTORAD__EVENT__SALES__MODEL_VARIANT__COLUMN_ID];
                                  if (raw === undefined || raw === null) {
                                      return "";
                                  }
                                  return String(raw);
                              },
                              valueFormatter: (value: string) => {
                                  if (value === "") {
                                      return "-";
                                  }
                                  const product = products?.find((p) => p.id === value);
                                  return product === undefined ? value : getProductCatalogueDisplayName(product, productCatalogueTemplates);
                              },
                              sortingCapability: true,
                              filteringCapability_contains: true,
                          },
                          {
                              id: EMOTORAD__EVENT__SALES__PREFERRED_COLOR__COLUMN_ID,
                              type: OttopilotColumnType.string,
                              displayName: "Preferred Colour",
                              valueGetter: (event: EventWithEventContainer) => {
                                  const raw = event.additionalColumnValues[EMOTORAD__EVENT__SALES__PREFERRED_COLOR__COLUMN_ID];
                                  if (raw === undefined || raw === null) {
                                      return "";
                                  }
                                  return String(raw);
                              },
                              valueFormatter: formatEmotoradPreferredColorProductIdForDisplay,
                              sortingCapability: true,
                              filteringCapability_contains: true,
                          },
                          {
                              id: EMOTORAD__EVENT_CONTAINER__SOURCE_CHANNEL__COLUMN_ID,
                              type: OttopilotColumnType.string,
                              displayName: "Source Channel",
                              valueGetter: (event: EventWithEventContainer) => {
                                  const raw = resolveEmotoradSourceChannelRawValue(event);
                                  if (raw === null || raw === undefined || String(raw).trim() === "") {
                                      return "";
                                  }
                                  return String(raw).trim();
                              },
                              valueFormatter: formatEmotoradSourceChannelValueForDisplay,
                              sortingCapability: true,
                              filteringCapability_contains: true,
                          },
                          {
                              id: EMOTORAD__EVENT__SALES__SOURCE_CHANNEL__COLUMN_ID,
                              type: OttopilotColumnType.string,
                              displayName: "Source Channel",
                              valueGetter: (event: EventWithEventContainer) => {
                                  const raw = resolveEmotoradSourceChannelRawValue(event);
                                  if (raw === null || raw === undefined || String(raw).trim() === "") {
                                      return "";
                                  }
                                  return String(raw).trim();
                              },
                              valueFormatter: formatEmotoradSourceChannelValueForDisplay,
                              sortingCapability: true,
                              filteringCapability_contains: true,
                          },
                          {
                              id: EMOTORAD__EVENT__ENQUIRY__SOURCE_CHANNEL__COLUMN_ID,
                              type: OttopilotColumnType.string,
                              displayName: "Source Channel",
                              valueGetter: (event: EventWithEventContainer) => {
                                  const raw = resolveEmotoradSourceChannelRawValue(event);
                                  if (raw === null || raw === undefined || String(raw).trim() === "") {
                                      return "";
                                  }
                                  return String(raw).trim();
                              },
                              valueFormatter: formatEmotoradSourceChannelValueForDisplay,
                              sortingCapability: true,
                              filteringCapability_contains: true,
                          },
                          ...[
                              {id: EMOTORAD__EVENT__ENQUIRY__LEAD_STATUS__COLUMN_ID, name: "Lead Status"},
                              {
                                  id: EMOTORAD__EVENT__ENQUIRY__DIGITAL_LEAD_STATUS__COLUMN_ID,
                                  name: "Digital Lead Status",
                              },
                              {id: EMOTORAD__EVENT_CONTAINER__ACTION_PLATFORM__COLUMN_ID, name: "Action Platform"},
                              {id: EMOTORAD__EVENT__SALES__ACTION_PLATFORM__COLUMN_ID, name: "Action Platform"},
                              {id: EMOTORAD__EVENT__ENQUIRY__CURRENT_PLATFORM__COLUMN_ID, name: "Current Platform"},
                              {
                                  id: EMOTORAD__EVENT__ENQUIRY__PRIMARY_RIDING_PURPOSE__COLUMN_ID,
                                  name: "Primary Riding Purpose",
                              },
                              {id: EMOTORAD__EVENT__ENQUIRY__EXPLORING_FOR__COLUMN_ID, name: "Exploring For"},
                              {
                                  id: EMOTORAD__EVENT__ENQUIRY__EXPECTED_RIDING_FREQUENCY__COLUMN_ID,
                                  name: "Expected Riding Frequency",
                              },
                              {id: EMOTORAD__EVENT__ENQUIRY__HEIGHT_OF_RIDER__COLUMN_ID, name: "Height of Rider"},
                              {id: EMOTORAD__EVENT__ENQUIRY__FIRST_CYCLE__COLUMN_ID, name: "Is this your first cycle"},
                              {
                                  id: EMOTORAD__EVENT__ENQUIRY__OTHER_BRANDS_CONSIDERED__COLUMN_ID,
                                  name: "Other Brands Considered",
                              },
                              {id: EMOTORAD__EVENT__ENQUIRY__GENDER__COLUMN_ID, name: "Gender"},
                              {id: EMOTORAD__USER__GENDER__COLUMN_ID, name: "Gender"},
                              {id: EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_NAME__COLUMN_ID, name: "Assigned Dealer"},
                          ].map(({id, name}) => ({
                              id,
                              type: OttopilotColumnType.string,
                              displayName: name,
                              valueGetter: (event: EventWithEventContainer) => {
                                  if (id === EMOTORAD__EVENT__SALES__ACTION_PLATFORM__COLUMN_ID) {
                                      const rawActionPlatform =
                                          event.additionalColumnValues[EMOTORAD__EVENT__SALES__ACTION_PLATFORM__COLUMN_ID] ??
                                          event.eventContainer.additionalColumnValues[EMOTORAD__EVENT_CONTAINER__ACTION_PLATFORM__COLUMN_ID] ??
                                          event.additionalColumnValues[EMOTORAD__EVENT__ENQUIRY__CURRENT_PLATFORM__COLUMN_ID];
                                      if (rawActionPlatform === undefined || rawActionPlatform === null || String(rawActionPlatform).trim() === "") {
                                          return "";
                                      }
                                      return String(rawActionPlatform).trim();
                                  }
                                  const value = event.additionalColumnValues[id] ?? event.eventContainer.additionalColumnValues[id];
                                  if (value === undefined || value === null || String(value).trim() === "") {
                                      return "";
                                  }
                                  return String(value).trim();
                              },
                              valueFormatter: (raw: string) => {
                                  if (raw === "") {
                                      return "";
                                  }
                                  const journeyColumn = getAdditionalColumns(journeysModuleConfiguration).find((c) => c.id === id);
                                  const eventColumn = eventTemplates.flatMap((t) => getAdditionalColumns(t)).find((c) => c.id === id);
                                  const column = journeyColumn ?? eventColumn;
                                  const opts = getAdditionalColumnOptions(column, userDefinedObjectOptionsByTemplateId) ?? [];
                                  if (opts.length > 0) {
                                      const option = opts.find((o) => o.id === raw);
                                      if (option !== undefined) {
                                          return option.displayName;
                                      }
                                  }
                                  if (id === EMOTORAD__EVENT__ENQUIRY__DIGITAL_LEAD_STATUS__COLUMN_ID) {
                                      const match = Object.values(emotoradEnquiryDigitalLeadStatuses).find((s) => String(s.id) === raw);
                                      return match !== undefined ? match.displayName : raw;
                                  }
                                  if (id === EMOTORAD__EVENT__ENQUIRY__LEAD_STATUS__COLUMN_ID) {
                                      const match = Object.values(emotoradLeadEventStatuses).find((s) => String(s.id) === raw);
                                      return match !== undefined ? match.displayName : raw;
                                  }
                                  if (
                                      id === EMOTORAD__EVENT_CONTAINER__ACTION_PLATFORM__COLUMN_ID ||
                                      id === EMOTORAD__EVENT__ENQUIRY__CURRENT_PLATFORM__COLUMN_ID ||
                                      id === EMOTORAD__EVENT__SALES__ACTION_PLATFORM__COLUMN_ID ||
                                      id === EMOTORAD__EVENT__CALLS__CURRENT_PLATFORM__COLUMN_ID
                                  ) {
                                      const match = Object.values(emotoradCurrentPlatforms).find((p) => String(p.id) === raw);
                                      return match !== undefined ? match.displayName : raw;
                                  }
                                  if (id === EMOTORAD__EVENT__ENQUIRY__GENDER__COLUMN_ID || id === EMOTORAD__USER__GENDER__COLUMN_ID) {
                                      const match = Object.values(emotoradGenders).find((g) => String(g.id) === raw);
                                      return match !== undefined ? match.displayName : raw;
                                  }
                                  if (id === EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_NAME__COLUMN_ID) {
                                      const ouDisplayName = getOuDisplayName(raw as Uuid);
                                      if (ouDisplayName !== "-" && ouDisplayName !== raw) {
                                          return ouDisplayName;
                                      }
                                      const userDisplayName = getUserDisplayName(raw as Uuid, users, userTemplates, platformUserDetailsByUserId);
                                      if (userDisplayName !== "?" && userDisplayName !== "-") {
                                          return userDisplayName;
                                      }
                                      return raw;
                                  }
                                  return raw;
                              },
                              sortingCapability: true,
                              filteringCapability_contains: true,
                          })),
                          {
                              id: EMOTORAD__EVENT_CONTAINER__LEAD_OWNER__COLUMN_ID,
                              type: OttopilotColumnType.string,
                              displayName: "Lifecycle Owner",
                              valueGetter: (event: EventWithEventContainer) => {
                                  const value =
                                      event.eventContainer.additionalColumnValues[EMOTORAD__EVENT_CONTAINER__LEAD_OWNER__COLUMN_ID] ??
                                      event.additionalColumnValues[EMOTORAD__EVENT_CONTAINER__LEAD_OWNER__COLUMN_ID];
                                  if (value === undefined || value === null || String(value).trim() === "") {
                                      return "";
                                  }
                                  return String(value).trim();
                              },
                              valueFormatter: (raw: string) => {
                                  if (raw === "") {
                                      return "";
                                  }
                                  const userDisplayName = getUserDisplayName(raw as Uuid, users, userTemplates, platformUserDetailsByUserId);
                                  return userDisplayName !== "?" && userDisplayName !== "-" ? userDisplayName : raw;
                              },
                              sortingCapability: true,
                              filteringCapability_contains: true,
                          },
                          {
                              id: EMOTORAD__EVENT__ENQUIRY__PREFERRED_COLOR__COLUMN_ID,
                              type: OttopilotColumnType.string,
                              displayName: "Preferred Colour",
                              valueGetter: (event: EventWithEventContainer) => {
                                  const value = event.additionalColumnValues[EMOTORAD__EVENT__ENQUIRY__PREFERRED_COLOR__COLUMN_ID];
                                  if (value === undefined || value === null || String(value).trim() === "") {
                                      return "";
                                  }
                                  return String(value).trim();
                              },
                              valueFormatter: formatEmotoradPreferredColorProductIdForDisplay,
                              sortingCapability: true,
                              filteringCapability_contains: true,
                          },
                      ]
                    : lineItemsWithEmotoradDispositionSynthetic;
                const lineItemsWithEmotoradCallLabels =
                    isEmotoradCallTemplate || isEmotoradTestrideTemplate || isEmotoradWalkinTemplate
                        ? (isEmotoradCallTemplate
                              ? [
                                    ...lineItemsWithEmotoradJourneyDispositionSynthetic,
                                    ...[
                                        {id: EMOTORAD__EVENT__CALLS__STATUS__COLUMN_ID, displayName: "Call Status"},
                                        {
                                            id: EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_NAME__COLUMN_ID,
                                            displayName: "Assigned Dealer",
                                        },
                                        {
                                            id: EMOTORAD__EVENT__CALLS__CURRENT_PLATFORM__COLUMN_ID,
                                            displayName: "Action Platform",
                                        },
                                        {
                                            id: EMOTORAD__EVENT__CALLS__PRIMARY_RIDING_PURPOSE__COLUMN_ID,
                                            displayName: "Primary Riding Purpose",
                                        },
                                        {
                                            id: EMOTORAD__EVENT__CALLS__EXPLORING_FOR__COLUMN_ID,
                                            displayName: "Exploring For",
                                        },
                                        {
                                            id: EMOTORAD__EVENT__CALLS__EXPECTED_RIDING_FREQUENCY__COLUMN_ID,
                                            displayName: "Expected Riding Frequency",
                                        },
                                        {
                                            id: EMOTORAD__EVENT__CALLS__HEIGHT_OF_RIDER__COLUMN_ID,
                                            displayName: "Height of Rider",
                                        },
                                    ].map<OttopilotColumn<EventWithEventContainer>>(({id, displayName}) => ({
                                        id,
                                        type: OttopilotColumnType.string,
                                        displayName,
                                        valueGetter: (event: EventWithEventContainer) => {
                                            if (id === EMOTORAD__EVENT__CALLS__CURRENT_PLATFORM__COLUMN_ID) {
                                                return getEmotoradCallEventCurrentPlatformRaw(event.additionalColumnValues);
                                            }
                                            const value = event.additionalColumnValues[id] ?? event.eventContainer.additionalColumnValues[id];
                                            return eventTableAdditionalColumnRawToString(value);
                                            //   if (value === undefined || value === null || value === "") return "";
                                            //   const stringValue = String(value);
                                            //   if (id === EMOTORAD__EVENT__CALLS__STATUS__COLUMN_ID) {
                                            //       const statusLabel = emotoradCallStatuses[stringValue as EmotoradCallStatusEnum]?.displayName;
                                            //       if (statusLabel !== undefined) return statusLabel;
                                            //   }
                                            //   if (id === EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_NAME__COLUMN_ID) {
                                            //       const ouDisplayName = getOuDisplayName(stringValue as Uuid);
                                            //       if (ouDisplayName !== "" && ouDisplayName !== stringValue) {
                                            //           return ouDisplayName;
                                            //       }
                                            //       const userDisplayName = getUserDisplayName(stringValue as Uuid, users, userTemplates, platformUserDetailsByUserId);
                                            //       if (userDisplayName !== "?" && userDisplayName !== "-") {
                                            //           return userDisplayName;
                                            //       }
                                            //   }
                                            //   const journeyColumn = journeysModuleConfiguration.additionalColumns.find((c) => c.id === id);
                                            //   const eventColumn = eventTemplates.flatMap((t) => t.additionalColumns).find((c) => c.id === id);
                                            //   const options =
                                            //       (journeyColumn?.configuration as {options?: Array<{id: Uuid; displayName: string}>} | undefined)?.options
                                            //       ?? (eventColumn?.configuration as {options?: Array<{id: Uuid; displayName: string}>} | undefined)?.options
                                            //       ?? [];
                                            //   const optionMatch = options.find((option) => String(option.id) === stringValue);
                                            //   if (optionMatch !== undefined) {
                                            //       return optionMatch.displayName;
                                            //   }
                                            //   return stringValue;
                                        },
                                        valueFormatter: (value) => {
                                            if (value === undefined || value === null || value === "") return "";
                                            const stringValue = String(value);
                                            if (id === EMOTORAD__EVENT__CALLS__STATUS__COLUMN_ID) {
                                                const statusLabel = emotoradCallStatuses[stringValue as EmotoradCallStatusEnum]?.displayName;
                                                if (statusLabel !== undefined) return statusLabel;
                                            }
                                            if (id === EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_NAME__COLUMN_ID) {
                                                const ouDisplayName = getOuDisplayName(stringValue as Uuid);
                                                if (ouDisplayName !== "" && ouDisplayName !== stringValue) {
                                                    return ouDisplayName;
                                                }
                                                const userDisplayName = getUserDisplayName(stringValue as Uuid, users, userTemplates, platformUserDetailsByUserId);
                                                if (userDisplayName !== "?" && userDisplayName !== "-") {
                                                    return userDisplayName;
                                                }
                                            }
                                            const journeyColumn = getAdditionalColumns(journeysModuleConfiguration).find((c) => c.id === id);
                                            const eventColumn = eventTemplates.flatMap((t) => getAdditionalColumns(t)).find((c) => c.id === id);
                                            const options = [
                                                ...(getAdditionalColumnOptions(journeyColumn, userDefinedObjectOptionsByTemplateId) ?? []),
                                                ...(getAdditionalColumnOptions(eventColumn, userDefinedObjectOptionsByTemplateId) ?? []),
                                            ];
                                            const optionMatch = options.find((option) => String(option.id) === stringValue);
                                            if (optionMatch !== undefined) {
                                                return optionMatch.displayName;
                                            }
                                            if (id === EMOTORAD__EVENT__CALLS__CURRENT_PLATFORM__COLUMN_ID) {
                                                return formatEmotoradCallEventCurrentPlatformForDisplay(stringValue);
                                            }
                                            return stringValue;
                                        },
                                        sortingCapability: true,
                                        filteringCapability_contains: true,
                                    })),
                                    ...[
                                        {
                                            id: EMOTORAD__EVENT__CALLS__SCHEDULED_BY__COLUMN_ID,
                                            displayName: "Scheduled By",
                                        },
                                        {id: EMOTORAD__EVENT__CALLS__LOGGED_BY__COLUMN_ID, displayName: "Logged By"},
                                        {
                                            id: EMOTORAD__EVENT__CALLS__CANCELLED_BY__COLUMN_ID,
                                            displayName: "Cancelled By",
                                        },
                                    ].map<OttopilotColumn<EventWithEventContainer>>(({id, displayName}) => ({
                                        id,
                                        type: OttopilotColumnType.string,
                                        displayName,
                                        valueGetter: (event: EventWithEventContainer) => {
                                            const rawValue = event.additionalColumnValues[id] ?? event.eventContainer.additionalColumnValues[id];
                                            return eventTableAdditionalColumnRawToString(rawValue);
                                        },
                                        valueFormatter: (rawValue) => {
                                            if (rawValue === undefined || rawValue === null || rawValue === "") return "";
                                            const userDisplayName = getUserDisplayName(rawValue as Uuid, users, userTemplates, platformUserDetailsByUserId);
                                            return userDisplayName === "?" ? String(rawValue) : userDisplayName;
                                        },
                                        sortingCapability: true,
                                        filteringCapability_contains: true,
                                    })),
                                ]
                              : lineItemsWithEmotoradJourneyDispositionSynthetic
                          )
                              .concat(
                                  isEmotoradWalkinTemplate
                                      ? [
                                            ...[
                                                {
                                                    id: EMOTORAD__EVENT__WALKINS__STATUS__COLUMN_ID,
                                                    displayName: "Walkin Status",
                                                },
                                                {
                                                    id: EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_NAME__COLUMN_ID,
                                                    displayName: "Assigned Dealer",
                                                },
                                                {
                                                    id: EMOTORAD__EVENT__WALKINS__EXPLORING_FOR__COLUMN_ID,
                                                    displayName: "Exploring For",
                                                },
                                                {
                                                    id: EMOTORAD__EVENT__WALKINS__PRIMARY_RIDING_PURPOSE__COLUMN_ID,
                                                    displayName: "Primary Riding Purpose",
                                                },
                                                {
                                                    id: EMOTORAD__EVENT__WALKINS__HEIGHT_OF_RIDER__COLUMN_ID,
                                                    displayName: "Height of Rider",
                                                },
                                                {
                                                    id: EMOTORAD__EVENT__WALKINS__EXPECTED_RIDING_FREQUENCY__COLUMN_ID,
                                                    displayName: "Expected Riding Frequency",
                                                },
                                            ].map<OttopilotColumn<EventWithEventContainer>>(({id, displayName}) => ({
                                                id,
                                                type: OttopilotColumnType.string,
                                                displayName,
                                                valueGetter: (event: EventWithEventContainer) => {
                                                    const value = event.additionalColumnValues[id] ?? event.eventContainer.additionalColumnValues[id];
                                                    return eventTableAdditionalColumnRawToString(value);
                                                },
                                                valueFormatter: (value) => {
                                                    if (value === undefined || value === null || value === "") return "";
                                                    const stringValue = String(value);
                                                    if (id === EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_NAME__COLUMN_ID) {
                                                        const ouDisplayName = getOuDisplayName(stringValue as Uuid);
                                                        if (ouDisplayName !== "" && ouDisplayName !== stringValue) {
                                                            return ouDisplayName;
                                                        }
                                                        const userDisplayName = getUserDisplayName(stringValue as Uuid, users, userTemplates, platformUserDetailsByUserId);
                                                        if (userDisplayName !== "?" && userDisplayName !== "-") {
                                                            return userDisplayName;
                                                        }
                                                    }
                                                    const journeyColumn = getAdditionalColumns(journeysModuleConfiguration).find((c) => c.id === id);
                                                    const eventColumn = eventTemplates.flatMap((t) => getAdditionalColumns(t)).find((c) => c.id === id);
                                                    const options = [
                                                        ...(getAdditionalColumnOptions(journeyColumn, userDefinedObjectOptionsByTemplateId) ?? []),
                                                        ...(getAdditionalColumnOptions(eventColumn, userDefinedObjectOptionsByTemplateId) ?? []),
                                                    ];
                                                    const optionMatch = options.find((option) => String(option.id) === stringValue);
                                                    if (optionMatch !== undefined) {
                                                        return optionMatch.displayName;
                                                    }
                                                    return stringValue;
                                                },
                                                sortingCapability: true,
                                                filteringCapability_contains: true,
                                            })),
                                            ...[
                                                {
                                                    id: EMOTORAD__EVENT__WALKINS__SCHEDULED_BY__COLUMN_ID,
                                                    displayName: "Scheduled By",
                                                },
                                                {
                                                    id: EMOTORAD__EVENT__WALKINS__LOGGED_BY__COLUMN_ID,
                                                    displayName: "Logged By",
                                                },
                                                {
                                                    id: EMOTORAD__EVENT__WALKINS__CANCELLED_BY__COLUMN_ID,
                                                    displayName: "Cancelled By",
                                                },
                                            ].map<OttopilotColumn<EventWithEventContainer>>(({id, displayName}) => ({
                                                id,
                                                type: OttopilotColumnType.string,
                                                displayName,
                                                valueGetter: (event: EventWithEventContainer) => {
                                                    const rawValue = event.additionalColumnValues[id] ?? event.eventContainer.additionalColumnValues[id];
                                                    return eventTableAdditionalColumnRawToString(rawValue);
                                                },
                                                valueFormatter: (rawValue) => {
                                                    if (rawValue === undefined || rawValue === null || rawValue === "") return "";
                                                    const userDisplayName = getUserDisplayName(rawValue as Uuid, users, userTemplates, platformUserDetailsByUserId);
                                                    return userDisplayName === "?" ? String(rawValue) : userDisplayName;
                                                },
                                                sortingCapability: true,
                                                filteringCapability_contains: true,
                                            })),
                                            {
                                                id: EMOTORAD__EVENT__WALKINS__LOGGED_ON__COLUMN_ID,
                                                type: OttopilotColumnType.string,
                                                displayName: "Logged On",
                                                valueGetter: (event: EventWithEventContainer) => {
                                                    const value =
                                                        event.additionalColumnValues[EMOTORAD__EVENT__WALKINS__LOGGED_ON__COLUMN_ID] ??
                                                        event.eventContainer.additionalColumnValues[EMOTORAD__EVENT__WALKINS__LOGGED_ON__COLUMN_ID];
                                                    return value;
                                                },
                                                valueFormatter: (value) => {
                                                    if (value === undefined || value === null || value === "") return "";
                                                    return formatDateTime(String(value));
                                                },
                                                sortingCapability: true,
                                                filteringCapability_contains: true,
                                            } as OttopilotColumn<EventWithEventContainer>,
                                        ]
                                      : [],
                              )
                              .concat(
                                  isEmotoradTestrideTemplate
                                      ? [
                                            ...[
                                                {
                                                    id: EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_NAME__COLUMN_ID,
                                                    displayName: "Assigned Dealer",
                                                },
                                                {
                                                    id: EMOTORAD__EVENT__TESTRIDES__STATUS__COLUMN_ID,
                                                    displayName: "Testride Status",
                                                },
                                                {
                                                    id: EMOTORAD__EVENT__TESTRIDES__EXPLORING_FOR__COLUMN_ID,
                                                    displayName: "Exploring For",
                                                },
                                                {
                                                    id: EMOTORAD__EVENT__TESTRIDES__PRIMARY_RIDING_PURPOSE__COLUMN_ID,
                                                    displayName: "Primary Riding Purpose",
                                                },
                                                {
                                                    id: EMOTORAD__EVENT__TESTRIDES__HEIGHT_OF_RIDER__COLUMN_ID,
                                                    displayName: "Height of Rider",
                                                },
                                                {
                                                    id: EMOTORAD__EVENT__TESTRIDES__EXPECTED_RIDING_FREQUENCY__COLUMN_ID,
                                                    displayName: "Expected Riding Frequency",
                                                },
                                            ].map<OttopilotColumn<EventWithEventContainer>>(({id, displayName}) => ({
                                                id,
                                                type: OttopilotColumnType.string,
                                                displayName,
                                                valueGetter: (event: EventWithEventContainer) => {
                                                    const value = event.additionalColumnValues[id] ?? event.eventContainer.additionalColumnValues[id];
                                                    return eventTableAdditionalColumnRawToString(value);
                                                },
                                                valueFormatter: (value) => {
                                                    if (value === undefined || value === null || value === "") return "";
                                                    const stringValue = String(value);
                                                    if (id === EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_NAME__COLUMN_ID) {
                                                        const ouDisplayName = getOuDisplayName(stringValue as Uuid);
                                                        if (ouDisplayName !== "" && ouDisplayName !== stringValue) {
                                                            return ouDisplayName;
                                                        }
                                                        const userDisplayName = getUserDisplayName(stringValue as Uuid, users, userTemplates, platformUserDetailsByUserId);
                                                        if (userDisplayName !== "?" && userDisplayName !== "-") {
                                                            return userDisplayName;
                                                        }
                                                    }
                                                    const journeyColumn = getAdditionalColumns(journeysModuleConfiguration).find((c) => c.id === id);
                                                    const eventColumn = eventTemplates.flatMap((t) => getAdditionalColumns(t)).find((c) => c.id === id);
                                                    const options = [
                                                        ...(getAdditionalColumnOptions(journeyColumn, userDefinedObjectOptionsByTemplateId) ?? []),
                                                        ...(getAdditionalColumnOptions(eventColumn, userDefinedObjectOptionsByTemplateId) ?? []),
                                                    ];
                                                    const optionMatch = options.find((option) => String(option.id) === stringValue);
                                                    if (optionMatch !== undefined) {
                                                        return optionMatch.displayName;
                                                    }
                                                    return stringValue;
                                                },
                                                sortingCapability: true,
                                                filteringCapability_contains: true,
                                            })),
                                            ...[
                                                {
                                                    id: EMOTORAD__EVENT__TESTRIDES__SCHEDULED_BY__COLUMN_ID,
                                                    displayName: "Scheduled By",
                                                },
                                                {
                                                    id: EMOTORAD__EVENT__TESTRIDES__LOGGED_BY__COLUMN_ID,
                                                    displayName: "Logged By",
                                                },
                                                {
                                                    id: EMOTORAD__EVENT__TESTRIDES__CANCELLED_BY__COLUMN_ID,
                                                    displayName: "Cancelled By",
                                                },
                                            ].map<OttopilotColumn<EventWithEventContainer>>(({id, displayName}) => ({
                                                id,
                                                type: OttopilotColumnType.string,
                                                displayName,
                                                valueGetter: (event: EventWithEventContainer) => {
                                                    const rawValue = event.additionalColumnValues[id] ?? event.eventContainer.additionalColumnValues[id];
                                                    return eventTableAdditionalColumnRawToString(rawValue);
                                                },
                                                valueFormatter: (rawValue) => {
                                                    if (rawValue === undefined || rawValue === null || rawValue === "") return "";
                                                    const userDisplayName = getUserDisplayName(rawValue as Uuid, users, userTemplates, platformUserDetailsByUserId);
                                                    return userDisplayName === "?" ? String(rawValue) : userDisplayName;
                                                },
                                                sortingCapability: true,
                                                filteringCapability_contains: true,
                                            })),
                                            {
                                                id: EMOTORAD__EVENT__TESTRIDES__TEST_RIDE_DATE__COLUMN_ID,
                                                type: OttopilotColumnType.string,
                                                displayName:
                                                    getAdditionalColumns(eventTemplate).find((c) => c.id === EMOTORAD__EVENT__TESTRIDES__TEST_RIDE_DATE__COLUMN_ID)
                                                        ?.displayName ?? "Test ride date",
                                                valueGetter: (event: EventWithEventContainer) => {
                                                    const value =
                                                        event.additionalColumnValues[EMOTORAD__EVENT__TESTRIDES__TEST_RIDE_DATE__COLUMN_ID] ??
                                                        event.eventContainer.additionalColumnValues[EMOTORAD__EVENT__TESTRIDES__TEST_RIDE_DATE__COLUMN_ID];
                                                    if (value === undefined || value === null || value === "") {
                                                        return "";
                                                    }
                                                    return String(value).trim();
                                                },
                                                valueFormatter: (value: string) => {
                                                    if (value === "") return "";
                                                    const formatted = formatEmotoradTestRideDateOnlyForDisplay(value);
                                                    return formatted !== "-" ? formatted : value;
                                                },
                                                sortingCapability: true,
                                                filteringCapability_contains: true,
                                            } as OttopilotColumn<EventWithEventContainer>,
                                            {
                                                id: EMOTORAD__EVENT__TESTRIDES__LAST_TEST_RIDE_DATE__COLUMN_ID,
                                                type: OttopilotColumnType.string,
                                                displayName:
                                                    getAdditionalColumns(eventTemplate).find((c) => c.id === EMOTORAD__EVENT__TESTRIDES__LAST_TEST_RIDE_DATE__COLUMN_ID)
                                                        ?.displayName ?? "Last test ride date",
                                                valueGetter: (event: EventWithEventContainer) => {
                                                    const value =
                                                        event.additionalColumnValues[EMOTORAD__EVENT__TESTRIDES__LAST_TEST_RIDE_DATE__COLUMN_ID] ??
                                                        event.eventContainer.additionalColumnValues[EMOTORAD__EVENT__TESTRIDES__LAST_TEST_RIDE_DATE__COLUMN_ID];
                                                    if (value === undefined || value === null || value === "") {
                                                        return "";
                                                    }
                                                    return String(value).trim();
                                                },
                                                valueFormatter: (value: string) => {
                                                    if (value === "") {
                                                        return "";
                                                    }
                                                    const formatted = formatDateTime(value as never);
                                                    return formatted !== "-" ? formatted : value;
                                                },
                                                sortingCapability: true,
                                                filteringCapability_contains: true,
                                            } as OttopilotColumn<EventWithEventContainer>,
                                            {
                                                id: EMOTORAD__EVENT__TESTRIDES__SCHEDULED_ON__COLUMN_ID,
                                                type: OttopilotColumnType.string,
                                                displayName:
                                                    getAdditionalColumns(eventTemplate).find((c) => c.id === EMOTORAD__EVENT__TESTRIDES__SCHEDULED_ON__COLUMN_ID)
                                                        ?.displayName ?? "Scheduled on",
                                                valueGetter: (event: EventWithEventContainer) => {
                                                    const value =
                                                        event.additionalColumnValues[EMOTORAD__EVENT__TESTRIDES__SCHEDULED_ON__COLUMN_ID] ??
                                                        event.eventContainer.additionalColumnValues[EMOTORAD__EVENT__TESTRIDES__SCHEDULED_ON__COLUMN_ID];
                                                    if (value === undefined || value === null || value === "") {
                                                        return "";
                                                    }
                                                    return String(value).trim();
                                                },
                                                valueFormatter: (value: string) => {
                                                    if (value === "") {
                                                        return "";
                                                    }
                                                    const formatted = formatDateTime(value as never);
                                                    return formatted !== "-" ? formatted : value;
                                                },
                                                sortingCapability: true,
                                                filteringCapability_contains: true,
                                            } as OttopilotColumn<EventWithEventContainer>,
                                            {
                                                id: EMOTORAD__EVENT__TESTRIDES__LOGGED_ON__COLUMN_ID,
                                                type: OttopilotColumnType.string,
                                                displayName:
                                                    getAdditionalColumns(eventTemplate).find((c) => c.id === EMOTORAD__EVENT__TESTRIDES__LOGGED_ON__COLUMN_ID)
                                                        ?.displayName ?? "Logged on",
                                                valueGetter: (event: EventWithEventContainer) => {
                                                    const value =
                                                        event.additionalColumnValues[EMOTORAD__EVENT__TESTRIDES__LOGGED_ON__COLUMN_ID] ??
                                                        event.eventContainer.additionalColumnValues[EMOTORAD__EVENT__TESTRIDES__LOGGED_ON__COLUMN_ID];
                                                    if (value === undefined || value === null || value === "") {
                                                        return "";
                                                    }
                                                    return String(value).trim();
                                                },
                                                valueFormatter: (value: string) => {
                                                    if (value === "") {
                                                        return "";
                                                    }
                                                    const formatted = formatDateTime(value as never);
                                                    return formatted !== "-" ? formatted : value;
                                                },
                                                sortingCapability: true,
                                                filteringCapability_contains: true,
                                            } as OttopilotColumn<EventWithEventContainer>,
                                            {
                                                id: EMOTORAD__EVENT__TESTRIDES__CANCELLED_ON__COLUMN_ID,
                                                type: OttopilotColumnType.string,
                                                displayName:
                                                    getAdditionalColumns(eventTemplate).find((c) => c.id === EMOTORAD__EVENT__TESTRIDES__CANCELLED_ON__COLUMN_ID)
                                                        ?.displayName ?? "Cancelled on",
                                                valueGetter: (event: EventWithEventContainer) => {
                                                    const value =
                                                        event.additionalColumnValues[EMOTORAD__EVENT__TESTRIDES__CANCELLED_ON__COLUMN_ID] ??
                                                        event.eventContainer.additionalColumnValues[EMOTORAD__EVENT__TESTRIDES__CANCELLED_ON__COLUMN_ID];
                                                    if (value === undefined || value === null || value === "") {
                                                        return "";
                                                    }
                                                    return String(value).trim();
                                                },
                                                valueFormatter: (value: string) => {
                                                    if (value === "") {
                                                        return "";
                                                    }
                                                    const formatted = formatDateTime(value as never);
                                                    return formatted !== "-" ? formatted : value;
                                                },
                                                sortingCapability: true,
                                                filteringCapability_contains: true,
                                            } as OttopilotColumn<EventWithEventContainer>,
                                        ]
                                      : [],
                              )
                              .map((column) => {
                                  const normalizedDisplayName = column.displayName.trim().toLowerCase();
                                  if (normalizedDisplayName === "zsm name" || normalizedDisplayName === "zsm") {
                                      return {
                                          ...column,
                                          displayName: "RM Name",
                                      };
                                  }
                                  return column;
                              })
                        : lineItemsWithEmotoradJourneyDispositionSynthetic.map((column) =>
                              isEmotoradSalesTemplate === true && column.id === EMOTORAD__EVENT__SALES__SHOWROOM_ID__COLUMN_ID
                                  ? {...column, displayName: "Franchise ID"}
                                  : column,
                          );

                const lineItemsWithBobcardApplicationsInitiatedLabels = isBobcardApplicationsInitiatedTemplate
                    ? [
                          ...lineItemsWithEmotoradCallLabels,
                          {
                              id: BOBCARD__EVENT_CONTAINER__APPLICATION_STAGE__COLUMN_ID,
                              type: OttopilotColumnType.string,
                              displayName: "Application Stage",
                              valueGetter: (event: EventWithEventContainer) => {
                                  const value =
                                      event.eventContainer.additionalColumnValues[BOBCARD__EVENT_CONTAINER__APPLICATION_STAGE__COLUMN_ID] ??
                                      event.additionalColumnValues[BOBCARD__EVENT_CONTAINER__APPLICATION_STAGE__COLUMN_ID];
                                  return value ? String(value) : "";
                              },
                              valueFormatter: (value) => {
                                  const stringValue = String(value ?? "");
                                  const mapped = Object.values(bobcardApplicationStages).find((stage) => String(stage.id) === stringValue);
                                  return mapped?.displayName ?? stringValue;
                              },
                              sortingCapability: true,
                              filteringCapability_in: {
                                  valuesQueryFn: async () => Object.values(bobcardApplicationStages).map((s) => s.id),
                              },
                          } as OttopilotColumn<EventWithEventContainer>,
                          {
                              id: BOBCARD__EVENT_CONTAINER__REJECTION_STAGE__COLUMN_ID,
                              type: OttopilotColumnType.string,
                              displayName: "Rejection Stage",
                              valueGetter: (event: EventWithEventContainer) => {
                                  const value =
                                      event.eventContainer.additionalColumnValues[BOBCARD__EVENT_CONTAINER__REJECTION_STAGE__COLUMN_ID] ??
                                      event.additionalColumnValues[BOBCARD__EVENT_CONTAINER__REJECTION_STAGE__COLUMN_ID];
                                  return value ? String(value) : "";
                              },
                              valueFormatter: (value) => {
                                  const stringValue = String(value ?? "");
                                  const mapped = Object.values(bobcardRejectionStages).find((stage) => String(stage.id) === stringValue);
                                  return mapped?.displayName ?? stringValue;
                              },
                              sortingCapability: true,
                              filteringCapability_in: {
                                  valuesQueryFn: async () => Object.values(bobcardRejectionStages).map((s) => s.id),
                              },
                          } as OttopilotColumn<EventWithEventContainer>,
                          {
                              id: BOBCARD__EVENT__ENQUIRY__DIGITAL_LEAD_STATUS__COLUMN_ID,
                              type: OttopilotColumnType.string,
                              displayName: "Digital Lead Status",
                              valueGetter: (event: EventWithEventContainer) => {
                                  const value =
                                      event.additionalColumnValues[BOBCARD__EVENT__ENQUIRY__DIGITAL_LEAD_STATUS__COLUMN_ID] ??
                                      event.eventContainer.additionalColumnValues[BOBCARD__EVENT__ENQUIRY__DIGITAL_LEAD_STATUS__COLUMN_ID];
                                  return value ? String(value) : "";
                              },
                              valueFormatter: (value) => {
                                  const stringValue = String(value ?? "");
                                  const mapped = Object.values(bobcardEnquiryDigitalLeadStatuses).find((status) => String(status.id) === stringValue);
                                  return mapped?.displayName ?? stringValue;
                              },
                              sortingCapability: true,
                              filteringCapability_in: {
                                  valuesQueryFn: async () => Object.values(bobcardEnquiryDigitalLeadStatuses).map((s) => s.id),
                              },
                          } as OttopilotColumn<EventWithEventContainer>,
                      ]
                    : lineItemsWithEmotoradCallLabels;
                const explicitById = new Map<string, OttopilotColumn<EventWithEventContainer>>();
                for (const column of lineItemsWithEmotoradDispositionSynthetic) {
                    explicitById.set(column.id, column as OttopilotColumn<EventWithEventContainer>);
                }
                for (const column of lineItemsWithBobcardApplicationsInitiatedLabels) {
                    explicitById.set(column.id, column as OttopilotColumn<EventWithEventContainer>);
                }
                if (
                    isEmotoradLeadsTemplate === true ||
                    isEmotoradCallTemplate === true ||
                    isEmotoradSalesTemplate === true ||
                    isEmotoradWalkinTemplate === true ||
                    isEmotoradTestrideTemplate === true
                ) {
                    const resolveDealerId = (event: EventWithEventContainer): Uuid | null => {
                        const raw = event.eventContainer.additionalColumnValues[EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_NAME__COLUMN_ID];
                        if (typeof raw !== "string" || raw.trim() === "") return null;
                        return raw as Uuid;
                    };
                    const resolveModelVariantRawProductId = (event: EventWithEventContainer): string => {
                        const fromCallEvent = event.additionalColumnValues[EMOTORAD__EVENT__CALLS__MODEL_VARIANT__COLUMN_ID];
                        const fromJourney = event.eventContainer.additionalColumnValues[EMOTORAD__EVENT_CONTAINER__MODEL_VARIANT__COLUMN_ID];
                        const rawValue = fromCallEvent !== null && fromCallEvent !== undefined && String(fromCallEvent).trim() !== "" ? fromCallEvent : fromJourney;
                        if (rawValue === null || rawValue === undefined || String(rawValue).trim() === "") return "";
                        return String(rawValue).trim();
                    };
                    if (isEmotoradTestrideTemplate === true) {
                        const testRideTimeColumn = getAdditionalColumns(eventTemplate).find((c) => c.id === EMOTORAD__EVENT__TESTRIDES__TEST_RIDE_TIME__COLUMN_ID);
                        explicitById.set(EMOTORAD__EVENT__TESTRIDES__TEST_RIDE_TIME__COLUMN_ID, {
                            id: EMOTORAD__EVENT__TESTRIDES__TEST_RIDE_TIME__COLUMN_ID,
                            type: OttopilotColumnType.string,
                            displayName: testRideTimeColumn?.displayName ?? "Test ride time",
                            valueGetter: (event: EventWithEventContainer) => getEmotoradTestRideEffectiveSlotHhMmFromEventRow(event),
                            valueFormatter: (slot: unknown) => {
                                const s = slot === null || slot === undefined ? "" : String(slot).trim();
                                if (s === "") return "";
                                return formatEmotoradTestRideShortRangeLabelFromSlotHhMm(s);
                            },
                            sortingCapability: true,
                            filteringCapability_contains: true,
                        } satisfies OttopilotColumn<EventWithEventContainer>);
                    }
                    if (isEmotoradCallTemplate === true) {
                        const callCurrentPlatformColumn = getAdditionalColumns(eventTemplate).find((c) => c.id === EMOTORAD__EVENT__CALLS__CURRENT_PLATFORM__COLUMN_ID);
                        explicitById.set(EMOTORAD__EVENT__CALLS__CURRENT_PLATFORM__COLUMN_ID, {
                            id: EMOTORAD__EVENT__CALLS__CURRENT_PLATFORM__COLUMN_ID,
                            type: OttopilotColumnType.string,
                            displayName: callCurrentPlatformColumn?.displayName ?? "Action Platform",
                            valueGetter: (event: EventWithEventContainer) => getEmotoradCallEventCurrentPlatformRaw(event.additionalColumnValues),
                            valueFormatter: (raw: string) => formatEmotoradCallEventCurrentPlatformForDisplay(raw),
                            sortingCapability: true,
                            filteringCapability_contains: true,
                        } satisfies OttopilotColumn<EventWithEventContainer>);
                        explicitById.set(EMOTORAD__EVENT_CONTAINER__MODEL_VARIANT__COLUMN_ID, {
                            id: EMOTORAD__EVENT_CONTAINER__MODEL_VARIANT__COLUMN_ID,
                            type: OttopilotColumnType.string,
                            displayName: "Model Variant",
                            valueGetter: (event: EventWithEventContainer) => resolveModelVariantRawProductId(event),
                            valueFormatter: (productId: string) => {
                                if (productId === "") return "";
                                const product = products.find((item) => item.id === productId);
                                return product === undefined ? productId : getProductCatalogueDisplayName(product, productCatalogueTemplates);
                            },
                            sortingCapability: true,
                            filteringCapability_contains: true,
                        } satisfies OttopilotColumn<EventWithEventContainer>);
                    }
                    explicitById.set(EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_NAME__COLUMN_ID, {
                        id: EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_NAME__COLUMN_ID,
                        type: OttopilotColumnType.string,
                        displayName:
                            getAdditionalColumns(journeysModuleConfiguration).find((c) => c.id === EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_NAME__COLUMN_ID)
                                ?.displayName ?? "Assigned Dealer",
                        valueGetter: (event: EventWithEventContainer): string =>
                            String(event.eventContainer.additionalColumnValues[EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_NAME__COLUMN_ID] ?? ""),
                        valueFormatter: (value: string) => {
                            if (value === "") {
                                return "-";
                            }
                            const ou = organizationalUnits.find((unit) => unit.id === value);
                            if (ou !== undefined) {
                                const ouTemplate = organizationalUnitTemplates.find((template) => template.id === ou.templateId);
                                if (ouTemplate !== undefined) {
                                    return processOttopilotStringTemplate(ouTemplate.instanceDisplayNameTemplate, ou.additionalColumnValues);
                                }
                            }
                            return value;
                        },
                        sortingCapability: true,
                        filteringCapability_contains: true,
                    } satisfies OttopilotColumn<EventWithEventContainer>);
                    explicitById.set(EMOTORAD__EVENT_CONTAINER__DEALER_PINCODE__COLUMN_ID, {
                        id: EMOTORAD__EVENT_CONTAINER__DEALER_PINCODE__COLUMN_ID,
                        type: OttopilotColumnType.string,
                        displayName: "Assigned Dealer Pincode",
                        valueGetter: (event: EventWithEventContainer) => getDealerFieldByLabel(resolveDealerId(event), ["Pincode", "Pin Code", "Dealer Pincode"]),
                        sortingCapability: true,
                        filteringCapability_contains: true,
                    } satisfies OttopilotColumn<EventWithEventContainer>);
                    const buildDealerFieldValuesQueryFn = (extract: (event: EventWithEventContainer) => string) => async (): Promise<Array<string>> => {
                        const result = await getEventsWithEventContainers({
                            accessToken: heimdallUser.accessToken,
                            intellsysBaseUrl: intellsysBaseUrl,
                            filterConfiguration: {
                                revisionId: generateUuid(),
                                lineItems: [
                                    {
                                        columnId: "templateId",
                                        operation: FilterOperation.equals,
                                        value: eventTemplateId,
                                    },
                                ],
                            },
                            sortConfiguration: {revisionId: generateUuid(), lineItems: []},
                            searchConfiguration: {revisionId: generateUuid(), searchValue: "", searchColumns: []},
                            limit: 5000,
                            offset: 0,
                            workspaceId: workspace.id,
                            eventTemplateId: eventTemplateId,
                            eventTemplateAdditionalColumnIds: getAdditionalColumns(eventTemplate).map((col) => col.id),
                            eventContainerAdditionalColumnIds: getAdditionalColumns(journeysModuleConfiguration).map((col) => col.id),
                        });
                        if (result.success !== true) {
                            return [];
                        }
                        const values = new Set<string>();
                        for (const event of result.data) {
                            const value = extract(event);
                            if (value !== "") {
                                values.add(value);
                            }
                        }
                        return Array.from(values).sort();
                    };
                    explicitById.set(EMOTORAD__EVENT_CONTAINER__DEALER_CITY__COLUMN_ID, {
                        id: EMOTORAD__EVENT_CONTAINER__DEALER_CITY__COLUMN_ID,
                        type: OttopilotColumnType.string,
                        displayName: "Assigned Dealer City",
                        valueGetter: (event: EventWithEventContainer) => {
                            const raw = event.eventContainer.additionalColumnValues[EMOTORAD__EVENT_CONTAINER__DEALER_CITY__COLUMN_ID];
                            if (raw !== null && raw !== undefined && String(raw).trim() !== "") {
                                return String(raw);
                            }
                            return getDealerFieldByLabel(resolveDealerId(event), ["City", "Dealer City"]);
                        },
                        sortingCapability: true,
                        filteringCapability_contains: true,
                        filteringCapability_in: {
                            valuesQueryFn: buildDealerFieldValuesQueryFn((event) => getDealerFieldByLabel(resolveDealerId(event), ["City", "Dealer City"])),
                        },
                    } satisfies OttopilotColumn<EventWithEventContainer>);
                    explicitById.set(EMOTORAD__EVENT_CONTAINER__DEALER_STATE__COLUMN_ID, {
                        id: EMOTORAD__EVENT_CONTAINER__DEALER_STATE__COLUMN_ID,
                        type: OttopilotColumnType.string,
                        displayName: "Assigned Dealer State",
                        valueGetter: (event: EventWithEventContainer) => {
                            const raw = event.eventContainer.additionalColumnValues[EMOTORAD__EVENT_CONTAINER__DEALER_STATE__COLUMN_ID];
                            if (raw !== null && raw !== undefined && String(raw).trim() !== "") {
                                return String(raw);
                            }
                            return getDealerFieldByLabel(resolveDealerId(event), ["State", "Dealer State"]);
                        },
                        sortingCapability: true,
                        filteringCapability_contains: true,
                        filteringCapability_in: {
                            valuesQueryFn: buildDealerFieldValuesQueryFn((event) => getDealerFieldByLabel(resolveDealerId(event), ["State", "Dealer State"])),
                        },
                    } satisfies OttopilotColumn<EventWithEventContainer>);
                    explicitById.set(EMOTORAD__EVENT_CONTAINER__DEALER_ZONE__COLUMN_ID, {
                        id: EMOTORAD__EVENT_CONTAINER__DEALER_ZONE__COLUMN_ID,
                        type: OttopilotColumnType.string,
                        displayName: "Assigned Dealer Zone",
                        valueGetter: (event: EventWithEventContainer) => {
                            const raw = event.eventContainer.additionalColumnValues[EMOTORAD__EVENT_CONTAINER__DEALER_ZONE__COLUMN_ID];
                            if (raw !== null && raw !== undefined && String(raw).trim() !== "") {
                                return String(raw);
                            }
                            return resolveAssignedDealerZoneFromOuAncestorTree(resolveDealerId(event));
                        },
                        sortingCapability: true,
                        filteringCapability_contains: true,
                        filteringCapability_in: {
                            valuesQueryFn: buildDealerFieldValuesQueryFn((event) => resolveAssignedDealerZoneFromOuAncestorTree(resolveDealerId(event))),
                        },
                    } satisfies OttopilotColumn<EventWithEventContainer>);
                    explicitById.set(EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_CONTACT__COLUMN_ID, {
                        id: EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_CONTACT__COLUMN_ID,
                        type: OttopilotColumnType.string,
                        displayName: "Assigned Dealer Contact",
                        valueGetter: resolveEmotoradDealerContact,
                        sortingCapability: true,
                        filteringCapability_in: {
                            valuesQueryFn: () => getEmotoradComputedColumnFilterValues(resolveEmotoradDealerContact),
                        },
                    } satisfies OttopilotColumn<EventWithEventContainer>);
                    explicitById.set(EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_EMAIL__COLUMN_ID, {
                        id: EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_EMAIL__COLUMN_ID,
                        type: OttopilotColumnType.string,
                        displayName: "Assigned Dealer Email",
                        valueGetter: resolveEmotoradDealerEmail,
                        sortingCapability: true,
                        filteringCapability_in: {
                            valuesQueryFn: () => getEmotoradComputedColumnFilterValues(resolveEmotoradDealerEmail),
                        },
                    } satisfies OttopilotColumn<EventWithEventContainer>);
                    const journeyKamColumnConfig = getAdditionalColumns(journeysModuleConfiguration).find((c) => c.id === EMOTORAD__EVENT_CONTAINER__KAM_NAME__COLUMN_ID);
                    const journeyZsmColumnConfig = getAdditionalColumns(journeysModuleConfiguration).find((c) => c.id === EMOTORAD__EVENT_CONTAINER__ZSM_NAME__COLUMN_ID);
                    const resolveStoredUserUuidToDisplayName = (rawValue: unknown): string => {
                        if (rawValue === null || rawValue === undefined) {
                            return "";
                        }
                        const trimmed = String(rawValue).trim();
                        if (trimmed === "") {
                            return "";
                        }
                        const looksLikeUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(trimmed);
                        if (looksLikeUuid !== true) {
                            return trimmed;
                        }
                        const displayName = getUserDisplayName(trimmed as Uuid, users, userTemplates, platformUserDetailsByUserId);
                        if (displayName !== "?" && displayName !== "-") {
                            return displayName;
                        }
                        return trimmed;
                    };
                    explicitById.set(EMOTORAD__EVENT_CONTAINER__KAM_NAME__COLUMN_ID, {
                        id: EMOTORAD__EVENT_CONTAINER__KAM_NAME__COLUMN_ID,
                        type: OttopilotColumnType.string,
                        displayName: journeyKamColumnConfig?.displayName ?? "KAM Name",
                        valueGetter: (event: EventWithEventContainer) => {
                            const rawKam = event.eventContainer.additionalColumnValues[EMOTORAD__EVENT_CONTAINER__KAM_NAME__COLUMN_ID];
                            const resolved = resolveStoredUserUuidToDisplayName(String(rawKam ?? ""));
                            if (resolved !== "") {
                                return resolved;
                            }
                            return resolveDealerKamRmValue(event, "kam");
                        },
                        sortingCapability: true,
                        filteringCapability_contains: true,
                    } satisfies OttopilotColumn<EventWithEventContainer>);
                    explicitById.set(EMOTORAD__EVENT_CONTAINER__ZSM_NAME__COLUMN_ID, {
                        id: EMOTORAD__EVENT_CONTAINER__ZSM_NAME__COLUMN_ID,
                        type: OttopilotColumnType.string,
                        displayName:
                            (isEmotoradCallTemplate === true || isEmotoradTestrideTemplate === true) &&
                            (journeyZsmColumnConfig?.displayName ?? "").trim().toLowerCase() === "zsm name"
                                ? "RM Name"
                                : (journeyZsmColumnConfig?.displayName ?? "RM Name"),
                        valueGetter: (event: EventWithEventContainer) => {
                            const rawRm = event.eventContainer.additionalColumnValues[EMOTORAD__EVENT_CONTAINER__ZSM_NAME__COLUMN_ID];
                            const resolved = resolveStoredUserUuidToDisplayName(String(rawRm ?? ""));
                            if (resolved !== "") {
                                return resolved;
                            }
                            return resolveDealerKamRmValue(event, "rm");
                        },
                        sortingCapability: true,
                        filteringCapability_contains: true,
                    } satisfies OttopilotColumn<EventWithEventContainer>);
                }
                if (isEmotoradLeadsTemplate === true) {
                    explicitById.set(EMOTORAD__EVENT__ENQUIRY__CREATED_BY_NAME__COLUMN_ID, {
                        id: EMOTORAD__EVENT__ENQUIRY__CREATED_BY_NAME__COLUMN_ID,
                        type: OttopilotColumnType.string,
                        displayName: "Created by",
                        valueGetter: (event: EventWithEventContainer) => String(event.createdBy),
                        valueFormatter: (userId: string) => {
                            const userDisplayName = getUserDisplayName(userId as Uuid, users, userTemplates, platformUserDetailsByUserId);
                            return userDisplayName !== "?" ? userDisplayName : userId;
                        },
                        sortingCapability: true,
                        filteringCapability_in: {
                            valuesQueryFn: getEmotoradEventCreatorUserIds,
                        },
                    } satisfies OttopilotColumn<EventWithEventContainer>);
                    explicitById.set(EMOTORAD__EVENT__ENQUIRY__CREATED_BY_ROLE__COLUMN_ID, {
                        id: EMOTORAD__EVENT__ENQUIRY__CREATED_BY_ROLE__COLUMN_ID,
                        type: OttopilotColumnType.string,
                        displayName: "Created by role",
                        valueGetter: (event: EventWithEventContainer) => {
                            const createdByUser = users.find((item) => item.userId === event.createdBy);
                            const templateId = createdByUser?.templateId;
                            return templateId === undefined ? "" : String(templateId);
                        },
                        valueFormatter: (templateId: string) => {
                            if (templateId === "") {
                                return "";
                            }
                            return userTemplates.find((item) => item.id === (templateId as Uuid))?.displayName ?? "";
                        },
                        sortingCapability: true,
                        filteringCapability_in: {
                            valuesQueryFn: getEmotoradEventCreatorRoleTemplateIds,
                        },
                    } satisfies OttopilotColumn<EventWithEventContainer>);
                    explicitById.set("createdAt", {
                        id: "createdAt",
                        type: OttopilotColumnType.iso8601DateTime,
                        displayName: "Created at",
                        valueGetter: (event: EventWithEventContainer) => event.createdAt,
                        valueFormatter: (value) => formatDateTime(value),
                        sortingCapability: true,
                        filteringCapability_contains: true,
                    } satisfies OttopilotColumn<EventWithEventContainer>);
                    explicitById.set(EMOTORAD__EVENT__ENQUIRY__ASSIGNED_ON__COLUMN_ID, {
                        id: EMOTORAD__EVENT__ENQUIRY__ASSIGNED_ON__COLUMN_ID,
                        type: OttopilotColumnType.string,
                        displayName: "Assigned on",
                        valueGetter: (event: EventWithEventContainer) => {
                            const rawAssignedOn =
                                event.additionalColumnValues[EMOTORAD__EVENT__ENQUIRY__ASSIGNED_ON__COLUMN_ID] ??
                                event.eventContainer.additionalColumnValues[EMOTORAD__EVENT_CONTAINER__ASSIGNED_ON__COLUMN_ID];
                            if (rawAssignedOn === null || rawAssignedOn === undefined || String(rawAssignedOn).trim() === "") {
                                return "";
                            }
                            return String(rawAssignedOn).trim();
                        },
                        valueFormatter: (raw: string) => {
                            if (raw === "") {
                                return "";
                            }
                            return formatDateTime(raw as never);
                        },
                        sortingCapability: true,
                        filteringCapability_contains: true,
                    } satisfies OttopilotColumn<EventWithEventContainer>);
                    explicitById.set(EMOTORAD__EVENT_CONTAINER__ASSIGNED_ON__COLUMN_ID, {
                        id: EMOTORAD__EVENT_CONTAINER__ASSIGNED_ON__COLUMN_ID,
                        type: OttopilotColumnType.string,
                        displayName: "Assigned on",
                        valueGetter: (event: EventWithEventContainer) => {
                            const rawAssignedOn =
                                event.additionalColumnValues[EMOTORAD__EVENT__ENQUIRY__ASSIGNED_ON__COLUMN_ID] ??
                                event.eventContainer.additionalColumnValues[EMOTORAD__EVENT_CONTAINER__ASSIGNED_ON__COLUMN_ID];
                            if (rawAssignedOn === null || rawAssignedOn === undefined || String(rawAssignedOn).trim() === "") {
                                return "";
                            }
                            return String(rawAssignedOn).trim();
                        },
                        valueFormatter: (raw: string) => {
                            if (raw === "") {
                                return "";
                            }
                            return formatDateTime(raw as never);
                        },
                        sortingCapability: true,
                        filteringCapability_contains: true,
                    } satisfies OttopilotColumn<EventWithEventContainer>);
                    explicitById.set(EMOTORAD__EVENT__ENQUIRY__AON__COLUMN_ID, {
                        id: EMOTORAD__EVENT__ENQUIRY__AON__COLUMN_ID,
                        type: OttopilotColumnType.string,
                        displayName: "AON",
                        valueGetter: (event: EventWithEventContainer) => {
                            const rawAon =
                                event.additionalColumnValues[EMOTORAD__EVENT__ENQUIRY__AON__COLUMN_ID] ??
                                event.eventContainer.additionalColumnValues[EMOTORAD__EVENT_CONTAINER__AON__COLUMN_ID as Uuid];
                            return rawAon === null || rawAon === undefined ? "" : String(rawAon);
                        },
                        sortingCapability: true,
                        filteringCapability_contains: false,
                        filteringCapability_in: {
                            valuesQueryFn: queryEventAonFilterValuesFromJourneyCreatedAt,
                        },
                        customFilter: buildEventAonCustomFilterForColumn(emotoradEnquiryAonConfig, String(EMOTORAD__EVENT__ENQUIRY__AON__COLUMN_ID)),
                    } satisfies OttopilotColumn<EventWithEventContainer>);
                }
                if (isEmotoradSalesTemplate === true) {
                    const journeyCustomerPincodeColumn = getAdditionalColumns(journeysModuleConfiguration).find(
                        (c) => c.id === EMOTORAD__EVENT_CONTAINER__CUSTOMER_PINCODE__COLUMN_ID,
                    );
                    explicitById.set(EMOTORAD__EVENT_CONTAINER__CUSTOMER_PINCODE__COLUMN_ID, {
                        id: EMOTORAD__EVENT_CONTAINER__CUSTOMER_PINCODE__COLUMN_ID,
                        type: OttopilotColumnType.string,
                        displayName: journeyCustomerPincodeColumn?.displayName ?? "Customer Pincode",
                        valueGetter: (event: EventWithEventContainer) => {
                            const fromSalesEvent = event.additionalColumnValues[EMOTORAD__EVENT__SALES__CUSTOMER_PINCODE__COLUMN_ID];
                            if (fromSalesEvent !== null && fromSalesEvent !== undefined && String(fromSalesEvent).trim() !== "") {
                                return String(fromSalesEvent);
                            }
                            const fromJourney = event.eventContainer.additionalColumnValues[EMOTORAD__EVENT_CONTAINER__CUSTOMER_PINCODE__COLUMN_ID];
                            if (fromJourney !== null && fromJourney !== undefined && String(fromJourney).trim() !== "") {
                                return String(fromJourney);
                            }
                            return "";
                        },
                        sortingCapability: true,
                        filteringCapability_contains: true,
                    } satisfies OttopilotColumn<EventWithEventContainer>);
                    const emotoradSalesActionPlatformLabelsById = (() => {
                        const map = new Map<string, string>();
                        const addFrom = (column: unknown) => {
                            for (const o of getAdditionalColumnOptions(column, userDefinedObjectOptionsByTemplateId) ?? []) {
                                const key = String(o.id);
                                if (!map.has(key)) map.set(key, o.displayName);
                            }
                        };
                        addFrom(getAdditionalColumns(eventTemplate).find((c) => c.id === EMOTORAD__EVENT__SALES__ACTION_PLATFORM__COLUMN_ID));
                        addFrom(getAdditionalColumns(journeysModuleConfiguration).find((c) => c.id === EMOTORAD__EVENT_CONTAINER__ACTION_PLATFORM__COLUMN_ID));
                        addFrom(eventTemplates.flatMap((t) => getAdditionalColumns(t)).find((c) => c.id === EMOTORAD__EVENT__ENQUIRY__CURRENT_PLATFORM__COLUMN_ID));
                        for (const option of getEmotoradCurrentPlatformSelectOptions()) {
                            const key = String(option.id);
                            if (!map.has(key)) {
                                map.set(key, option.displayName);
                            }
                        }
                        return map;
                    })();
                    const formatKamRmStoredValueForDisplay = (value: string): string => {
                        if (value === "") {
                            return "";
                        }
                        const trimmed = value.trim();
                        const looksLikeUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(trimmed);
                        if (looksLikeUuid !== true) {
                            return value;
                        }
                        const displayName = getUserDisplayName(trimmed as Uuid, users, userTemplates, platformUserDetailsByUserId);
                        if (displayName !== "?" && displayName !== "-") {
                            return displayName;
                        }
                        return value;
                    };
                    explicitById.set(EMOTORAD__EVENT__SALES__CREATED_BY_NAME__COLUMN_ID, {
                        id: EMOTORAD__EVENT__SALES__CREATED_BY_NAME__COLUMN_ID,
                        type: OttopilotColumnType.string,
                        displayName: "Created by",
                        valueGetter: (event: EventWithEventContainer) => String(event.createdBy),
                        valueFormatter: (userId: string) => {
                            const userDisplayName = getUserDisplayName(userId as Uuid, users, userTemplates, platformUserDetailsByUserId);
                            return userDisplayName !== "?" ? userDisplayName : userId;
                        },
                        sortingCapability: true,
                        filteringCapability_in: {
                            valuesQueryFn: getEmotoradEventCreatorUserIds,
                        },
                    } satisfies OttopilotColumn<EventWithEventContainer>);
                    explicitById.set(EMOTORAD__EVENT__SALES__ACTION_PLATFORM__COLUMN_ID, {
                        id: EMOTORAD__EVENT__SALES__ACTION_PLATFORM__COLUMN_ID,
                        type: OttopilotColumnType.string,
                        displayName: "Action Platform",
                        valueGetter: (event: EventWithEventContainer) => {
                            const rawActionPlatform =
                                event.additionalColumnValues[EMOTORAD__EVENT__SALES__ACTION_PLATFORM__COLUMN_ID] ??
                                event.eventContainer.additionalColumnValues[EMOTORAD__EVENT_CONTAINER__ACTION_PLATFORM__COLUMN_ID] ??
                                event.additionalColumnValues[EMOTORAD__EVENT__ENQUIRY__CURRENT_PLATFORM__COLUMN_ID];
                            if (rawActionPlatform === undefined || rawActionPlatform === null || String(rawActionPlatform).trim() === "") {
                                return "";
                            }
                            return String(rawActionPlatform).trim();
                        },
                        valueFormatter: (rawId: string) => {
                            if (rawId === "") {
                                return "";
                            }
                            return emotoradSalesActionPlatformLabelsById.get(rawId) ?? formatEmotoradCurrentPlatformCell(rawId);
                        },
                        sortingCapability: true,
                        filteringCapability_contains: true,
                    } satisfies OttopilotColumn<EventWithEventContainer>);
                    explicitById.set(EMOTORAD__EVENT__SALES__CREATED_BY_ROLE__COLUMN_ID, {
                        id: EMOTORAD__EVENT__SALES__CREATED_BY_ROLE__COLUMN_ID,
                        type: OttopilotColumnType.string,
                        displayName: "Created by role",
                        valueGetter: (event: EventWithEventContainer) => {
                            const createdByUser = users.find((item) => item.userId === event.createdBy);
                            const templateId = createdByUser?.templateId;
                            return templateId === undefined ? "" : String(templateId);
                        },
                        valueFormatter: (templateId: string) => {
                            if (templateId === "") {
                                return "";
                            }
                            return userTemplates.find((item) => item.id === (templateId as Uuid))?.displayName ?? "";
                        },
                        sortingCapability: true,
                        filteringCapability_in: {
                            valuesQueryFn: getEmotoradEventCreatorRoleTemplateIds,
                        },
                    } satisfies OttopilotColumn<EventWithEventContainer>);
                    explicitById.set(EMOTORAD__EVENT_CONTAINER__CUSTOMER_ZONE__COLUMN_ID, {
                        id: EMOTORAD__EVENT_CONTAINER__CUSTOMER_ZONE__COLUMN_ID,
                        type: OttopilotColumnType.string,
                        displayName: "Customer Zone",
                        valueGetter: resolveEmotoradSalesCustomerZone,
                        sortingCapability: true,
                        filteringCapability_in: {
                            valuesQueryFn: getEmotoradSalesCustomerZoneFilterValues,
                        },
                    } satisfies OttopilotColumn<EventWithEventContainer>);
                    explicitById.set(EMOTORAD__EVENT__SALES__KAM__COLUMN_ID, {
                        id: EMOTORAD__EVENT__SALES__KAM__COLUMN_ID,
                        type: OttopilotColumnType.string,
                        displayName: "KAM",
                        valueGetter: (event: EventWithEventContainer) => {
                            const rawKam =
                                event.additionalColumnValues[EMOTORAD__EVENT__SALES__KAM__COLUMN_ID] ??
                                event.eventContainer.additionalColumnValues[EMOTORAD__EVENT_CONTAINER__KAM_NAME__COLUMN_ID];
                            if (rawKam !== undefined && rawKam !== null && String(rawKam).trim() !== "") {
                                return String(rawKam).trim();
                            }
                            return resolveDealerKamRmValue(event, "kam");
                        },
                        valueFormatter: formatKamRmStoredValueForDisplay,
                        sortingCapability: true,
                        filteringCapability_contains: true,
                    } satisfies OttopilotColumn<EventWithEventContainer>);
                    explicitById.set(EMOTORAD__EVENT__SALES__RM__COLUMN_ID, {
                        id: EMOTORAD__EVENT__SALES__RM__COLUMN_ID,
                        type: OttopilotColumnType.string,
                        displayName: "RM",
                        valueGetter: (event: EventWithEventContainer) => {
                            const rawRm =
                                event.additionalColumnValues[EMOTORAD__EVENT__SALES__RM__COLUMN_ID] ??
                                event.eventContainer.additionalColumnValues[EMOTORAD__EVENT_CONTAINER__ZSM_NAME__COLUMN_ID];
                            if (rawRm !== undefined && rawRm !== null && String(rawRm).trim() !== "") {
                                return String(rawRm).trim();
                            }
                            return resolveDealerKamRmValue(event, "rm");
                        },
                        valueFormatter: formatKamRmStoredValueForDisplay,
                        sortingCapability: true,
                        filteringCapability_contains: true,
                    } satisfies OttopilotColumn<EventWithEventContainer>);
                }
                const eventAdditionalColumnById = new Map(getAdditionalColumns(eventTemplate).map((column) => [column.id, column]));
                const journeyAdditionalColumnById = new Map(getAdditionalColumns(journeysModuleConfiguration).map((column) => [column.id, column]));
                const buildFallbackExplicitColumn = (id: string): OttopilotColumn<EventWithEventContainer> => {
                    if (id === "updatedBy") {
                        return {
                            id: id,
                            type: OttopilotColumnType.uuid,
                            displayName: "Updated by",
                            valueGetter: (event) => event.updatedBy,
                            valueFormatter: (value) => getUserDisplayName(value, users, userTemplates, platformUserDetailsByUserId),
                            sortingCapability: true,
                            filteringCapability_in: {
                                valuesQueryFn: getEmotoradEventUpdaterUserIds,
                            },
                        };
                    }
                    if (id === "createdBy") {
                        return {
                            id: id,
                            type: OttopilotColumnType.uuid,
                            displayName: "Created by",
                            valueGetter: (event) => event.createdBy,
                            valueFormatter: (value) => getUserDisplayName(value, users, userTemplates, platformUserDetailsByUserId),
                            sortingCapability: false,
                            filteringCapability_contains: false,
                        };
                    }
                    if (id === "updatedAt" || id === "createdAt") {
                        return {
                            id: id,
                            type: OttopilotColumnType.iso8601DateTime,
                            displayName: id === "updatedAt" ? "Updated at" : "Created at",
                            valueGetter: (event) => (id === "updatedAt" ? event.updatedAt : event.createdAt),
                            valueFormatter: (value) => formatDateTime(value),
                            sortingCapability: true,
                            filteringCapability_contains: true,
                        };
                    }
                    const eventColumn = eventAdditionalColumnById.get(id as Uuid);
                    if (eventColumn !== undefined) {
                        const fallbackDisplayName =
                            isEmotoradSalesTemplate === true && id === EMOTORAD__EVENT__SALES__SHOWROOM_ID__COLUMN_ID
                                ? "Franchise ID"
                                : (isEmotoradCallTemplate || isEmotoradTestrideTemplate) && eventColumn.displayName.trim().toLowerCase() === "zsm name"
                                  ? "RM Name"
                                  : eventColumn.displayName;
                        const formatEventColumnDisplayValue = (raw: unknown): string => {
                            const formatted = getUserDefinedObjectAdditionalColumnDetails(eventColumn).stringValueGetter({
                                value: raw as never,
                                users: users,
                                userTemplates: userTemplates,
                                platformUserDetailsByUserId,
                                organizationalUnits: organizationalUnits,
                                organizationalUnitTemplates: organizationalUnitTemplates,
                                products: products,
                            });
                            const label = formatted === null || formatted === undefined ? "" : String(formatted);
                            if (label !== "" && label !== "?") {
                                return label;
                            }
                            if (isAmaronWorkspace(workspaceCapabilities) === true && isAmaronFuelTypeColumnId(id) === true) {
                                return formatAmaronFuelTypeIdForDisplay(eventTableAdditionalColumnRawToString(raw));
                            }
                            return label === "?" ? eventTableAdditionalColumnRawToString(raw) : label;
                        };
                        return {
                            id: id,
                            type: OttopilotColumnType.string,
                            displayName: fallbackDisplayName,
                            valueGetter: (event) => {
                                let raw: unknown = event.additionalColumnValues[id as Uuid];
                                if (
                                    isEmotoradWalkinTemplate === true &&
                                    String(id) === String(EMOTORAD__EVENT__WALKINS__PURCHASE_TIMELINE__COLUMN_ID) &&
                                    (raw === null || raw === undefined || raw === "")
                                ) {
                                    raw = event.eventContainer.additionalColumnValues[EMOTORAD__EVENT_CONTAINER__PURCHASE_TIMELINE__COLUMN_ID];
                                }
                                return eventTableAdditionalColumnRawToString(raw);
                            },
                            valueFormatter: (value) => formatEventColumnDisplayValue(value),
                            sortingCapability: true,
                            filteringCapability_contains: true,
                        };
                    }
                    const journeyColumn = journeyAdditionalColumnById.get(id as Uuid);
                    if (isEmotoradWorkspace(workspace) === true && String(id) === String(EMOTORAD__EVENT_CONTAINER__ACTION_PLATFORM__COLUMN_ID)) {
                        const journeyActionPlatformColumn = journeyAdditionalColumnById.get(EMOTORAD__EVENT_CONTAINER__ACTION_PLATFORM__COLUMN_ID);
                        const actionPlatformLabelsById = (() => {
                            const map = new Map<string, string>();
                            const addFrom = (column: unknown) => {
                                for (const option of getAdditionalColumnOptions(column, userDefinedObjectOptionsByTemplateId) ?? []) {
                                    const key = String(option.id);
                                    if (!map.has(key)) {
                                        map.set(key, option.displayName);
                                    }
                                }
                            };
                            addFrom(journeyActionPlatformColumn);
                            addFrom(getAdditionalColumns(eventTemplate).find((col) => col.id === EMOTORAD__EVENT__CALLS__CURRENT_PLATFORM__COLUMN_ID));
                            for (const option of getEmotoradCurrentPlatformSelectOptions()) {
                                const key = String(option.id);
                                if (!map.has(key)) {
                                    map.set(key, option.displayName);
                                }
                            }
                            return map;
                        })();
                        return {
                            id: id,
                            type: OttopilotColumnType.string,
                            displayName: journeyActionPlatformColumn?.displayName ?? "Action Platform",
                            valueGetter: (event: EventWithEventContainer) => {
                                const raw = event.eventContainer.additionalColumnValues[EMOTORAD__EVENT_CONTAINER__ACTION_PLATFORM__COLUMN_ID];
                                if (raw === null || raw === undefined || String(raw).trim() === "") {
                                    return "";
                                }
                                return String(raw).trim();
                            },
                            valueFormatter: (raw: string) => {
                                if (raw === "") {
                                    return "";
                                }
                                return actionPlatformLabelsById.get(raw) ?? formatEmotoradCurrentPlatformCell(raw);
                            },
                            sortingCapability: true,
                            filteringCapability_contains: true,
                        } satisfies OttopilotColumn<EventWithEventContainer>;
                    }
                    if (journeyColumn !== undefined) {
                        if (
                            isEmotoradTestrideTemplate === true &&
                            (isEmotoradJourneyTestRideCountColumnField(id as Uuid) === true ||
                                isEmotoradTestRideCountJourneyColumnDisplayName(journeyColumn.displayName) === true)
                        ) {
                            return {
                                id: id,
                                type: OttopilotColumnType.string,
                                displayName: journeyColumn.displayName,
                                valueGetter: (event) => emotoradJourneyTestRideCountCellString(event.eventContainer.additionalColumnValues, id as Uuid),
                                sortingCapability: true,
                                filteringCapability_contains: true,
                            };
                        }
                        const fallbackDisplayName =
                            (isEmotoradCallTemplate || isEmotoradTestrideTemplate) && journeyColumn.displayName.trim().toLowerCase() === "zsm name"
                                ? "RM Name"
                                : journeyColumn.displayName;
                        return {
                            id: id,
                            type: OttopilotColumnType.string,
                            displayName: fallbackDisplayName,
                            valueGetter: (event) => event.eventContainer.additionalColumnValues[id as Uuid] as string,
                            valueFormatter: (value) =>
                                getUserDefinedObjectAdditionalColumnDetails(journeyColumn).stringValueGetter({
                                    value: value as never,
                                    users: users,
                                    userTemplates: userTemplates,
                                    platformUserDetailsByUserId,
                                    organizationalUnits: organizationalUnits,
                                    organizationalUnitTemplates: organizationalUnitTemplates,
                                    products: products,
                                }),
                            sortingCapability: true,
                            filteringCapability_contains: true,
                        };
                    }
                    if (isDuluxWorkspace(workspaceCapabilities) === true) {
                        const duluxJourneyColumnDisplayName = resolveDuluxJourneyFallbackTableColumnDisplayName(id);
                        if (duluxJourneyColumnDisplayName !== undefined) {
                            return {
                                id: id,
                                type: OttopilotColumnType.string,
                                displayName: duluxJourneyColumnDisplayName,
                                valueGetter: (event) => String(event.eventContainer.additionalColumnValues[id as Uuid] ?? ""),
                                sortingCapability: true,
                                filteringCapability_contains: true,
                            };
                        }
                    }
                    return {
                        id: id,
                        type: OttopilotColumnType.string,
                        displayName: id,
                        valueGetter: (event) => String(event.additionalColumnValues[id as Uuid] ?? ""),
                        sortingCapability: true,
                        filteringCapability_contains: true,
                    };
                };
                if (shouldUseEmotoradExplicitColumns === true) {
                    for (const auditSystemColumnId of ["updatedBy", "updatedAt", "createdAt"] as const) {
                        if (workspaceExplicitOrderedEventColumnIds.includes(auditSystemColumnId) === true) {
                            explicitById.set(auditSystemColumnId, buildFallbackExplicitColumn(auditSystemColumnId));
                        }
                    }
                }
                const explicitOrderedColumnIdsUnique = mergeUniqueOrderedColumnIds(
                    workspaceExplicitOrderedEventColumnIds,
                    isAmaronWorkspace(workspaceCapabilities) === true ? getAmaronDefaultVisibleFilterColumnIdsForEventTemplate(eventTemplate.id) : [],
                );
                return finalizeScaffoldEventColumns(
                    explicitOrderedColumnIdsUnique.map((id) => (explicitById.get(id) ?? buildFallbackExplicitColumn(id)) as OttopilotColumn<EventWithEventContainer>),
                );
            })(),
        },
        views: [
            ...((isEmotoradListSupportedTemplate === true && emotoradEventsListConfig.enabled === true) ||
            (isRegistryListSupportedTemplate === true && registryEventsListConfig?.enabled === true)
                ? [
                      createOttopilotListView(EVENTS_LIST_VIEW_ID, (event: EventWithEventContainer, listCardContext) =>
                          eventsListPresentation.renderListCard(event, listCardContext),
                      ),
                  ]
                : []),
            {
                id: uniqueUuid`7485111d-3520-4028-a593-9ee492a82c33`,
                viewType: ViewType.table,
                visibleColumns: isEmotoradSalesTemplate
                    ? EMOTORAD_SALES_EVENT_TABLE_VISIBLE_COLUMN_IDS
                    : isEmotoradTestrideTemplate
                      ? EMOTORAD_TESTRIDE_EVENT_TABLE_VISIBLE_COLUMN_IDS
                      : isEmotoradCallTemplate
                        ? EMOTORAD_CALLS_EVENT_TABLE_VISIBLE_COLUMN_IDS
                        : isEmotoradWalkinTemplate
                          ? EMOTORAD_WALKINS_EVENT_TABLE_VISIBLE_COLUMN_IDS
                          : isEmotoradLeadsTemplate
                            ? EMOTORAD_LEADS_EVENT_TABLE_VISIBLE_COLUMN_IDS
                            : isBobcardApplicationsInitiatedTemplate === true
                              ? BOBCARD_ENQUIRY_EVENT_TABLE_VISIBLE_COLUMN_IDS
                              : isAmaronEnquiryTemplate === true
                                ? AMARON_ENQUIRY_EVENT_TABLE_VISIBLE_COLUMN_IDS
                                : isAmaronServiceTemplate === true
                                  ? AMARON_SERVICE_EVENT_TABLE_VISIBLE_COLUMN_IDS
                                  : isAmaronCallTemplate === true
                                    ? AMARON_CALLS_EVENT_TABLE_VISIBLE_COLUMN_IDS
                                    : isDuluxCallTemplate === true
                                      ? DULUX_CALLS_EVENT_TABLE_VISIBLE_COLUMN_IDS
                                      : isDuluxAppointmentsListTemplate === true
                                        ? DULUX_APPOINTMENTS_EVENT_TABLE_VISIBLE_COLUMN_IDS
                                        : isDuluxEnquiryTemplate === true
                                          ? DULUX_ENQUIRY_EVENT_TABLE_VISIBLE_COLUMN_IDS
                                          : isDuluxOnboardingTemplate === true
                                            ? DULUX_ONBOARDING_EVENT_TABLE_VISIBLE_COLUMN_IDS
                                            : eventTemplate.configuration.eventPage.tableViewColumns !== undefined
                                              ? eventTemplate.configuration.eventPage.tableViewColumns.map((c) => c.id)
                                              : [
                                                    // Default visible columns
                                                    // \"id\",
                                                    ...getAdditionalColumns(eventTemplate)
                                                        .filter((eventColumn) => {
                                                            // Filter out event columns that have the same display name as journey columns
                                                            const hasMatchingJourneyColumn = getAdditionalColumns(journeysModuleConfiguration).some(
                                                                (journeyColumn) => journeyColumn.displayName === eventColumn.displayName,
                                                            );
                                                            return !hasMatchingJourneyColumn;
                                                        })
                                                        .map((column) => column.id),
                                                    ...getAdditionalColumns(journeysModuleConfiguration).map((column) => column.id),
                                                    "status",
                                                    "disposition",
                                                    "createdBy",
                                                    "createdAt",
                                                    "updatedBy",
                                                    "updatedAt",
                                                ],
                otherArgs: {
                    // Removed navigation - view/edit buttons will open panels instead
                    // rowClass: "cursor-pointer",
                    // onRowClicked: (event: {data: EventWithEventContainer}) => {
                    //     // Don't navigate if user clicked on the action column
                    //     if (event.event.preventOnRowClicked === true) {
                    //         return;
                    //     }
                    //
                    //     navigate({
                    //         to: `/w/${workspace.id}/ottopilot/event-containers/${event.data.eventContainer.id}/events/${event.data.id}`,
                    //     });
                    // },
                },
            },
            {
                id: uniqueUuid`8baac944-642e-4856-b697-31a736e0d846`,
                viewType: ViewType.kanbanBoard,
                cardRenderer: (event: EventWithEventContainer) => (
                    <EventKanbanBoardCard
                        key={event.id}
                        event={event}
                        rowActionsConfig={effectiveEventRowActions}
                        onRequestAdditionalButtonModal={(payload) => {
                            setAdditionalButtonModal(payload);
                        }}
                        dataViewerQueryKeyPrefix={eventsDataViewerQueryKeyPrefix}
                    />
                ),
                columnConfig: eventKanbanColumnConfig,
                filterFieldId: eventTemplate.configuration.eventPage.kanbanBoardPageView.filterFieldId,
                ...(isDuluxWorkspace(workspaceCapabilities) === true && isDuluxListSupportedTemplate === true
                    ? {
                          kanbanLayout: {
                              ...DULUX_MODULE_KANBAN_LAYOUT,
                              ...(isArchivedSlice === true ? ARCHIVED_MODULE_KANBAN_LAYOUT : {}),
                          },
                      }
                    : isArchivedSlice === true
                      ? {kanbanLayout: ARCHIVED_MODULE_KANBAN_LAYOUT}
                      : {}),
            },
            ...(eventCalendarConfig !== undefined
                ? [
                      {
                          id: EVENTS_CALENDAR_VIEW_ID,
                          viewType: ViewType.calendar as const,
                          getEventType: getEventCalendarType,
                          legendItems: eventCalendarLegendItems,
                          getEventTypeLabel: getEventCalendarTypeLabel,
                          getEventColor: eventCalendarGetEventColorForView,
                          allDayStyle: eventCalendarAllDayStyleForView,
                          scheduleEmptyState: eventCalendarScheduleEmptyState,
                          onCreateEventClick: onEventCalendarCreateClick,
                          eventOpensDaySummaryModal: true,
                      },
                  ]
                : []),
            // {
            //     id: uniqueUuid`61070cb1-ffcd-48a3-88f5-eaaa5e849168`,
            //     viewType: ViewType.cards,
            //     cardRenderer: (event) => (
            //         <EventCard
            //             event={event}
            //             wrapInsideLink={true}
            //         />
            //     ),
            // },
        ],
        initialFilterConfiguration: eventsInitialFilterConfiguration,
        initialViewId: initialViewId,
        initialVisibleFilters: eventsInitialVisibleFiltersConfiguration,
        initialSearchConfiguration: eventsInitialSearchConfiguration,
        renderTableNoDataEmptyState: renderEventsTableNoDataEmptyState,
        listCardChrome: (
            <>
                {eventRowEngagement.scheduleCallModal}
                {eventRowEngagement.archiveModals}
                {(isEmotoradListSupportedTemplate === true || isRegistryListSupportedTemplate === true) &&
                ((isEmotoradListSupportedTemplate === true && emotoradEventsListConfig.includeEventCreateDrawer === true) ||
                    (isRegistryListSupportedTemplate === true && registryEventsListConfig?.includeEventCreateDrawer === true)) ? (
                    <WorkspaceListCardCreateChrome
                        opened={isListCreateDrawerOpen}
                        onClose={() => {
                            setListCreateDrawerOpen(false);
                            sharedEventFooterBindings.setSelectedCreateJourneyId(null);
                        }}
                        workspaceId={workspace.id}
                        items={listEventCreateDrawerItems}
                        eventContainerId={sharedEventFooterBindings.selectedCreateJourneyId}
                    />
                ) : null}
                {listNotesPanelChrome}
                {leadDispositionPanelChrome}
            </>
        ),
        readPanelComponent: (row, isExpanded) => {
            const panel = eventsDataViewerControllerRef.current?.currentlyOpenRowEngagementPanel;
            if (
                panel?.rowEngagementType === RowEngagementType.read &&
                panel.rowEngagementData?.id === row.id &&
                panel.readPanelLayout === "entityProfile"
            ) {
                const {eventContainer, ...eventWithoutContainer} = row;
                const journey = {
                    ...eventContainer,
                    events: [eventWithoutContainer],
                } as EventContainerWithEvents;
                return (
                    <JourneyEntityProfileAside
                        journey={journey}
                        profileFrom={eventRowEngagement.profileNavigationFrom}
                        onClose={() => {
                            eventsDataViewerControllerRef.current?.setCurrentlyOpenRowEngagementPanel({
                                rowEngagementType: RowEngagementType.none,
                                rowEngagementPanelType: RowEngagementPanelType.none,
                                rowEngagementData: null,
                            });
                        }}
                    />
                );
            }

            return (
                <EventViewPanel
                    row={row}
                    isExpanded={isExpanded}
                    eventTemplate={effectiveEventTemplateForViewPanel}
                    journeysModuleConfiguration={journeysModuleConfiguration}
                    eventTemplates={eventTemplates}
                    notesEventContainerSourceLabel={resolvedNotesEventContainerSourceLabelForEventPanels}
                />
            );
        },
        updatePanelComponent: (row, isExpanded) => {
            const effectiveEditPanel = effectiveEventTemplateForEditPanel.configuration.eventPage.editPanel;
            if (effectiveEditPanel !== undefined && effectiveEditPanel.columnGrouping.length === 0) {
                return null;
            }
            return (
                <EventEditPanel
                    row={row}
                    isExpanded={isExpanded}
                    eventTemplate={effectiveEventTemplateForEditPanel}
                    journeysModuleConfiguration={journeysModuleConfiguration}
                    eventTemplates={eventTemplates}
                    notesEventContainerSourceLabel={resolvedNotesEventContainerSourceLabelForEventPanels}
                    queryKeyPrefix={eventsDataViewerQueryKeyPrefix}
                    onSuccess={(updatedData) => {
                        eventsDataViewerController.setCurrentlyOpenRowEngagementPanel((prev) => {
                            if (prev.rowEngagementType === RowEngagementType.none) {
                                return prev;
                            }
                            return {
                                rowEngagementType: RowEngagementType.read,
                                rowEngagementPanelType: prev.rowEngagementPanelType,
                                rowEngagementData: updatedData as any,
                            };
                        });
                    }}
                />
            );
        },
        additionalActionComponent: undefined,
        tableRowActions: effectiveEventRowActions,
        tableSelectionBulkAction: isArchivedSlice === true ? "restore" : "delete",
        onRequestRestoreSelectedRows:
            isArchivedSlice === true
                ? (rows) => {
                      setRestoreEventTargets(rows);
                      setIsRestoreEventModalOpen(true);
                  }
                : undefined,
        rowEngagementDropdownOptions: (row) => {
            const exportColumns = orderColumnsLikeTable(
                eventsDataViewerController.columns.lineItems.filter(
                    isDuluxWorkspace(workspaceCapabilities) === true ? duluxColumnIsExportable : (col) => col.id !== "id" && col.id !== "actionButton",
                ),
                eventsDataViewerController.tableColumnConfig?.orderedVisibleColumnIds,
            );

            const dataToExport = transformRowForExport(row, exportColumns);

            const exportAndCopyItems = [
                <ExportDataDropdownItem
                    key="export"

                    onNativeCsvExport={isEmotoradWorkspace(workspace) ? undefined : () => nativeCsvRowExportRef.current(row.id)}
                    isVisible={isEmotoradWorkspace(workspace) !== true}
                />,
                <CopyDataDropdownItem
                    key="copy"
                    data={dataToExport}
                    showFlag={showFlag}
                />,
            ];

            // Amaron view panel ⋯ menu: Export + Copy only (Edit is the footer CTA; row actions stay on the list).
            if (isAmaronWorkspace(workspaceCapabilities) === true) {
                return exportAndCopyItems;
            }

            if (isArchivedSlice === true) {
                return [
                    ...exportAndCopyItems,
                    <RestoreDataDropdownItem
                        key="restore"
                        onSelect={() => {
                            setRestoreEventTargets([row]);
                            setIsRestoreEventModalOpen(true);
                        }}
                    />,
                ];
            }

            const visibleAdditionalConfigs = filterAdditionalButtonsVisibleForEvent({
                additionalButtons: additionalButtonConfig,
                event: row,
                currentUserTemplateIds: currentUserTemplateIds,
            });

            const isRestrictedByGlobalConstraints = isListCardFooterRestrictedByGlobalConstraints(
                row.eventContainer,
                journeyRowActionGlobalConstraints,
            );

            return [
                ...exportAndCopyItems,
                ...visibleAdditionalConfigs.map((config) => (
                    <EventAdditionalButtonTrigger
                        key={config.id}
                        config={config}
                        event={row}
                        layout="dropdownItem"
                        currentUserTemplateIds={currentUserTemplateIds}
                        workspaceId={workspace.id}
                        onOpenModal={(payload) => {
                            setAdditionalButtonModal(payload);
                        }}
                        isDisabled={isRestrictedByGlobalConstraints}
                    />
                )),
            ];
        },
    });
    eventsDataViewerControllerRef.current = eventsDataViewerController;

    const restoreEventMutation = useMutation({
        mutationFn: async () => {
            const targets = restoreEventTargetsRef.current;
            if (targets === null || targets.length === 0) {
                throw new Error("Cannot restore this event");
            }

            for (const event of targets) {
                const eventTemplateForRestore = eventTemplates.find((template) => template.id === event.templateId) ?? eventTemplate;

                const result = await restoreArchivedEvent({
                    accessToken: heimdallUser.accessToken,
                    intellsysBaseUrl,
                    workspaceId: workspace.id,
                    workspace: workspace,
                    workspaceCapabilities,
                    event: event,
                    eventTemplate: eventTemplateForRestore,
                    eventContainerTemplate: journeysModuleConfiguration,
                });

                if (result.success !== true) {
                    throw result.error;
                }
            }
        },
        onSuccess: () => {
            setShowRestoreSuccessToast(true);
            setIsRestoreEventModalOpen(false);
            setRestoreEventTargets(null);
            const controller = eventsDataViewerControllerRef.current;
            if (controller !== null) {
                controller.setCurrentlyOpenRowEngagementPanel({
                    rowEngagementType: RowEngagementType.none,
                    rowEngagementPanelType: RowEngagementPanelType.none,
                    rowEngagementData: null,
                });
            }
            void invalidateDataViewerQueriesAfterEventArchive(queryClient, eventsDataViewerQueryKeyPrefix, workspace.id);
        },
        onError: (error: unknown) => {
            showFlag({
                title: "Restore failed",
                description: error instanceof Error ? error.message : "Could not restore the event. Please try again.",
                appearance: "error",
                isAutoDismiss: true,
                autoDismissSeconds: 4,
            });
        },
    });

    // const eventsTableRowsQuery = useTableRows(eventsDataViewerController);

    // useEffect(() => {
    //     if (isEmotoradWorkspace(workspace) !== true) {
    //         return;
    //     }
    //     if (eventsTableRowsQuery.status !== "success") {
    //         return;
    //     }
    //     const rows = eventsTableRowsQuery.data.pages.flatMap((page) => page);
    //     if (rows.length === 0) {
    //         return;
    //     }
    //     const firstRow = rows[0]!;
    // }, [workspace, eventsTableRowsQuery.data, eventsTableRowsQuery.status]);

    // Sync view based on URL query param (search.view from useSearch updates when sidebar Table/Kanban link navigates)
    useEffect(() => {
        if (search.view === "create" && isDhiraaguWorkspace(workspace) === true) {
            navigate({
                to: "/w/$workspaceId/ottopilot/dhiraagu/journey/create",
                params: {workspaceId: workspace.id},
            });
            return;
        }
        const viewId =
            search.view === "list"
                ? EVENTS_LIST_VIEW_ID
                : search.view === "kanban"
                  ? EVENTS_KANBAN_VIEW_ID
                  : search.view === "calendar" && hasEventCalendarConfig
                    ? EVENTS_CALENDAR_VIEW_ID
                    : EVENTS_TABLE_VIEW_ID;
        eventsDataViewerController.setCurrentViewId(viewId);
    }, [search.view, workspace.id, navigate, hasEventCalendarConfig]);

    const eventsFilterTemplateScopeRef = useRef<Uuid | null>(null);

    useEffect(() => {
        const previousEventTemplateId = eventsFilterTemplateScopeRef.current;
        eventsFilterTemplateScopeRef.current = eventTemplateId;

        if (previousEventTemplateId !== null && previousEventTemplateId !== eventTemplateId) {
            const resetFilterConfiguration = mergeDefaultCreatedAtMonthFilter(eventsInitialFilterConfiguration);
            eventsDataViewerController.setFilterConfiguration(
                isAmaronWorkspace(workspaceCapabilities) === true
                    ? applyAmaronEventsInitialFilterOverlay(resetFilterConfiguration)
                    : resetFilterConfiguration,
            );
            eventsDataViewerController.setVisibleFilters(eventsInitialVisibleFiltersConfiguration);
            eventsDataViewerController.setSearchConfiguration(eventsInitialSearchConfiguration);
            eventsDataViewerController.setSortConfiguration(eventsInitialSortConfiguration);
        }

        // Clear user column layout so table/kanban use this template's view.visibleColumns / columnConfig.
        eventsDataViewerController.setTableColumnConfig(null);
        eventsDataViewerController.setKanbanColumnConfig(null);
        // `useOttopilotDataViewerController` returns a new object each render; depend on stable
        // `useState` setters only so this effect does not loop (Maximum update depth exceeded).
    }, [
        eventTemplateId,
        eventsInitialFilterConfiguration,
        eventsInitialVisibleFiltersConfiguration,
        eventsInitialSearchConfiguration,
        eventsInitialSortConfiguration,
        eventsDataViewerController.setFilterConfiguration,
        eventsDataViewerController.setVisibleFilters,
        eventsDataViewerController.setSearchConfiguration,
        eventsDataViewerController.setSortConfiguration,
        eventsDataViewerController.setTableColumnConfig,
        eventsDataViewerController.setKanbanColumnConfig,
        applyAmaronEventsInitialFilterOverlay,
        workspace,
    ]);

    const emotoradEventStatusNavConfig = useMemo(
        () => (isEmotoradWorkspace(workspace) === true ? getEmotoradEventTemplateStatusNavConfig(eventTemplateId) : null),
        [eventTemplateId, workspace],
    );

    useSyncModuleStatusFilterFromSearch({
        statusColumnId: emotoradEventStatusNavConfig?.statusColumnId ?? null,
        setFilterConfiguration: eventsDataViewerController.setFilterConfiguration,
        enabled: isEmotoradWorkspace(workspace) === true && emotoradEventStatusNavConfig !== null,
        validStatusIds: getEmotoradModuleStatusNavValidStatusIds(emotoradEventStatusNavConfig),
    });

    useSyncModuleStatusFilterFromSearch({
        statusColumnId: amaronEventStatusNavConfigForInitialFilter?.statusColumnId ?? null,
        setFilterConfiguration: eventsDataViewerController.setFilterConfiguration,
        enabled: isAmaronWorkspace(workspaceCapabilities) === true && amaronEventStatusNavConfigForInitialFilter !== null,
        validStatusIds: getAmaronModuleStatusNavValidStatusIds(amaronEventStatusNavConfigForInitialFilter),
        statusIdFromSearch: search.statusId,
        ignoreStatusIdSearchParam: eventSliceConfig.ignoreStatusIdSearchParam,
        alwaysUseInOperation: true,
    });

    useHideAmaronModuleStatusFilterUiInChildView({
        enabled: isAmaronWorkspace(workspaceCapabilities) === true && amaronEventStatusNavConfigForInitialFilter !== null,
        statusNavConfig: amaronEventStatusNavConfigForInitialFilter,
        setVisibleFilters: eventsDataViewerController.setVisibleFilters,
        validStatusIds: getAmaronModuleStatusNavValidStatusIds(amaronEventStatusNavConfigForInitialFilter),
        statusIdFromSearch: search.statusId,
        ignoreStatusIdSearchParam: eventSliceConfig.ignoreStatusIdSearchParam,
    });

    useSyncModuleStatusFilterFromSearch({
        statusColumnId: duluxEventStatusNavConfigForInitialFilter?.statusColumnId ?? null,
        setFilterConfiguration: eventsDataViewerController.setFilterConfiguration,
        enabled: isDuluxWorkspace(workspaceCapabilities) === true && duluxEventStatusNavConfigForInitialFilter !== null,
        validStatusIds: getDuluxModuleStatusNavValidStatusIds(duluxEventStatusNavConfigForInitialFilter),
        statusIdFromSearch: search.statusId,
        ignoreStatusIdSearchParam: eventSliceConfig.ignoreStatusIdSearchParam,
        alwaysUseInOperation: true,
    });

    useHideModuleStatusFilterUiInChildView({
        enabled: isDuluxWorkspace(workspaceCapabilities) === true && duluxEventStatusNavConfigForInitialFilter !== null,
        statusNavConfig: duluxEventStatusNavConfigForInitialFilter,
        setVisibleFilters: eventsDataViewerController.setVisibleFilters,
        validStatusIds: getDuluxModuleStatusNavValidStatusIds(duluxEventStatusNavConfigForInitialFilter),
        statusIdFromSearch: search.statusId,
        ignoreStatusIdSearchParam: eventSliceConfig.ignoreStatusIdSearchParam,
        moduleStatusFilterScaffold: DULUX_MODULE_STATUS_FILTER_SCAFFOLD,
    });

    const duluxEventPngFilename =
        isDuluxWorkspace(workspaceCapabilities) === true ? resolveDuluxEventExportFilename(eventTemplateId, "png") : undefined;

    const resolveEventsCsvQuery = (controller: typeof eventsDataViewerController) => {
        const filterConfigurationForBackend = isAmaronCallTemplate === true
            ? sanitizeAmaronDispositionFiltersForExport(controller.filterConfiguration, "eventCalls")
            : controller.filterConfiguration;
        const mappedFilterConfiguration = mapFilterConfigurationToFields(applyEventsApiFilterConfiguration(filterConfigurationForBackend));
        const filterConfigurationForExport: FilterConfiguration = {
            ...mappedFilterConfiguration,
            lineItems: [...mappedFilterConfiguration.lineItems,
                {columnId: "status", operation: FilterOperation.notEquals, value: OttopilotEventStatus.archived},
                {columnId: "templateId", operation: FilterOperation.equals, value: eventTemplateId}],
        };
        const eventTemplateAdditionalColumnIds = getAdditionalColumns(eventTemplate).map((item) => item.id);
        const eventContainerAdditionalColumnIds = getAdditionalColumns(journeysModuleConfiguration).map((item) => item.id);
        return {
            filters: convertFilterConfigurationToSrimanFiltersForEvents(filterConfigurationForExport, eventTemplateAdditionalColumnIds,
                eventContainerAdditionalColumnIds, controller.searchConfiguration),
            orderBy: convertSortConfigurationToSrimanOrderByForEvents(mapSortConfigurationToFields(controller.sortConfiguration),
                eventTemplateAdditionalColumnIds, eventContainerAdditionalColumnIds) as Array<{column: string; direction: "asc" | "desc"}>,
            templateId: eventTemplateId,
        };
    };
    const {

        onExportCsv: handleExportAllRowsLikeCsv,
        onExportCsvForRowId: exportEventRowCsv,
        onExportPng: handleExportAllRowsAsPng,
    } = useOttopilotExportActions({
        controller: eventsDataViewerController,

        pngFilename: duluxEventPngFilename ?? `events-${eventTemplateId}.png`,
        documentTitle: eventsPagePluralTitle,
        columnIsExportableFn: isDuluxWorkspace(workspaceCapabilities) === true ? duluxColumnIsExportable : undefined,
        csvExportMode: isEmotoradWorkspace(workspace) ? {kind: "disabled"} : {kind: "native", config: {
            baseUrl: intellsysBaseUrl,
            path: "/ottopilot/events/export",
            workspaceId: workspace.id,
            userId: heimdallUser.id,
            nullFilterColumnId: (id) => resolveEventsFilterColumnId(resolveTableColumnField(id),
                getAdditionalColumns(eventTemplate).map((item) => item.id), getAdditionalColumns(journeysModuleConfiguration).map((item) => item.id)),
            resolveColumnId: (column) => {
                const field = resolveTableColumnField(column.id);
                return nativeCsvEventColumn(
                    getAdditionalColumns,
                    isAmaronCallTemplate && isAmaronEventCallsDispositionUiFilterColumn(field) ? "disposition" : field,
                    journeysModuleConfiguration,
                    eventTemplate,
                    isAmaronWorkspace(workspaceCapabilities),
                );
            },
            resolveQuery: resolveEventsCsvQuery,
        }},
        onError: (message) => {
            showFlag({
                title: "Export failed",
                description: message,
                appearance: "error",
                isAutoDismiss: true,
                autoDismissSeconds: 3,
            });
        },
    });
    nativeCsvRowExportRef.current = exportEventRowCsv;

    const handleOpenArchivedData = () => {
        void navigate({
            to: `/w/${workspace.id}/ottopilot/events/${eventTemplateId}/archived`,
            search: {view: search.view ?? "table"},
        });
    };

    return (
        <>
            <TableOnlyPage
                title={isArchivedSlice ? eventSliceConfig.pageTitle : eventsPagePluralTitle}
                titleRightSection={
                    <div
                        className={
                            isMobile
                                ? "relative flex flex-wrap items-center justify-end gap-2 w-full min-w-0 sm:w-auto"
                                : "relative grid grid-flow-col auto-cols-max gap-ds-space-100"
                        }
                    >
                        {(() => {
                            if (eventSliceConfig.capabilities.showCreate !== true) {
                                return null;
                            }
                            let createForms = (eventTemplate.configuration.writeEventPageConfig ?? []).filter((item) => item.formType === "Create");

                            // If enquiry form is not in the config, add it as a fallback
                            if (eventTemplateId === (DhiraaguEventTypes.enquiry as Uuid) && createForms.length === 0) {
                                createForms = [
                                    {
                                        formId: "enquiry-create-fallback" as Uuid,
                                        formType: "Create",
                                        heading: "Create Enquiry",
                                    } as any,
                                ];
                            }
                            const isEmotoradLeadsTemplate =
                                isEmotoradWorkspace(workspace) === true &&
                                (eventTemplate.configuration.displayNameVariants.singularLowerCase.includes("lead") ||
                                    eventTemplate.configuration.displayNameVariants.pluralLowerCase.includes("lead"));
                            if (isEmotoradLeadsTemplate === true && createForms.length === 0) {
                                createForms = [
                                    {
                                        formId: EMOTORAD__LEAD__CREATE__FORM__ID,
                                        formType: "Create",
                                        heading: "Create Lead",
                                    } as any,
                                ];
                            }

                            const isEmotoradWalkinTemplate = isEmotoradWorkspace(workspace) === true && eventTemplateId === (EmotoradEventTypes.walkin as Uuid);
                            if (isEmotoradWalkinTemplate === true) {
                                createForms = [
                                    {
                                        formId: EMOTORAD__WALKIN__SCHEDULE__FORM__ID,
                                        formType: "Create",
                                        heading: "Schedule a walk-in",
                                    } as any,
                                    {
                                        formId: EMOTORAD__WALKIN__LOG__FORM__ID,
                                        formType: "Create",
                                        heading: "Log a walk-in",
                                    } as any,
                                ];
                            }

                            const isEmotoradSalesTemplate = isEmotoradWorkspace(workspace) === true && eventTemplateId === (EmotoradEventTypes.sales as Uuid);
                            if (isEmotoradSalesTemplate === true) {
                                createForms = [
                                    {
                                        formId: EMOTORAD__SALES__SCHEDULE__FORM__ID,
                                        formType: "Create",
                                        heading: "Create Sale",
                                    } as any,
                                ];
                            }

                            const isEmotoradTestrideTemplate =
                                isEmotoradWorkspace(workspace) === true && eventTemplateId === (EmotoradEventTypes.testride as Uuid);
                            if (isEmotoradTestrideTemplate === true) {
                                createForms = [
                                    {
                                        formId: EMOTORAD__TESTRIDE__SCHEDULE__FORM__ID,
                                        formType: "Create",
                                        heading: "Schedule a test ride",
                                    } as any,
                                    {
                                        formId: EMOTORAD__TESTRIDE__LOG__FORM__ID,
                                        formType: "Create",
                                        heading: "Log a test ride",
                                    } as any,
                                ];
                            }
                            const isEmotoradCallTemplate = isEmotoradWorkspace(workspace) === true && eventTemplateId === (EmotoradEventTypes.call as Uuid);
                            if (isEmotoradCallTemplate === true) {
                                createForms = [
                                    {
                                        formId: EMOTORAD__CALL__SCHEDULE__FORM__ID,
                                        formType: "Create",
                                        heading: "Schedule a call",
                                    } as any,
                                    {
                                        formId: EMOTORAD__CALL__LOG__FORM__ID,
                                        formType: "Create",
                                        heading: "Log a call",
                                    } as any,
                                ];
                            }

                            const isAmaronCallsTemplate = isAmaronWorkspace(workspaceCapabilities) === true && eventTemplateId === (AmaronEventTypes.Calls as Uuid);
                            if (isAmaronCallsTemplate === true) {
                                createForms = [
                                    {
                                        formId: AMARON__CALL__SCHEDULE__FORM__ID,
                                        formType: "Create",
                                        heading: "Schedule call",
                                    } as any,
                                    {
                                        formId: AMARON__CALL__LOG__FORM__ID,
                                        formType: "Create",
                                        heading: "Log call",
                                    } as any,
                                ];
                            }

                            const isDuluxCallsTemplate = isDuluxWorkspace(workspaceCapabilities) === true && eventTemplateId === (DuluxEventTypes.Calls as Uuid);
                            if (isDuluxCallsTemplate === true && createForms.length === 0) {
                                createForms = [
                                    {
                                        formId: DULUX__CALL__SCHEDULE__FORM__ID,
                                        formType: "Create",
                                        heading: "Schedule call",
                                    } as any,
                                    {
                                        formId: DULUX__CALL__LOG__FORM__ID,
                                        formType: "Create",
                                        heading: "Log call",
                                    } as any,
                                ];
                            }

                            const isDuluxAppointmentsTemplate =
                                isDuluxWorkspace(workspaceCapabilities) === true && eventTemplateId === (DuluxEventTypes.Appointments as Uuid);
                            if (isDuluxAppointmentsTemplate === true && createForms.length === 0) {
                                createForms = [
                                    {
                                        formId: DULUX__APPOINTMENT__SCHEDULE__FORM__ID,
                                        formType: "Create",
                                        heading: "Schedule appointment",
                                    } as any,
                                    {
                                        formId: DULUX__APPOINTMENT__LOG__FORM__ID,
                                        formType: "Create",
                                        heading: "Log an appointment",
                                    } as any,
                                ];
                            }

                            if (createForms.length === 0) {
                                return null;
                            }

                            const isCallTemplate = [
                                DhiraaguEventTypes.call as Uuid,
                                DuluxEventTypes.Calls as Uuid,
                                BobcardEventTypes.call as Uuid,
                                EmotoradEventTypes.call as Uuid,
                                AmaronEventTypes.Calls as Uuid,
                            ].includes(eventTemplateId as Uuid);

                            const getHref = (formId: Uuid) => {
                                return formId === DHIRAAGU__CALL__SCHEDULE__FORM__ID
                                    ? `/w/${workspace.id}/ottopilot/dhiraagu/call/schedule`
                                    : formId === DHIRAAGU__CALL__LOG__FORM__ID
                                      ? `/w/${workspace.id}/ottopilot/dhiraagu/call/log`
                                      : formId === DHIRAAGU__SALES__CREATE__FORM__ID
                                        ? `/w/${workspace.id}/ottopilot/dhiraagu/sale/create`
                                        : eventTemplateId === (DhiraaguEventTypes.enquiry as Uuid)
                                          ? `/w/${workspace.id}/ottopilot/dhiraagu/enquiry/create`
                                          : formId === DULUX__CALL__SCHEDULE__FORM__ID
                                            ? `/w/${workspace.id}/ottopilot/dulux/call/schedule`
                                            : formId === DULUX__CALL__LOG__FORM__ID
                                              ? `/w/${workspace.id}/ottopilot/dulux/call/log`
                                              : formId === DULUX__APPOINTMENT__SCHEDULE__FORM__ID
                                                ? `/w/${workspace.id}/ottopilot/dulux/appointment/schedule`
                                                : formId === DULUX__APPOINTMENT__LOG__FORM__ID
                                                  ? `/w/${workspace.id}/ottopilot/dulux/appointment/log`
                                                  : formId === BOBCARD__CALL__SCHEDULE__FORM__ID
                                                    ? `/w/${workspace.id}/ottopilot/bobcard/call/schedule`
                                                    : formId === BOBCARD__CALL__LOG__FORM__ID
                                                      ? `/w/${workspace.id}/ottopilot/bobcard/call/log`
                                                      : formId === EMOTORAD__CALL__SCHEDULE__FORM__ID
                                                        ? `/w/${workspace.id}/ottopilot/emotorad/call/schedule`
                                                        : formId === EMOTORAD__CALL__LOG__FORM__ID
                                                          ? `/w/${workspace.id}/ottopilot/emotorad/call/log`
                                                          : formId === AMARON__CALL__SCHEDULE__FORM__ID
                                                            ? `/w/${workspace.id}/ottopilot/amaron/call/schedule`
                                                            : formId === AMARON__CALL__LOG__FORM__ID
                                                              ? `/w/${workspace.id}/ottopilot/amaron/call/log`
                                                              : formId === EMOTORAD__SALES__SCHEDULE__FORM__ID
                                                                ? `/w/${workspace.id}/ottopilot/emotorad/sale/create`
                                                                : formId === EMOTORAD__TESTRIDE__SCHEDULE__FORM__ID
                                                                  ? `/w/${workspace.id}/ottopilot/emotorad/testride/schedule`
                                                                  : formId === EMOTORAD__TESTRIDE__LOG__FORM__ID
                                                                    ? `/w/${workspace.id}/ottopilot/emotorad/testride/log`
                                                                    : formId === EMOTORAD__WALKIN__SCHEDULE__FORM__ID
                                                                      ? `/w/${workspace.id}/ottopilot/emotorad/walkin/schedule`
                                                                      : formId === EMOTORAD__WALKIN__LOG__FORM__ID
                                                                        ? `/w/${workspace.id}/ottopilot/emotorad/walkin/log`
                                                                        : formId === EMOTORAD__LEAD__CREATE__FORM__ID
                                                                          ? `/w/${workspace.id}/ottopilot/emotorad/lead/create`
                                                                          : `/w/${workspace.id}/ottopilot/events/${eventTemplateId}/create?formId=${formId}`;
                            };

                            const useCallCreateDropdown =
                                createForms.length > 1 &&
                                (isCallTemplate === true ||
                                    isEmotoradTestrideTemplate === true ||
                                    isEmotoradWalkinTemplate === true ||
                                    isDuluxCallsTemplate === true ||
                                    isDuluxAppointmentsTemplate === true);

                            if (useCallCreateDropdown === true) {
                                return (
                                    <CallCreateDropdownButton
                                        isMobile={isMobile}
                                        items={createForms.map((form) => ({
                                            label: form.heading,
                                            onClick: () => navigate({to: getHref(form.formId)}),
                                        }))}
                                    />
                                );
                            }

                            return createForms.map((form) => {
                                const href = getHref(form.formId);

                                return (
                                    <AnankeButton
                                        key={form.formId}
                                        type="button"
                                        appearance="primary"
                                        iconBefore={(iconProps) => (
                                            <AddIcon
                                                {...iconProps}
                                                label=""
                                            />
                                        )}
                                        onClick={() => {
                                            void navigate({to: href});
                                        }}
                                    >
                                        {form.heading}
                                    </AnankeButton>
                                );
                            });
                        })()}
                        {eventSliceConfig.capabilities.showExportMenu === true &&
                            (isMobile ? (
                                <button
                                    type="button"
                                    onClick={() => setMoreActionsDrawerOpen(true)}

                                    className="w-[32px] h-[32px] flex items-center justify-center rounded-[3px] border"

                                    style={{
                                        borderColor: token("color.border", "#091E4224"),
                                        backgroundColor: token("elevation.surface", "#FFFFFF"),
                                        color: token("color.icon", "#44546F"),
                                    }}
                                >
                                    {<ShowMoreVerticalIcon label="More actions" />}
                                </button>
                            ) : (
                                <AnankeDropdownMenu<HTMLButtonElement>
                                    placement="bottom-end"
                                    shouldRenderToParent={true}
                                    trigger={({triggerRef, ...triggerProps}) => (
                                        <button
                                            {...triggerProps}
                                            ref={triggerRef as Ref<HTMLButtonElement>}
                                            type="button"

                                            className="w-[32px] h-[32px] flex items-center justify-center rounded-[3px] border"

                                            style={{
                                                borderColor: token("color.border", "#091E4224"),
                                                backgroundColor: token("elevation.surface", "#FFFFFF"),
                                                color: token("color.icon", "#44546F"),
                                            }}
                                        >
                                            {(
                                                <ShowMoreVerticalIcon label="More actions" />
                                            )}
                                        </button>
                                    )}
                                >
                                    <AnankeDropdownItemGroup>
                                        {isAmaronWorkspace(workspaceCapabilities) === true ? (
                                            <ExportFlatDropdownItems

                                                onExportCsv={isEmotoradWorkspace(workspace) === true ? undefined : handleExportAllRowsLikeCsv}
                                            />
                                        ) : (
                                            isEmotoradWorkspace(workspace) === true ? null : <ExportNestedDropdownItem

                                                onExportCsv={handleExportAllRowsLikeCsv}
                                            />
                                        )}
                                    </AnankeDropdownItemGroup>
                                    {eventSliceConfig.capabilities.showArchivedDataMenuItem === true ? (
                                        <AnankeDropdownItemGroup hasSeparator>
                                            <AnankeDropdownItem
                                                onClick={handleOpenArchivedData}
                                                elemBefore={<DeleteIcon label="" />}
                                            >
                                                Archived Data
                                            </AnankeDropdownItem>
                                        </AnankeDropdownItemGroup>
                                    ) : null}
                                </AnankeDropdownMenu>
                            ))}
                    </div>
                }
                stickyTitleSection={true}
                bottomSection={<OttopilotDataViewer controller={eventsDataViewerController} />}
            />

            {isMobile && eventSliceConfig.capabilities.showExportMenu === true && (
                <>
                    <BottomDrawer
                        open={moreActionsDrawerOpen}
                        setOpen={setMoreActionsDrawerOpen}
                        title="More actions"
                        fitContentOnOpen={true}
                    >
                        <ModuleMoreActionsDrawerContent
                            onExport={() => {
                                setMoreActionsDrawerOpen(false);
                                setExportDrawerOpen(true);
                            }}
                            onArchivedData={
                                eventSliceConfig.capabilities.showArchivedDataMenuItem === true
                                    ? () => {
                                          setMoreActionsDrawerOpen(false);
                                          handleOpenArchivedData();
                                      }
                                    : undefined
                            }
                        />
                    </BottomDrawer>
                    <BottomDrawer
                        open={exportDrawerOpen}
                        setOpen={setExportDrawerOpen}
                        title="Export"
                        fitContentOnOpen={true}
                    >
                        <ExportOptionsDrawerContent
                            onExportAsCsv={isEmotoradWorkspace(workspace) === true ? undefined : () => {
                                handleExportAllRowsLikeCsv();
                                setExportDrawerOpen(false);
                            }}
                            {...(isAmaronWorkspace(workspaceCapabilities) !== true
                                ? {
                                      onExportAsPng: () => {
                                          handleExportAllRowsAsPng();
                                          setExportDrawerOpen(false);
                                      },
                                  }
                                : {})}
                        />
                    </BottomDrawer>
                </>
            )}

            {additionalButtonModal !== null ? (
                <EventAdditionalButtonFormModal
                    config={additionalButtonModal.config}
                    event={additionalButtonModal.event}
                    eventTemplate={eventTemplate}
                    journeysModuleConfiguration={journeysModuleConfiguration}
                    opened={true}
                    onClose={() => {
                        setAdditionalButtonModal(null);
                    }}
                    onSuccess={() => {
                        eventsDataViewerController.setCurrentlyOpenRowEngagementPanel({
                            rowEngagementType: RowEngagementType.none,
                            rowEngagementPanelType: RowEngagementPanelType.none,
                            rowEngagementData: null,
                        });
                    }}
                    queryKeyPrefix={eventsDataViewerQueryKeyPrefix}
                    workspaceUserTemplateId={workspaceUser.templateId}
                    submitDisabledUntilValid={
                        isAmaronCallTemplate === true &&
                        additionalButtonModal.config.buttonKind === OttopilotAdditionalButtonKind.cancel
                    }
                />
            ) : null}
            {restoreEventTargets !== null && restoreEventTargets.length > 0 ? (
                <RestoreConfirmationModal
                    isOpen={isRestoreEventModalOpen}
                    setIsOpen={(isOpen) => {
                        setIsRestoreEventModalOpen(isOpen);
                        if (isOpen !== true) {
                            setRestoreEventTargets(null);
                        }
                    }}
                    itemType="event"
                    onConfirm={async () => {
                        await restoreEventMutation.mutateAsync();
                    }}
                    isLoading={restoreEventMutation.isPending}
                />
            ) : null}
            {showRestoreSuccessToast === true ? (
                <TableSuccessToast
                    title="Success"
                    description={`${eventTemplate.configuration.displayNameVariants.singularSentenceCase} details has been Restored successfully`}
                    backgroundColor={RESTORE_SUCCESS_TOAST_BACKGROUND}
                    onClose={() => setShowRestoreSuccessToast(false)}
                />
            ) : null}
        </>
    );
}

function DuluxScheduledCallTableEditButton(props: {event: EventWithEventContainer}) {
    const {workspace} = useWorkspace();
    const navigate = useNavigate();
    return (
        <AnankeButton
            type="button"
            onClick={(e) => {
                e.nativeEvent.stopPropagation();
                e.nativeEvent.preventDefault();
                const native = e.nativeEvent as MouseEvent & {preventOnRowClicked?: boolean};
                native.preventOnRowClicked = true;
                navigate({
                    to: `/w/${workspace.id}/ottopilot/dulux/call/edit?eventId=${props.event.id}`,
                });
            }}
        >
            Edit
        </AnankeButton>
    );
}

function DuluxScheduledAppointmentTableEditButton(props: {event: EventWithEventContainer}) {
    const {workspace} = useWorkspace();
    const navigate = useNavigate();
    return (
        <AnankeButton
            type="button"
            onClick={(e) => {
                e.nativeEvent.stopPropagation();
                e.nativeEvent.preventDefault();
                const native = e.nativeEvent as MouseEvent & {preventOnRowClicked?: boolean};
                native.preventOnRowClicked = true;
                navigate({
                    to: `/w/${workspace.id}/ottopilot/dulux/appointment/edit?eventId=${props.event.id}`,
                });
            }}
        >
            Edit
        </AnankeButton>
    );
}

function buildBobcardApplicationsInitiatedTableColumns(params: {eventTemplate: OttopilotEventTemplate}): any {
    return params.eventTemplate;
}
// function LinkOrDiv({to, children}: {to: string | null; children: React.ReactNode}) {
//     if (to === null) {
//         return <div>{children}</div>;
//     }

//     return <Link to={to}>{children}</Link>;
// }

// export function EventCard({event, wrapInsideLink}: {event: EventWithEventContainer; wrapInsideLink: boolean}) {
//     const cardPadding = "1rem";

//     const workspace = useWorkspace();
//     const eventTemplates = useEventTemplates();
//     const eventTemplate = eventTemplates.find((eventTemplate) => eventTemplate.id === event.templateId);
//     const users = useUsers();
//     const userTemplates = useUserTemplates();
//     const btlActivities = useBtlActivities();
//     const organizationalUnits = useOrganizationalUnits();
//     const organizationalUnitTemplates = useOrganizationalUnitTemplates();
//     const products = useProductCatalogue() ?? [];

//     // Filter out event columns that have the same display name as journey columns (use journey values for common fields)
//     const eventColumnsToShow =
//         eventTemplate === undefined
//             ? []
//             : eventTemplate.additionalColumns.filter((eventColumn) => {
//                   const hasMatchingJourneyColumn = journeysModuleConfiguration.additionalColumns.some(
//                       (journeyColumn) => journeyColumn.displayName === eventColumn.displayName,
//                   );
//                   return !hasMatchingJourneyColumn;
//               });

//     return (
//         <div className="relative">
//             <LinkOrDiv
//                 to={
//                     // wrapInsideLink === false
//                     //     ? null
//                     //     : event.journeyId === null
//                     //       ? null
//                     //       : `/w/${workspace.id}/ottopilot/event-containers/${event.journeyId}`
//                     wrapInsideLink === false ? null : `/w/${workspace.id}/ottopilot/event-containers/${event.eventContainer.id}`
//                 }
//             >
//                 <div
//                     className={twJoin(
//                         "w-full",
//                         // Border
//                         "border-ds-border border-ds-border-width border-solid",
//                         wrapInsideLink === false ? null : "hover:border-ds-border-bold",
//                         // Only border is animating
//                         "transition-colors duration-small",
//                         "rounded-[0.75rem]",
//                         "relative group",
//                     )}
//                     style={{
//                         padding: cardPadding,
//                         // borderRadius: token("radius.xlarge"),
//                     }}
//                 >
//                     <div className="grid grid-cols-1 gap-x-ds-space-100 items-start">
//                         {/* <div>
//                             <CustomerNameRenderer customer={customer} />

//                             <VerticalSpacer className="h-ds-space-050" />

//                             <div>
//                                 {processOttopilotStringTemplate(
//                                     customersModuleConfiguration.displayNameOttopilotStringTemplate,
//                                     customer.additionalColumnValues,
//                                 )}
//                             </div>
//                         </div> */}

//                         <AnankeAvatarItem
//                             avatar={<AnankeAvatar />}
//                             // primaryText={
//                             //     eventTemplate === undefined
//                             //         ? "?"
//                             //         : processOttopilotStringTemplate(
//                             //               eventTemplate.displayName,
//                             //               event.additionalColumnValues,
//                             //           )
//                             // }
//                             // secondaryText={eventTemplate === undefined ? "?" : eventTemplate.displayName}
//                             primaryText={eventTemplate === undefined ? "?" : eventTemplate.displayName}
//                         />
//                     </div>

//                     <VerticalSpacer className="h-ds-space-100" />

//                     <ItemBuilder
//                         items={eventColumnsToShow}
//                         itemBuilder={(column) => {
//                             return (
//                                 <div key={column.id}>
//                                     <span>{column.displayName}</span>:{" "}
//                                     {eventAdditionalColumnTypeToDetailsMap[column.type].stringValueGetter({
//                                         value: event.additionalColumnValues[column.id],
//                                         eventTemplates: eventTemplates,
//                                         users: users,
//                                         userTemplates: userTemplates,
//                                         organizationalUnits: organizationalUnits,
//                                         organizationalUnitTemplates: organizationalUnitTemplates,
//                                         btlActivities: btlActivities,
//                                         products: products,
//                                     })}
//                                 </div>
//                             );
//                         }}
//                     />

//                     <ItemBuilder
//                         items={journeysModuleConfiguration.additionalColumns}
//                         itemBuilder={(column) => {
//                             const eventTemplateForJourney =
//                                 eventTemplates.find((et) => et.id === event.templateId) ?? eventTemplates[0];
//                             if (eventTemplateForJourney === undefined) {
//                                 throw new Error("No event templates available");
//                             }
//                             return (
//                                 <div key={column.id}>
//                                     <span>{column.displayName}</span>:{" "}
//                                     {eventContainerAdditionalColumnTypeToDetailsMap[column.type].stringValueGetter({
//                                         value: event.eventContainer.additionalColumnValues[column.id],
//                                         eventTemplate: eventTemplateForJourney,
//                                         eventTemplates: eventTemplates,
//                                         users: users,
//                                         userTemplates: userTemplates,
//                                         organizationalUnits: organizationalUnits,
//                                         organizationalUnitTemplates: organizationalUnitTemplates,
//                                         journeysModuleConfiguration: journeysModuleConfiguration,
//                                         additionalValueKey: column.id,
//                                         btlActivities: btlActivities,
//                                         products: products,
//                                     })}
//                                 </div>
//                             );
//                         }}
//                     />

//                     <VerticalSpacer className="h-ds-space-100" />

//                     <AnankeTagGroup>
//                         {/* <AnankeSimpleTag
//                             text={userTemplate === undefined ? "?" : userTemplate.displayName}
//                             // color="yellow"
//                         /> */}

//                         <AnankeSimpleTag
//                             text={`Created on ${formatDate(event.createdAt)}`}
//                             color="yellow"
//                         />
//                     </AnankeTagGroup>
//                 </div>
//             </LinkOrDiv>
//         </div>
//     );
// }
